import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import Portal from './Portal';
import TenantListPage from './TenantListPage';
import UnitListPage from './UnitListPage';
import Sidebar from './Sidebar';
import ApplicantCard from './ApplicantCard';
import { db, storage, functions as firebaseFunctions } from '../firebase-config';
import { collection, query, onSnapshot, doc, getDoc, getDocs, where, updateDoc, addDoc, deleteDoc, serverTimestamp, deleteField, arrayUnion, arrayRemove, Timestamp } from "firebase/firestore";
import {
  countProposalProfessionals,
  normalizeMeetingProposals,
  hasPendingMeetingProposals,
  isProfessionalOnPreferredOfferList,
  MAX_PROFESSIONAL_MEETING_PROPOSALS,
  formatMeetingOptionForViewer,
} from '../utils/maintenanceMeetingProposals';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import AddressAutocomplete from './AddressAutocomplete';
import PaymentModal from './PaymentModal';
import ProfessionalRatingsHint from './ProfessionalRatingsHint';
import ProfessionalProfilePeekModal from './ProfessionalProfilePeekModal';
import DashboardAppearanceSettings from './DashboardAppearanceSettings';
import MaintenanceTriageChat from './MaintenanceTriageChat';
import {
  getStoredDashboardTheme,
  DASHBOARD_THEME_EVENT,
  DASHBOARD_THEME_STORAGE_KEY,
} from '../utils/dashboardTheme';
import { getOwnerDashboardSkin } from '../utils/ownerDashboardSkin';

/** Small (i) control; opens a short panel, closes on outside click. */
function InlineHelpBubble({ bubbleId, openId, onToggle, title, children, panelStyle = {} }) {
  const wrapRef = useRef(null);
  const open = openId === bubbleId;
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onToggle(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onToggle]);

  return (
    <span
      ref={wrapRef}
      className="inline-help-bubble"
      style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle', marginLeft: 6 }}
    >
      <button
        type="button"
        className="inline-help-bubble-trigger"
        aria-expanded={open}
        aria-label={title}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(open ? null : bubbleId);
        }}
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          border: '1px solid #cbd5e1',
          background: '#f8fafc',
          color: '#64748b',
          fontSize: 11,
          fontWeight: 700,
          fontStyle: 'italic',
          cursor: 'pointer',
          lineHeight: 1,
          padding: 0,
          flexShrink: 0,
        }}
      >
        i
      </button>
      {open ? (
        <div
          role="tooltip"
          className="inline-help-tooltip"
          style={{
            position: 'absolute',
            zIndex: 50,
            left: 0,
            top: 'calc(100% + 8px)',
            width: 280,
            maxWidth: 'min(280px, 85vw)',
            padding: '12px 14px',
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: 1.5,
            color: '#334155',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
            ...panelStyle,
          }}
        >
          {children}
        </div>
      ) : null}
    </span>
  );
}

/**
 * Workflow bucket for owner UI — mirrors the professional pipeline (open pool → in progress → scheduled → done).
 * Not stored separately; derived from `status`, assignment fields, and meeting data.
 */
function getOwnerMaintenanceWorkflowKey(req) {
  const raw = String(req?.status || 'pending').toLowerCase();
  if (raw === 'cancelled') return 'cancelled';
  if (raw === 'completed') return 'completed';
  if (raw === 'scheduled' || req?.acceptedMeetingTime) return 'scheduled';
  const hasProActivity =
    (req?.assignedProfessional && String(req.assignedProfessional).trim() !== '') ||
    (Array.isArray(req?.claimedByProfessionals) && req.claimedByProfessionals.length > 0) ||
    countProposalProfessionals(req) > 0;
  if (hasProActivity) return 'in_progress';
  if (raw === 'open') return 'open';
  return 'open';
}

function getOwnerMaintenanceDisplayLabel(req) {
  const key = getOwnerMaintenanceWorkflowKey(req);
  const labels = {
    cancelled: 'Cancelled',
    completed: 'Completed',
    scheduled: 'Scheduled',
    in_progress: 'In progress',
    open: 'Open',
  };
  return { key, label: labels[key] || key };
}

function isOwnerMaintenanceActive(req) {
  const k = getOwnerMaintenanceWorkflowKey(req);
  return k !== 'completed' && k !== 'cancelled';
}

/** Spotlight onboarding: targets use `data-owner-tip-target` on real controls. */
const OWNER_TOUR_STEPS = [
  {
    selector: '[data-owner-tip-target="owner-qa-toggle"]',
    title: 'Quick Actions',
    body: 'Open this menu to add a unit or create a maintenance request in a couple of taps.',
    preferDashboard: true,
    openSidebarMobile: false,
  },
  {
    selector: '[data-owner-tip-target="owner-manage-units"]',
    title: 'All units',
    body: 'All units is your hub for every property. Open the list to search and sort, use Add unit for another space, then open a unit card to view details and settings.',
    preferDashboard: false,
    openSidebarMobile: true,
  },
  {
    selector: '[data-owner-tip-target="owner-all-requests"]',
    title: 'All Requests',
    body: 'When a job is marked complete, open the request from here to optionally rate the professional.',
    preferDashboard: false,
    openSidebarMobile: true,
  },
];

const OWNER_REQ_STATUS_FILTER_LABELS = {
  all: 'All',
  open: 'Open',
  in_progress: 'In progress',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function OwnerDashboard({ user, userData, onLogout, onProfileUpdate, onViewTenants, onViewRequests }) {
  const delegation = useMemo(() => {
    const rawDel = userData?.delegatedFromOwnerId && String(userData.delegatedFromOwnerId).trim();
    const effectiveOwnerId = rawDel || user?.uid || '';
    const isDelegatedOwner = Boolean(rawDel);
    const delegateReadOnly =
      isDelegatedOwner && String(userData?.delegatePermissions || 'read_only') === 'read_only';
    return { effectiveOwnerId, isDelegatedOwner, delegateReadOnly, delegatedFromId: rawDel || null };
  }, [user?.uid, userData?.delegatedFromOwnerId, userData?.delegatePermissions]);

  const { effectiveOwnerId, isDelegatedOwner, delegateReadOnly, delegatedFromId } = delegation;

  // navigation handled via activeTab and shared Sidebar
  const [requests, setRequests] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(true);
  const [applicantFilter, setApplicantFilter] = useState('all'); // 'all' | 'pending' | 'follow_up' | 'approved' | 'rejected' | 'archived'
  const [ownerUnits, setOwnerUnits] = useState([]);
  const [unitTenantMap, setUnitTenantMap] = useState({});
  const [occupiedUnitIds, setOccupiedUnitIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'report', 'all-requests', 'preferred-pros', 'settings', or unit ID
  const [unitSubTab, setUnitSubTab] = useState('overview'); // 'overview', 'tenants', 'requests', 'settings'
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [showAddUnitForm, setShowAddUnitForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  // App settings
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationsEnabled');
    return saved ? JSON.parse(saved) : true;
  });
  const [emailAlerts, setEmailAlerts] = useState(() => {
    const saved = localStorage.getItem('emailAlerts');
    return saved ? JSON.parse(saved) : true;
  });
  const [quickActionUsage, setQuickActionUsage] = useState(() => {
    try {
      const stored = localStorage.getItem(`qa_usage_${user?.uid}`);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });
  const [qaOpen, setQaOpen] = useState(false);
  const qaButtonRef = useRef(null);
  const qaMenuRef = useRef(null);
  const [qaMenuStyle, setQaMenuStyle] = useState({});
  const [ownerThemeDark, setOwnerThemeDark] = useState(() => getStoredDashboardTheme() === 'dark');
  const [ownerBilling, setOwnerBilling] = useState(() => userData?.subscription || null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingMessage, setBillingMessage] = useState('');
  const [showBillingPrompt, setShowBillingPrompt] = useState(false);
  const [ownerTourActive, setOwnerTourActive] = useState(false);
  const [ownerTourStep, setOwnerTourStep] = useState(0);
  const [ownerTourSpot, setOwnerTourSpot] = useState(null);
  const [ownerTourTooltipPos, setOwnerTourTooltipPos] = useState({ left: 16, top: 16, width: 320 });
  const [ownerTourLayoutTick, setOwnerTourLayoutTick] = useState(0);
  const [primaryOwnerRow, setPrimaryOwnerRow] = useState(null);
  const [staffInviteEmail, setStaffInviteEmail] = useState('');
  const [staffInvitePermission, setStaffInvitePermission] = useState('read_only');
  const [staffInviteBusy, setStaffInviteBusy] = useState(false);
  const [staffInviteMsg, setStaffInviteMsg] = useState('');
  const [staffInvites, setStaffInvites] = useState([]); // owner_staff_invites rows (pending + accepted)
  const [staffMembers, setStaffMembers] = useState([]);

  useEffect(() => {
    const syncFromStorage = () => setOwnerThemeDark(getStoredDashboardTheme() === 'dark');
    const onThemeEvent = (e) => {
      const m = e?.detail?.mode;
      if (m === 'dark' || m === 'light') setOwnerThemeDark(m === 'dark');
    };
    const onStorage = (ev) => {
      if (ev.key === DASHBOARD_THEME_STORAGE_KEY) syncFromStorage();
    };
    setOwnerThemeDark(document.documentElement.classList.contains('theme-dark'));
    window.addEventListener(DASHBOARD_THEME_EVENT, onThemeEvent);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(DASHBOARD_THEME_EVENT, onThemeEvent);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Keep subscription info fresh (OwnersData is not always reloaded immediately after returning from Stripe).
  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsub = onSnapshot(
      doc(db, 'OwnersData', user.uid),
      (snap) => {
        const d = snap.data() || {};
        setOwnerBilling(d.subscription || null);
      },
      () => {}
    );
    return () => unsub();
  }, [user?.uid]);

  const selectedPlan = useMemo(() => {
    try {
      return (sessionStorage.getItem('td_owner_selected_plan') || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }, []); // read once; sessionStorage changes should be set before navigating here

  const activePlanKey = String(ownerBilling?.planKey || 'free').toLowerCase();
  const subscriptionActive = ['active', 'trialing'].includes(
    String(ownerBilling?.status || '').toLowerCase()
  );

  // After returning from Stripe Checkout, verify the session and sync OwnersData subscription fields.
  useEffect(() => {
    if (!user?.uid) return;
    const qs = new URLSearchParams(window.location.search);
    const sessionId = qs.get('sub_session_id')?.trim();
    if (!sessionId) return;
    (async () => {
      setBillingBusy(true);
      setBillingMessage('');
      try {
        const fn = httpsCallable(firebaseFunctions, 'verifyOwnerSubscriptionCheckoutSession');
        const resp = await fn({ sessionId });
        if (resp?.data?.verified) {
          setBillingMessage('✅ Subscription activated.');
          if (onProfileUpdate) onProfileUpdate();
        } else {
          setBillingMessage('Payment not completed yet. If you were charged, try refreshing.');
        }
      } catch (e) {
        setBillingMessage(e?.message || 'Could not verify subscription payment.');
      } finally {
        setBillingBusy(false);
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete('sub_session_id');
          const next = u.pathname + (u.searchParams.toString() ? `?${u.searchParams.toString()}` : '');
          window.history.replaceState({}, '', next);
        } catch {
          /* ignore */
        }
      }
    })();
  }, [user?.uid, onProfileUpdate]);

  // Post-signup prompt: if a paid plan was chosen, prompt owner to complete checkout.
  useEffect(() => {
    if (!user?.uid) return;
    if (!selectedPlan || selectedPlan === 'free') return;
    if (subscriptionActive) return;
    if (activePlanKey === selectedPlan) return;
    setShowBillingPrompt(true);
  }, [user?.uid, selectedPlan, subscriptionActive, activePlanKey]);

  const startSubscriptionCheckout = useCallback(async (planKey) => {
    const key = String(planKey || '').trim().toLowerCase();
    if (!key || key === 'free') return;
    setBillingBusy(true);
    setBillingMessage('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'createOwnerSubscriptionCheckoutSession');
      const resp = await fn({ planKey: key });
      const url = resp?.data?.url;
      if (!url) throw new Error('Missing checkout URL.');
      window.location.href = url;
    } catch (e) {
      setBillingMessage(e?.message || 'Could not start subscription checkout.');
      setBillingBusy(false);
    }
  }, []);

  const openBillingPortal = useCallback(async () => {
    setBillingBusy(true);
    setBillingMessage('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'createOwnerBillingPortalSession');
      const resp = await fn({});
      const url = resp?.data?.url;
      if (!url) throw new Error('Missing billing portal URL.');
      window.location.href = url;
    } catch (e) {
      setBillingMessage(e?.message || 'Could not open billing portal.');
      setBillingBusy(false);
    }
  }, []);

  useEffect(() => {
    function handleOutside(e) {
      const target = e.target;
      if (
        qaButtonRef.current && !qaButtonRef.current.contains(target) &&
        qaMenuRef.current && !qaMenuRef.current.contains(target)
      ) setQaOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    if (qaOpen && qaButtonRef.current) {
      const rect = qaButtonRef.current.getBoundingClientRect();
      const menuWidth = 220;
      const left = rect.right + window.scrollX - menuWidth;
      setQaMenuStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY + 10,
        left: Math.max(8, left),
        minWidth: menuWidth,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
        zIndex: 1200
      });
    }
  }, [qaOpen]);

  // Owner spotlight tour: first visit (or again after 7 days).
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) return;
    if (isDelegatedOwner) return;
    const key = `owner_quick_tips_last_shown_${uid}`;
    let last = 0;
    try {
      last = Number(localStorage.getItem(key) || 0) || 0;
    } catch {
      last = 0;
    }
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (!last || Date.now() - last > sevenDaysMs) {
      setOwnerTourStep(0);
      setOwnerTourActive(true);
    }
  }, [user?.uid, isDelegatedOwner]);

  const dismissOwnerTour = useCallback(() => {
    const uid = user?.uid;
    if (uid) {
      try {
        localStorage.setItem(`owner_quick_tips_last_shown_${uid}`, String(Date.now()));
      } catch {
        /* ignore */
      }
    }
    setOwnerTourActive(false);
    setOwnerTourSpot(null);
  }, [user?.uid]);

  useEffect(() => {
    if (!delegatedFromId) {
      setPrimaryOwnerRow(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, 'OwnersData', delegatedFromId),
      (snap) => setPrimaryOwnerRow(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => setPrimaryOwnerRow(null)
    );
    return () => unsub();
  }, [delegatedFromId]);

  useEffect(() => {
    if (!user?.uid || isDelegatedOwner) {
      setStaffInvites([]);
      setStaffMembers([]);
      return undefined;
    }
    const q1 = query(collection(db, 'owner_staff_invites'), where('primaryOwnerId', '==', user.uid));
    const u1 = onSnapshot(
      q1,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const at = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : Number(a?.createdAt) || 0;
          const bt = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : Number(b?.createdAt) || 0;
          return bt - at;
        });
        setStaffInvites(rows);
      },
      () => setStaffInvites([])
    );
    const q2 = query(
      collection(db, 'owner_staff_members'),
      where('primaryOwnerId', '==', user.uid),
      where('status', '==', 'active')
    );
    const u2 = onSnapshot(
      q2,
      (snap) => setStaffMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setStaffMembers([])
    );
    return () => {
      u1();
      u2();
    };
  }, [user?.uid, isDelegatedOwner]);

  useEffect(() => {
    if (!ownerTourActive) return undefined;
    const step = OWNER_TOUR_STEPS[ownerTourStep];
    if (!step) return undefined;
    if (step.preferDashboard && activeTab !== 'dashboard') {
      setActiveTab('dashboard');
      requestAnimationFrame(() => setOwnerTourLayoutTick((t) => t + 1));
    }
    if (step.openSidebarMobile && typeof window !== 'undefined' && window.innerWidth < 768) {
      setMobileMenuOpen(true);
      requestAnimationFrame(() => setOwnerTourLayoutTick((t) => t + 1));
    }
    return undefined;
  }, [ownerTourActive, ownerTourStep, activeTab]);

  useEffect(() => {
    if (!ownerTourActive) return undefined;
    const onResize = () => setOwnerTourLayoutTick((t) => t + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ownerTourActive]);

  useLayoutEffect(() => {
    if (!ownerTourActive) {
      setOwnerTourSpot(null);
      return;
    }
    const step = OWNER_TOUR_STEPS[ownerTourStep];
    if (!step) return;
    const el = document.querySelector(step.selector);
    const pad = 10;
    if (!el || !(el instanceof HTMLElement)) {
      setOwnerTourSpot(null);
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const tw = Math.min(360, Math.max(260, vw - 32));
      setOwnerTourTooltipPos({ left: Math.max(12, (vw - tw) / 2), top: vh * 0.22, width: tw });
      return;
    }
    try {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch {
      /* ignore */
    }
    const r = el.getBoundingClientRect();
    const spot = {
      left: r.left - pad,
      top: r.top - pad,
      width: r.width + pad * 2,
      height: r.height + pad * 2,
    };
    setOwnerTourSpot(spot);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = Math.min(360, Math.max(260, vw - 32));
    const gap = 14;
    const centerFirstStep = ownerTourStep === 0;
    let left = centerFirstStep
      ? (vw - tw) / 2
      : spot.left + spot.width / 2 - tw / 2;
    let top = spot.top + spot.height + gap;
    const estH = 220;
    if (top + estH > vh - 16) {
      top = spot.top - estH - gap;
    }
    left = Math.max(12, Math.min(left, vw - tw - 12));
    top = Math.max(12, Math.min(top, vh - estH - 12));
    setOwnerTourTooltipPos({ left, top, width: tw });
  }, [ownerTourActive, ownerTourStep, activeTab, mobileMenuOpen, ownerUnits.length, ownerTourLayoutTick]);

  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'open' | 'in_progress' | 'scheduled' | 'completed' | 'cancelled'
  const [reportRange, setReportRange] = useState('30d'); // '30d', '90d', '12m'
  const [newUnitFormData, setNewUnitFormData] = useState({
    unitNumber: '',
    address: '',
    monthlyRent: '',
    leaseStart: '',
    leaseEnd: '',
    readyForApplicants: false,
    vacancyNotes: ''
  });
  const [newTenantInfo, setNewTenantInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [newTenantDocs, setNewTenantDocs] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const docInputRef = useRef(null);

  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileFirstName, setProfileFirstName] = useState('');
  const [profileLastName, setProfileLastName] = useState('');
  const [profileAddress, setProfileAddress] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [useDifferentContactEmail, setUseDifferentContactEmail] = useState(false);
  const [profileContactEmail, setProfileContactEmail] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePassword, setProfilePassword] = useState('');
  const [profilePasswordConfirm, setProfilePasswordConfirm] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [realtorConnections, setRealtorConnections] = useState([]);
  const [loadingRealtorConnections, setLoadingRealtorConnections] = useState(true);
  const [realtorConnectionsError, setRealtorConnectionsError] = useState('');
  const [realtorInviteActionId, setRealtorInviteActionId] = useState('');
  const [realtorInviteMessage, setRealtorInviteMessage] = useState('');

  // Tenant info editing
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [editTenantEmail, setEditTenantEmail] = useState('');
  const [editTenantPhone, setEditTenantPhone] = useState('');

  // Pending tenant info (for invites before tenant is registered)
  const [editingPendingTenant, setEditingPendingTenant] = useState(false);
  const [pendingTenantName, setPendingTenantName] = useState('');
  const [pendingTenantEmail, setPendingTenantEmail] = useState('');
  const [pendingTenantPhone, setPendingTenantPhone] = useState('');

  // Request editing state
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editRequestText, setEditRequestText] = useState('');
  const [editRequestCategory, setEditRequestCategory] = useState('');
  const [editRequestPriority, setEditRequestPriority] = useState('normal');
  const [editRequestLocation, setEditRequestLocation] = useState('');
  const [editRequestImages, setEditRequestImages] = useState([]);
  const [editRequestNewImages, setEditRequestNewImages] = useState([]);
  const [editRequestPermission, setEditRequestPermission] = useState(false);
  const [editRequestPreferredContact, setEditRequestPreferredContact] = useState('phone');
  const [editRequestUploading, setEditRequestUploading] = useState(false);

  /** When set, Tenants tab opens this tenant’s detail/documents modal */
  const [tenantsTabFocusId, setTenantsTabFocusId] = useState(null);
  const clearTenantsTabFocus = useCallback(() => setTenantsTabFocusId(null), []);

  // Unit settings state (service fees, etc)
  const [unitServiceFeeEnabled, setUnitServiceFeeEnabled] = useState(false);
  const [unitServiceFeeAmount, setUnitServiceFeeAmount] = useState('');
  const [unitReadyForApplicants, setUnitReadyForApplicants] = useState(false);
  const [unitVacancyNotes, setUnitVacancyNotes] = useState('');
  const [unitSettingsMessage, setUnitSettingsMessage] = useState('');

  // Platform settings state (global owner dashboard)
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteModalUnitId, setInviteModalUnitId] = useState(null);
  const [inviteModalEmailSending, setInviteModalEmailSending] = useState(false);

  // Owner request creation state
  const [creatingOwnerRequest, setCreatingOwnerRequest] = useState(false);
  const [ownerRequestSelectedUnitId, setOwnerRequestSelectedUnitId] = useState('');
  const [ownerRequestDesc, setOwnerRequestDesc] = useState('');
  const [ownerRequestCategory, setOwnerRequestCategory] = useState('');
  const [ownerRequestPriority, setOwnerRequestPriority] = useState('normal');
  const [ownerRequestLocation, setOwnerRequestLocation] = useState('');
  const [ownerRequestImages, setOwnerRequestImages] = useState([]);
  const [ownerRequestPreferredFirst, setOwnerRequestPreferredFirst] = useState(false);
  const [ownerRequestPreferredFirstHours, setOwnerRequestPreferredFirstHours] = useState(48);
  /** Photo uploads to Storage (do not reuse for submit — avoids races with addDoc). */
  const [ownerRequestUploading, setOwnerRequestUploading] = useState(false);
  const [ownerRequestSubmitting, setOwnerRequestSubmitting] = useState(false);
  const [ownerRequestSuccessOverlay, setOwnerRequestSuccessOverlay] = useState(false);
  const [ownerRequestSubmitError, setOwnerRequestSubmitError] = useState(null);
  const [ownerTriageSession, setOwnerTriageSession] = useState({
    messages: [],
    aiTriageSummary: null,
  });
  // Owner create-request gate: keep form locked until user explicitly continues.
  const [ownerCreateFormUnlocked, setOwnerCreateFormUnlocked] = useState(false);
  const [ownerCreateChatUserMessaged, setOwnerCreateChatUserMessaged] = useState(false);
  const [ownerCreateChatAssistantReplied, setOwnerCreateChatAssistantReplied] = useState(false);

  useEffect(() => {
    if (!creatingOwnerRequest) {
      setOwnerCreateFormUnlocked(false);
      setOwnerCreateChatUserMessaged(false);
      setOwnerCreateChatAssistantReplied(false);
      return;
    }
    // Fresh modal open: lock until the user explicitly continues.
    setOwnerCreateFormUnlocked(false);
    setOwnerCreateChatUserMessaged(false);
    setOwnerCreateChatAssistantReplied(false);
  }, [creatingOwnerRequest]);

  const mergeOwnerTriageDraft = useCallback((draft) => {
    if (!draft) return;
    if (draft.category) setOwnerRequestCategory(draft.category);
    if (draft.priority) setOwnerRequestPriority(draft.priority);
    if (draft.location) setOwnerRequestLocation(draft.location);
    if (draft.description && String(draft.description).trim()) {
      setOwnerRequestDesc(String(draft.description).trim());
    }
  }, []);

  const handleOwnerTriageSessionUpdate = useCallback((meta) => {
    setOwnerTriageSession({
      messages: meta.messages || [],
      aiTriageSummary: meta.aiTriageSummary ?? null,
    });
  }, []);

  const handleOwnerTriageGateMilestone = useCallback((e) => {
    if (e.kind === 'user_message') setOwnerCreateChatUserMessaged(true);
    if (e.kind === 'assistant_reply') setOwnerCreateChatAssistantReplied(true);
  }, []);

  const unlockOwnerCreateFormAnyway = useCallback(() => {
    setOwnerCreateFormUnlocked(true);
  }, []);

  const skipOwnerAssistantToForm = useCallback(() => {
    setOwnerCreateFormUnlocked(true);
  }, []);

  // Request detail modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [ownerModalRatingStars, setOwnerModalRatingStars] = useState(0);
  const [ownerModalRatingComment, setOwnerModalRatingComment] = useState('');
  const [ownerModalRatingSaving, setOwnerModalRatingSaving] = useState(false);

  useEffect(() => {
    setOwnerModalRatingStars(0);
    setOwnerModalRatingComment('');
  }, [selectedRequest?.id]);
  const [showPaymentModal, setShowPaymentModal] = useState(null); // holds request object for payment
  /** `{ professionalId, fallbackName }` when owner taps a professional name */
  const [proProfilePeek, setProProfilePeek] = useState(null);

  /** Preferred professionals (saved on `OwnersData.preferredProfessionalIds`). */
  const [preferredProEmail, setPreferredProEmail] = useState('');
  const [preferredProBusy, setPreferredProBusy] = useState(false);
  const [preferredProMessage, setPreferredProMessage] = useState('');
  const [preferredProMessageError, setPreferredProMessageError] = useState(false);
  const [preferredProHelpId, setPreferredProHelpId] = useState(null);
  /** Inline (i) panel for All Requests workflow copy — cleared when leaving the tab. */
  const [allRequestsHelpId, setAllRequestsHelpId] = useState(null);
  const [preferredProDetails, setPreferredProDetails] = useState({});
  const [ownerProInvites, setOwnerProInvites] = useState([]);
  const [preferredNudgeBusy, setPreferredNudgeBusy] = useState(false);
  const [preferredNudgeMessage, setPreferredNudgeMessage] = useState('');

  useEffect(() => {
    if (!ownerTourActive) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [ownerTourActive]);

  // 1. Fetch all units owned by this owner (or by the primary owner when signed in as team member)
  useEffect(() => {
    if (!user || !effectiveOwnerId) return;
    const q = query(collection(db, "units"), where("ownerId", "==", effectiveOwnerId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const unitsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOwnerUnits(unitsData);
    });
    return () => unsubscribe();
  }, [user, effectiveOwnerId]);

  // 2. Build a map of unitNumber -> tenant list from TenantsData
  useEffect(() => {
    if (!user || !effectiveOwnerId) return;
    const q = query(collection(db, 'TenantsData'), where('ownerId', '==', effectiveOwnerId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nextMap = {};
      const nextOccupiedUnitIds = new Set();

      snapshot.docs.forEach((tenantDoc) => {
        const tenant = { id: tenantDoc.id, ...tenantDoc.data() };
        if (tenant.unitNumber) {
          const unitNumberKey = String(tenant.unitNumber);
          nextMap[unitNumberKey] = [...(nextMap[unitNumberKey] || []), tenant];
        }
        if (tenant.unitId) {
          nextOccupiedUnitIds.add(tenant.unitId);
          nextMap[tenant.unitId] = [...(nextMap[tenant.unitId] || []), tenant];
        }
      });

      setUnitTenantMap(nextMap);
      setOccupiedUnitIds(nextOccupiedUnitIds);
    }, (error) => {
      console.error('Error loading tenant occupancy map:', error);
      setUnitTenantMap({});
      setOccupiedUnitIds(new Set());
    });

    return () => unsubscribe();
  }, [user, effectiveOwnerId]);

  // 2b. Fetch applicants submitted to this owner (by ownerId and/or ownerEmail from realtor routing)
  useEffect(() => {
    if (!user || !effectiveOwnerId) return;
    const byOwnerId = new Map();
    const byOwnerEmail = new Map();
    const merge = () => {
      const merged = new Map();
      byOwnerId.forEach((v, k) => merged.set(k, v));
      byOwnerEmail.forEach((v, k) => merged.set(k, v));
      const docs = Array.from(merged.values());
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setApplicants(docs);
      setLoadingApplicants(false);
    };

    const qByOwner = query(collection(db, 'applicants'), where('ownerId', '==', effectiveOwnerId));
    const unsubOwner = onSnapshot(
      qByOwner,
      (snap) => {
        byOwnerId.clear();
        snap.docs.forEach((d) => byOwnerId.set(d.id, { id: d.id, ...d.data() }));
        merge();
      },
      (err) => {
        console.error('Applicants listener (ownerId) error:', err);
        setLoadingApplicants(false);
      }
    );

    const emailKey = isDelegatedOwner
      ? (primaryOwnerRow?.contactEmail || primaryOwnerRow?.email || '').trim().toLowerCase()
      : (user.email || '').trim().toLowerCase();
    let unsubEmail = () => {};
    if (emailKey) {
      const qByEmail = query(collection(db, 'applicants'), where('ownerEmail', '==', emailKey));
      unsubEmail = onSnapshot(
        qByEmail,
        (snap) => {
          byOwnerEmail.clear();
          snap.docs.forEach((d) => byOwnerEmail.set(d.id, { id: d.id, ...d.data() }));
          merge();
        },
        (err) => {
          console.error('Applicants listener (ownerEmail) error:', err);
          setLoadingApplicants(false);
        }
      );
    }

    return () => {
      unsubOwner();
      unsubEmail();
    };
  }, [user, effectiveOwnerId, isDelegatedOwner, primaryOwnerRow?.contactEmail, primaryOwnerRow?.email]);

  // 2c. Fetch realtor invitation / connection records for this owner
  useEffect(() => {
    if (isDelegatedOwner) {
      setRealtorConnections([]);
      setRealtorConnectionsError('');
      setLoadingRealtorConnections(false);
      return undefined;
    }
    if (!user?.email) return;
    const q = query(collection(db, 'invitations'), where('ownerEmail', '==', user.email.toLowerCase()));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.sentAt?.seconds || 0) - (a.sentAt?.seconds || 0));
      setRealtorConnections(docs);
      setRealtorConnectionsError('');
      setLoadingRealtorConnections(false);
    }, (err) => {
      console.error('Realtor connections listener error:', err);
      setRealtorConnections([]);
      setRealtorConnectionsError(err?.message || 'Unable to load realtor connection details.');
      setLoadingRealtorConnections(false);
    });
    return () => unsub();
  }, [user?.email, isDelegatedOwner]);

  const handleRespondToRealtorInvite = async (invitationId, nextStatus) => {
    if (!user?.uid || !invitationId) return;

    const actionLabel = nextStatus === 'connected' ? 'accept' : 'reject';
    const confirmed = window.confirm(
      nextStatus === 'connected'
        ? 'Accept this realtor connection? Future applications from this realtor will route to your dashboard.'
        : 'Reject this realtor invitation? It will be removed from your settings view.'
    );
    if (!confirmed) return;

    setRealtorInviteActionId(invitationId);
    setRealtorInviteMessage('');

    try {
      if (nextStatus === 'connected') {
        // Call cloud function to accept invitation (handles relationship creation securely)
        const acceptInvite = httpsCallable(firebaseFunctions, 'acceptOwnerInvite');
        await acceptInvite({ invitationId });
      } else {
        // For rejection, just update the invitation directly
        await updateDoc(doc(db, 'invitations', invitationId), {
          ownerUid: user.uid,
          status: nextStatus,
          respondedAt: serverTimestamp(),
          rejectedAt: serverTimestamp(),
        });
      }

      setRealtorInviteMessage(
        nextStatus === 'connected'
          ? 'Realtor connection accepted.'
          : 'Realtor invitation rejected and removed from your view.'
      );
    } catch (error) {
      console.error(`Error trying to ${actionLabel} realtor invitation:`, error);
      setRealtorInviteMessage(error?.message || `Could not ${actionLabel} this invitation right now.`);
    } finally {
      setRealtorInviteActionId('');
    }
  };

  // After Stripe 3DS / redirect, finalize payment on return to /owner
  useEffect(() => {
    if (!user?.uid) return;
    const qs = new URLSearchParams(window.location.search);
    const redirectStatus = qs.get('redirect_status');
    const piId = qs.get('payment_intent');
    if (!piId || redirectStatus !== 'succeeded') return;

    let pending;
    try {
      pending = JSON.parse(sessionStorage.getItem('td_pending_payment') || 'null');
    } catch {
      return;
    }
    if (!pending?.requestId) return;

    (async () => {
      try {
        const finalize = httpsCallable(firebaseFunctions, 'finalizePayment');
        const resp = await finalize({
          paymentIntentId: piId,
          requestId: pending.requestId,
          billingName: pending.billingName,
          billingEmail: pending.billingEmail,
          cardLast4: null,
        });
        if (!resp.data?.success) return;
        sessionStorage.removeItem('td_pending_payment');
        window.history.replaceState({}, '', window.location.pathname);
        alert('✅ Payment successful! The professional has been notified.');
      } catch (e) {
        console.error('Stripe return finalize error:', e);
      }
    })();
  }, [user]);

  // 3. Fetch maintenance requests for this owner's tenants
  useEffect(() => {
    if (!user || !effectiveOwnerId) return;
    // Query requests where ownerId matches this owner
    const q = query(collection(db, "maintenance_requests"), where("ownerId", "==", effectiveOwnerId));



    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        // Use Promise.all to fetch tenant data for all requests
        Promise.all(
          querySnapshot.docs.map(async (requestDoc) => {
            const data = requestDoc.data();

            let userData = {};
            if (data.tenantId) {
              try {
                const userRef = doc(db, "TenantsData", data.tenantId);
                const userSnap = await getDoc(userRef);
                userData = userSnap.exists() ? userSnap.data() : {};
              } catch (error) {
                console.error('Error fetching tenant data for', data.tenantId, ':', error);
              }
            }

            return {
              id: requestDoc.id,
              ...data,
              tenantInfo: userData
            };
          })
        ).then((requestList) => {
          setRequests(requestList);
        }).catch((error) => {
          console.error('Error processing requests:', error);
        });
      } catch (error) {
        console.error('Error in onSnapshot callback:', error);
      }
    }, (error) => {
      console.error('❌ OwnerDashboard: Error fetching maintenance requests:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
    });



    return () => unsubscribe();
  }, [user, effectiveOwnerId]);

  // Email deep link: /owner?requestId=… opens the request detail modal (after requests load).
  useEffect(() => {
    if (!user?.uid || delegateReadOnly) return;
    const qs = new URLSearchParams(window.location.search);
    const rid = qs.get('requestId')?.trim();
    if (!rid) return;
    if (!Array.isArray(requests) || requests.length === 0) return;
    const match = requests.find((r) => r.id === rid);
    if (!match) return;
    if (selectedRequest?.id === rid) {
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('requestId');
        const next = u.pathname + (u.searchParams.toString() ? `?${u.searchParams.toString()}` : '');
        window.history.replaceState({}, '', next);
      } catch {
        /* ignore */
      }
      return;
    }
    setActiveTab('all-requests');
    setSelectedRequest(match);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('requestId');
      const next = u.pathname + (u.searchParams.toString() ? `?${u.searchParams.toString()}` : '');
      window.history.replaceState({}, '', next);
    } catch {
      /* ignore */
    }
  }, [user?.uid, delegateReadOnly, requests, selectedRequest?.id]);

  const generateShareLink = (unitId) => {
    const baseUrl = window.location.origin;
    const unit = ownerUnits.find(u => u.id === unitId);
    const params = new URLSearchParams({
      unitId: unitId,
      ownerId: effectiveOwnerId
    });



    const tName = unit?.pendingTenantName || unit?.invitedTenant?.name;
    const tEmail = unit?.pendingTenantEmail || unit?.invitedTenant?.email;
    const tPhone = unit?.pendingTenantPhone || unit?.invitedTenant?.phone;
    if (tName) params.append('tenantName', tName);
    if (tEmail) params.append('tenantEmail', tEmail);
    if (tPhone) params.append('tenantPhone', tPhone);



    return `${baseUrl}/login?${params.toString()}`;
  };

  /** When automated invite email fails, still start the 7-day reminder clock so manual link share is covered (see dailyTenantInviteReminders). */
  const markTenantInviteReminderClock = useCallback(async (unitId) => {
    if (!unitId) return;
    try {
      await updateDoc(doc(db, 'units', unitId), {
        tenantInviteSentAt: serverTimestamp(),
        tenantInviteReminderPending: true,
        tenantInviteReminderSentAt: deleteField(),
      });
    } catch (e) {
      console.warn('markTenantInviteReminderClock:', e?.message || e);
    }
  }, []);

  const startEditingUnit = (unit) => {
    setEditingUnitId(unit.id);
    setEditFormData({
      unitNumber: unit.unitNumber,
      address: unit.address,
      monthlyRent: unit.monthlyRent,
      leaseStart: unit.leaseStart || '',
      leaseEnd: unit.leaseEnd || ''
    });
  };

  const saveUnitEdit = async () => {
    if (!editingUnitId) return;
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }
    try {
      const unitRef = doc(db, 'units', editingUnitId);
      await updateDoc(unitRef, {
        unitNumber: editFormData.unitNumber,
        address: editFormData.address,
        monthlyRent: editFormData.monthlyRent,
        leaseStart: editFormData.leaseStart || '',
        leaseEnd: editFormData.leaseEnd || ''
      });
      setEditingUnitId(null);
      setEditFormData({});
    } catch (error) {
      alert('Error updating unit: ' + error.message);
    }
  };

  const cancelEditingUnit = () => {
    setEditingUnitId(null);
    setEditFormData({});
  };

  const deleteUnit = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'units', unitId));
      setActiveTab('all-requests');
    } catch (error) {
      alert('Error deleting unit: ' + error.message);
    }
  };

  // Manage pending tenant info (before actual registration)
  const startEditingPendingTenant = (unit) => {
    setEditingPendingTenant(true);
    setPendingTenantName(unit.pendingTenantName || unit.invitedTenant?.name || '');
    setPendingTenantEmail(unit.pendingTenantEmail || unit.invitedTenant?.email || '');
    setPendingTenantPhone(unit.pendingTenantPhone || unit.invitedTenant?.phone || '');
  };

  const savePendingTenantInfo = async () => {
    if (!activeUnit) return;
    try {
      const name = pendingTenantName.trim();
      const email = pendingTenantEmail.trim();
      const phone = pendingTenantPhone.trim();
      await updateDoc(doc(db, 'units', activeUnit.id), {
        pendingTenantName: name,
        pendingTenantEmail: email,
        pendingTenantPhone: phone,
        invitedTenant: email
          ? { name, email, phone }
          : null,
      });
      setEditingPendingTenant(false);
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        try {
          const sendTenantUnitInvite = httpsCallable(firebaseFunctions, 'sendTenantUnitInvite');
          await sendTenantUnitInvite({ unitId: activeUnit.id });
          alert('✓ Tenant details saved and invitation email sent.');
        } catch (inviteErr) {
          console.error('sendTenantUnitInvite:', inviteErr);
          await markTenantInviteReminderClock(activeUnit.id);
          alert(
            `✓ Tenant details saved, but the email could not be sent: ${inviteErr.message || inviteErr.code || 'error'}. Use "Send Invite Link" to copy the signup URL.`
          );
        }
      } else {
        alert('✓ Tenant details saved');
      }
    } catch (error) {
      alert('Error saving tenant details: ' + error.message);
    }
  };

  const cancelEditingPendingTenant = () => {
    setEditingPendingTenant(false);
    setPendingTenantName('');
    setPendingTenantEmail('');
    setPendingTenantPhone('');
  };

  // Unit settings handlers
  const saveUnitSettings = async () => {
    if (!activeUnit) return;
    if (unitReadyForApplicants && activeTenants.length > 0) {
      setUnitSettingsMessage('❌ Remove the current tenant before marking this unit as vacant and ready for applicants.');
      return;
    }
    try {
      await updateDoc(doc(db, 'units', activeUnit.id), {
        serviceFeeEnabled: unitServiceFeeEnabled,
        serviceFeeAmount: unitServiceFeeEnabled ? parseFloat(unitServiceFeeAmount) : 0,
        readyForApplicants: unitReadyForApplicants,
        vacancyNotes: unitReadyForApplicants ? unitVacancyNotes.trim() : '',
        vacancyUpdatedAt: serverTimestamp()
      });
      setUnitSettingsMessage('✓ Settings saved successfully');
      setTimeout(() => setUnitSettingsMessage(''), 3000);
    } catch (error) {
      setUnitSettingsMessage('❌ Error saving settings: ' + error.message);
    }
  };

  const removeTenant = async (tenant) => {
    if (!window.confirm(`Remove ${tenant.fullName} from this unit? Their account will remain but they will be unlinked from the unit.`)) return;
    try {
      await updateDoc(doc(db, 'TenantsData', tenant.id), {
        unitNumber: '',
        unitId: '',
        ownerId: ''
      });
      try {
        const syncPlacement = httpsCallable(firebaseFunctions, 'syncApplicantPlacementEnded');
        await syncPlacement({ tenantId: tenant.id });
      } catch (syncErr) {
        console.warn('syncApplicantPlacementEnded:', syncErr);
      }
    } catch (error) {
      alert('Error removing tenant: ' + error.message);
    }
  };

  const startEditingTenantInfo = (tenant) => {
    setEditingTenantId(tenant.id);
    setEditTenantEmail(tenant.email || '');
    setEditTenantPhone(tenant.phoneNumber || '');
  };

  const cancelEditingTenantInfo = () => {
    setEditingTenantId(null);
    setEditTenantEmail('');
    setEditTenantPhone('');
  };

  const saveTenantInfo = async () => {
    if (!editingTenantId) return;
    try {
      await updateDoc(doc(db, 'TenantsData', editingTenantId), {
        email: editTenantEmail,
        phoneNumber: editTenantPhone
      });
      cancelEditingTenantInfo();
    } catch (error) {
      alert('Error updating tenant info: ' + error.message);
    }
  };

  // Request editing functions
  const startEditingRequest = (req) => {
    setEditingRequestId(req.id);
    setEditRequestText(req.description || '');
    setEditRequestCategory(req.category || '');
    setEditRequestPriority(req.priority || 'normal');
    setEditRequestLocation(req.location || '');
    setEditRequestImages(req.images || []);
    setEditRequestNewImages([]);
    setEditRequestPermission(req.permissionToEnter || false);
    setEditRequestPreferredContact(req.preferredContact || 'phone');
  };

  const saveRequestEdit = async () => {
    if (!editRequestText.trim() || !editRequestCategory) {
      alert('Description and category are required');
      return;
    }



    setEditRequestUploading(true);
    try {
      // Upload any new images
      let allImageUrls = [...editRequestImages];



      if (editRequestNewImages.length > 0) {
        for (const img of editRequestNewImages) {
          const storageRef = ref(storage, `maintenance_images/owner-edited/${Date.now()}_${img.name}`);
          const snapshot = await uploadBytes(storageRef, img);
          const url = await getDownloadURL(snapshot.ref);
          allImageUrls.push(url);
        }
      }



      const reqRef = doc(db, 'maintenance_requests', editingRequestId);
      await updateDoc(reqRef, {
        description: editRequestText,
        category: editRequestCategory,
        priority: editRequestPriority,
        location: editRequestLocation,
        images: allImageUrls,
        permissionToEnter: editRequestPermission,
        preferredContact: editRequestPreferredContact,
        updatedAt: new Date()
      });



      cancelRequestEdit();
    } catch (error) {
      console.error('Error saving request edit:', error);
      alert('Error saving changes: ' + error.message);
    }
    setEditRequestUploading(false);
  };

  const cancelRequestEdit = () => {
    setEditingRequestId(null);
    setEditRequestText('');
    setEditRequestCategory('');
    setEditRequestPriority('normal');
    setEditRequestLocation('');
    setEditRequestImages([]);
    setEditRequestNewImages([]);
    setEditRequestPermission(false);
    setEditRequestPreferredContact('phone');
  };

  const deleteRequest = async (requestId) => {
    const confirmed = window.confirm('Are you sure you want to delete this maintenance request? This action cannot be undone.');
    if (!confirmed) return;
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }

    try {
      await deleteDoc(doc(db, 'maintenance_requests', requestId));
      if (editingRequestId === requestId) {
        cancelRequestEdit();
      }
    } catch (error) {
      alert('Error deleting request: ' + error.message);
    }
  };

  const submitOwnerModalRating = async () => {
    if (!selectedRequest?.id || !user?.uid) return;
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }
    const stars = ownerModalRatingStars;
    if (!stars || stars < 1 || stars > 5) {
      alert('Please choose a rating from 1 to 5 stars.');
      return;
    }
    const commentRaw = (ownerModalRatingComment || '').trim();
    const ownerRating = {
      stars,
      ownerId: effectiveOwnerId,
      createdAt: new Date(),
    };
    if (commentRaw) {
      ownerRating.comment = commentRaw.slice(0, 2000);
    }
    try {
      setOwnerModalRatingSaving(true);
      await updateDoc(doc(db, 'maintenance_requests', selectedRequest.id), { ownerRating });
      const snap = await getDoc(doc(db, 'maintenance_requests', selectedRequest.id));
      if (snap.exists()) {
        setSelectedRequest({ id: snap.id, ...snap.data() });
      }
      setOwnerModalRatingStars(0);
      setOwnerModalRatingComment('');
    } catch (e) {
      console.error(e);
      alert(e.message || 'Could not save your review.');
    } finally {
      setOwnerModalRatingSaving(false);
    }
  };

  const createOwnerRequest = async () => {
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }
    const selectedUnit = ownerUnits.find(u => u.id === ownerRequestSelectedUnitId) || activeUnit;
    if (!selectedUnit || !ownerRequestDesc.trim() || !ownerRequestCategory) {
      alert('Please select a unit and fill in all required fields');
      return;
    }
    if (!ownerRequestDesc.trim() || ownerRequestDesc.trim().length < 6) {
      alert('Please provide a more detailed description of the issue (at least 6 characters).');
      return;
    }

    try {
      setOwnerRequestSubmitError(null);
      setOwnerRequestSubmitting(true);
      const preferredFirstPayload =
        ownerRequestPreferredFirst && preferredProfessionalIds.length > 0
          ? {
              preferredOfferProIds: [...preferredProfessionalIds],
              preferredOfferUntil: Timestamp.fromMillis(
                Date.now() +
                  Math.max(1, Math.min(168, Number(ownerRequestPreferredFirstHours) || 48)) *
                    60 *
                    60 *
                    1000
              ),
            }
          : {};

      let linkedTenantId = null;
      let linkedTenantInfo = null;
      try {
        const byUnitId = query(
          collection(db, 'TenantsData'),
          where('ownerId', '==', effectiveOwnerId),
          where('unitId', '==', selectedUnit.id)
        );
        let tSnap = await getDocs(byUnitId);
        if (tSnap.empty && selectedUnit.unitNumber != null && String(selectedUnit.unitNumber).trim() !== '') {
          const byNumber = query(
            collection(db, 'TenantsData'),
            where('ownerId', '==', effectiveOwnerId),
            where('unitNumber', '==', String(selectedUnit.unitNumber).trim())
          );
          tSnap = await getDocs(byNumber);
        }
        if (!tSnap.empty) {
          const tdoc = tSnap.docs[0];
          linkedTenantId = tdoc.id;
          const td = tdoc.data() || {};
          const fullName =
            [td.firstName, td.lastName].filter(Boolean).join(' ').trim() ||
            (td.fullName && String(td.fullName).trim()) ||
            (td.name && String(td.name).trim()) ||
            '';
          linkedTenantInfo = {
            fullName,
            email: (td.email && String(td.email).trim()) || '',
            phoneNumber: (td.phoneNumber && String(td.phoneNumber).trim()) || (td.phone && String(td.phone).trim()) || '',
          };
        }
      } catch (tenantLookupErr) {
        console.warn('Owner maintenance: could not resolve tenant for unit', tenantLookupErr);
      }

      await addDoc(collection(db, 'maintenance_requests'), {
        unitId: selectedUnit.id,
        unitNumber: selectedUnit.unitNumber,
        ownerId: effectiveOwnerId,
        createdBy: 'owner',
        ...(linkedTenantId ? { tenantId: linkedTenantId, tenantInfo: linkedTenantInfo } : {}),
        description: ownerRequestDesc,
        category: ownerRequestCategory,
        priority: ownerRequestPriority,
        location: ownerRequestLocation,
        images: ownerRequestImages,
        status: 'pending',
        createdAt: new Date(),
        ownerInfo: {
          firstName: userData?.firstName || 'Owner',
          lastName: userData?.lastName || '',
          email: user.email,
          phone: userData?.phone || ''
        },
        ...preferredFirstPayload,
        ...(ownerTriageSession.messages.length > 0
          ? {
              aiTriageSummary: ownerTriageSession.aiTriageSummary || null,
              aiTriageTranscript: ownerTriageSession.messages,
            }
          : {}),
      });

      // Reset form
      setOwnerRequestDesc('');
      setOwnerRequestCategory('');
      setOwnerRequestPriority('normal');
      setOwnerRequestLocation('');
      setOwnerRequestImages([]);
      setOwnerTriageSession({ messages: [], aiTriageSummary: null });
      setOwnerRequestSubmitting(false);
      setOwnerRequestSuccessOverlay(true);
    } catch (error) {
      setOwnerRequestSubmitError(error.message || 'Could not create the request. Please try again.');
      setOwnerRequestSubmitting(false);
    }
  };

  const handleOwnerRequestImageUpload = async (files) => {
    if (!files) return;



    try {
      setOwnerRequestUploading(true);
      const unitFolder = ownerRequestSelectedUnitId || activeUnit?.id || 'owner';
      for (let file of files) {
        const storageRef = ref(storage, `requests/${unitFolder}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        setOwnerRequestImages(prev => [...prev, url]);
      }
      setOwnerRequestUploading(false);
    } catch (error) {
      alert('Error uploading image: ' + error.message);
      setOwnerRequestUploading(false);
    }
  };

  const addNewUnit = async () => {
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }
    if (!newUnitFormData.unitNumber.trim() || !newUnitFormData.address.trim()) {
      alert('Please fill in unit number and address');
      return;
    }
    // Check for duplicate unit number for this owner
    const duplicate = ownerUnits.find(
      u => u.unitNumber.trim().toLowerCase() === newUnitFormData.unitNumber.trim().toLowerCase()
    );
    if (duplicate) {
      alert(`You already have a unit with number "${newUnitFormData.unitNumber}". Please use a different unit number.`);
      return;
    }
    setSendingInvite(true);
    const tenantEmailTrim = newTenantInfo.email.trim();
    const tenantNameTrim = newTenantInfo.name.trim();
    const tenantPhoneTrim = newTenantInfo.phone.trim();
    const hasTenantInvite = Boolean(tenantEmailTrim);
    let tenantInviteEmailSent = false;
    try {
      // 1. Create the unit (pendingTenant* powers "Current tenant" + invite link query params; invitedTenant for signup prefill)
      const unitRef = await addDoc(collection(db, 'units'), {
        ownerId: effectiveOwnerId,
        unitNumber: newUnitFormData.unitNumber,
        address: newUnitFormData.address,
        zipCode: newUnitFormData.zipCode || '',
        city: newUnitFormData.city || '',
        state: newUnitFormData.state || '',
        monthlyRent: newUnitFormData.monthlyRent || '0',
        leaseStart: newUnitFormData.leaseStart || '',
        leaseEnd: newUnitFormData.leaseEnd || '',
        readyForApplicants: !!newUnitFormData.readyForApplicants,
        vacancyNotes: newUnitFormData.readyForApplicants ? (newUnitFormData.vacancyNotes || '').trim() : '',
        vacancyUpdatedAt: new Date(),
        invitedTenant: hasTenantInvite
          ? { name: tenantNameTrim, email: tenantEmailTrim, phone: tenantPhoneTrim }
          : null,
        pendingTenantName: hasTenantInvite ? tenantNameTrim : '',
        pendingTenantEmail: hasTenantInvite ? tenantEmailTrim : '',
        pendingTenantPhone: hasTenantInvite ? tenantPhoneTrim : '',
        createdAt: new Date()
      });

      // 2. Upload documents if any
      let uploadedDocs = [];
      if (newTenantDocs.length > 0) {
        for (const file of newTenantDocs) {
          const storageRef = ref(storage, `unit-documents/${unitRef.id}/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          uploadedDocs.push({ name: file.name, url: downloadURL });
        }
        // Save document links to the unit
        await updateDoc(doc(db, 'units', unitRef.id), { documents: uploadedDocs });
      }

      // 3. Send invite via Cloud Function (Gmail API — same as owner/realtor invites; replaces unused Firestore "mail" extension queue)
      if (hasTenantInvite) {
        try {
          const sendTenantUnitInvite = httpsCallable(firebaseFunctions, 'sendTenantUnitInvite');
          await sendTenantUnitInvite({ unitId: unitRef.id });
          tenantInviteEmailSent = true;
        } catch (inviteErr) {
          console.error('sendTenantUnitInvite:', inviteErr);
          await markTenantInviteReminderClock(unitRef.id);
          const msg = inviteErr?.message || inviteErr?.code || 'Unknown error';
          alert(
            `Unit created, but the invitation email could not be sent (${msg}). Open this unit → Lease & tenant → "Send Invite Link" to copy the link, or deploy the sendTenantUnitInvite Cloud Function.`
          );
        }
      }

      setNewUnitFormData({ unitNumber: '', address: '', monthlyRent: '', leaseStart: '', leaseEnd: '', readyForApplicants: false, vacancyNotes: '' });
      setNewTenantInfo({ name: '', email: '', phone: '' });
      setNewTenantDocs([]);
      setShowAddUnitForm(false);
      setUnitSubTab('overview');
      setActiveTab(unitRef.id);
      if (hasTenantInvite && tenantInviteEmailSent) {
        alert(`Unit created! An invitation email was sent to ${tenantEmailTrim}.`);
      }
    } catch (error) {
      alert('Error adding unit: ' + error.message);
    } finally {
      setSendingInvite(false);
    }
  };

  // --- PROFILE MANAGEMENT ---
  const startProfileEdit = () => {
    setProfileFirstName(userData?.firstName || '');
    setProfileLastName(userData?.lastName || '');
    setProfileAddress(userData?.homeAddress || '');
    setProfilePhone(userData?.phoneNumber || '');
    setUseDifferentContactEmail(!!userData?.contactEmail);
    setProfileContactEmail(userData?.contactEmail || '');
    setProfileEmail(user?.email || '');
    setProfilePassword('');
    setProfilePasswordConfirm('');
    setProfileMessage('');
    setEditingProfile(true);
  };

  const cancelProfileEdit = () => {
    setEditingProfile(false);
    setProfileFirstName('');
    setProfileLastName('');
    setProfileAddress('');
    setProfilePhone('');
    setUseDifferentContactEmail(false);
    setProfileContactEmail('');
    setProfileEmail('');
    setProfilePassword('');
    setProfilePasswordConfirm('');
    setProfileMessage('');
  };

  const saveProfile = async () => {
    try {
      setProfileMessage('');



      if (!profileFirstName.trim() || !profileLastName.trim() || !profileAddress.trim()) {
        setProfileMessage('Please fill in all required fields');
        return;
      }

      // Update Firestore profile
      const userRef = doc(db, 'OwnersData', user.uid);
      await updateDoc(userRef, {
        firstName: profileFirstName,
        lastName: profileLastName,
        homeAddress: profileAddress,
        phoneNumber: profilePhone,
        contactEmail: useDifferentContactEmail ? profileContactEmail : ''
      });

      // Re-authenticate if updating password or email (Firebase security requirement)
      const needsReauth = profilePassword.trim() || (profileEmail.trim() && profileEmail !== user.email);

      if (needsReauth) {
        const currentPassword = prompt('Firebase requires you to confirm your current password to change sensitive settings.\n\nPlease enter your current password:');
        if (!currentPassword) {
          setProfileMessage('Password update cancelled');
          return;
        }

        try {
          const { reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
          const credential = EmailAuthProvider.credential(user.email, currentPassword);
          await reauthenticateWithCredential(user, credential);
        } catch (reAuthError) {
          setProfileMessage('Current password is incorrect. Please try again.');
          return;
        }
      }

      // Update password if provided
      if (profilePassword.trim()) {
        if (profilePassword !== profilePasswordConfirm) {
          setProfileMessage('Passwords do not match');
          return;
        }
        if (profilePassword.length < 6) {
          setProfileMessage('Password must be at least 6 characters');
          return;
        }

        const { updatePassword } = await import('firebase/auth');
        await updatePassword(user, profilePassword);
      }

      // Update email if changed
      if (profileEmail.trim() && profileEmail !== user.email) {
        const { verifyBeforeUpdateEmail } = await import('firebase/auth');
        await verifyBeforeUpdateEmail(user, profileEmail);
        setProfileMessage('✓ Profile updated! A verification email has been sent to your new email address. Your account email will change after verification is completed.');
        setEditingProfile(false);
        if (onProfileUpdate) onProfileUpdate();
        return;
      }

      setProfileMessage('✓ Profile updated successfully!');
      setEditingProfile(false);
      if (onProfileUpdate) onProfileUpdate();
      setTimeout(() => setProfileMessage(''), 3000);
    } catch (error) {
      setProfileMessage('Error: ' + error.message);
    }
  };

  const sendShareLinkViaEmail = (unitNumber, shareLink) => {
    const subject = `Join Tenant Dispatch - Unit ${unitNumber}`;
    const body = `I'd like you to join my property management system.\n\nClick this link to sign up for Unit ${unitNumber}:\n\n${shareLink}\n\nAfter signing up, you can submit maintenance requests directly through the portal.`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Link copied to clipboard!');
  };

  const activeUnit = ownerUnits.find(u => u.id === activeTab);
  const ownerTriageSelectedUnit =
    ownerUnits.find((u) => u.id === ownerRequestSelectedUnitId) || activeUnit || null;
  const activeTenants = activeUnit ? (unitTenantMap[activeUnit.unitNumber] || []) : [];
  const activeRequests = activeUnit ? requests.filter(req => (req.unitNumber && String(req.unitNumber) === String(activeUnit.unitNumber)) || (req.tenantInfo?.unitNumber && String(req.tenantInfo.unitNumber) === String(activeUnit.unitNumber))) : [];

  const parseRequestDate = (request) => {
    const rawDate = request?.createdAt;
    if (!rawDate) return null;
    if (typeof rawDate.toDate === 'function') return rawDate.toDate();
    if (typeof rawDate.seconds === 'number') return new Date(rawDate.seconds * 1000);

    const date = new Date(rawDate);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  // KPI: unique people (same email = one; else Firestore doc id) — avoids inflated counts from duplicate TenantsData rows.
  const totalTenantsCount = (() => {
    const keys = new Set();
    Object.values(unitTenantMap).forEach((tenantList) => {
      (tenantList || []).forEach((tenant) => {
        if (!tenant) return;
        const emailKey = tenant.email && String(tenant.email).toLowerCase().trim();
        if (emailKey) keys.add(emailKey);
        else if (tenant.id) keys.add(tenant.id);
      });
    });
    return keys.size;
  })();
  const openRequestsCount = requests.filter(isOwnerMaintenanceActive).length;

  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const completedThisMonthCount = requests.filter((request) => {
    const requestDate = parseRequestDate(request);
    return request.status === 'completed' && requestDate && requestDate >= startOfMonth;
  }).length;

  const recentRequests = [...requests]
    .sort((a, b) => {
      const aDate = parseRequestDate(a);
      const bDate = parseRequestDate(b);
      return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
    })
    .slice(0, 6);

  const preferredProfessionalIds = useMemo(() => {
    const src = isDelegatedOwner ? primaryOwnerRow : userData;
    const raw = src?.preferredProfessionalIds;
    if (!Array.isArray(raw)) return [];
    return [...new Set(raw.map(String).filter(Boolean))];
  }, [isDelegatedOwner, primaryOwnerRow, userData]);

  const preferredProJobCounts = useMemo(() => {
    const m = {};
    for (const id of preferredProfessionalIds) {
      m[id] = { completed: 0, assigned: 0 };
    }
    for (const r of requests) {
      const pid = r.assignedProfessional && String(r.assignedProfessional).trim();
      if (!pid || !m[pid]) continue;
      m[pid].assigned += 1;
      if (r.status === 'completed') m[pid].completed += 1;
    }
    return m;
  }, [requests, preferredProfessionalIds]);

  const suggestedProfessionalIds = useMemo(() => {
    const pref = new Set(preferredProfessionalIds);
    const seen = new Set();
    const out = [];
    for (const r of requests) {
      const pid = r.assignedProfessional && String(r.assignedProfessional).trim();
      if (!pid || pref.has(pid) || seen.has(pid)) continue;
      seen.add(pid);
      out.push(pid);
    }
    return out;
  }, [requests, preferredProfessionalIds]);

  useEffect(() => {
    let cancelled = false;
    const ids = [...new Set([...preferredProfessionalIds, ...suggestedProfessionalIds])];
    if (ids.length === 0) {
      setPreferredProDetails({});
      return undefined;
    }
    (async () => {
      const next = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, 'ProfessionalsData', id));
            if (snap.exists()) {
              const d = snap.data() || {};
              next[id] = {
                businessName: d.businessName || d.companyName || 'Professional',
                email: d.email || '',
              };
            } else {
              next[id] = { businessName: 'Unknown professional', email: '' };
            }
          } catch {
            next[id] = { businessName: 'Could not load', email: '' };
          }
        })
      );
      if (!cancelled) setPreferredProDetails(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [preferredProfessionalIds, suggestedProfessionalIds]);

  const addPreferredProfessionalById = useCallback(
    async (professionalId) => {
      if (delegateReadOnly) {
        alert('Your team access is read-only.');
        return;
      }
      const pid = String(professionalId || '').trim();
      if (!pid || !user?.uid) return;
      if (preferredProfessionalIds.includes(pid)) {
        setPreferredProMessageError(false);
        setPreferredProMessage('That professional is already in your list.');
        setTimeout(() => setPreferredProMessage(''), 4000);
        return;
      }
      setPreferredProBusy(true);
      setPreferredProMessage('');
      setPreferredProMessageError(false);
      try {
        await updateDoc(doc(db, 'OwnersData', effectiveOwnerId), {
          preferredProfessionalIds: arrayUnion(pid),
        });
        onProfileUpdate?.();
        setPreferredProMessageError(false);
        setPreferredProMessage('Saved to your preferred list.');
        setTimeout(() => setPreferredProMessage(''), 4000);
      } catch (e) {
        setPreferredProMessageError(true);
        setPreferredProMessage(e?.message || 'Could not save.');
      } finally {
        setPreferredProBusy(false);
      }
    },
    [user?.uid, effectiveOwnerId, preferredProfessionalIds, onProfileUpdate, delegateReadOnly]
  );

  const removePreferredProfessional = useCallback(
    async (professionalId) => {
      if (delegateReadOnly) {
        alert('Your team access is read-only.');
        return;
      }
      const pid = String(professionalId || '').trim();
      if (!pid || !user?.uid) return;
      setPreferredProBusy(true);
      setPreferredProMessage('');
      setPreferredProMessageError(false);
      try {
        await updateDoc(doc(db, 'OwnersData', effectiveOwnerId), {
          preferredProfessionalIds: arrayRemove(pid),
        });
        onProfileUpdate?.();
        setPreferredProMessageError(false);
        setPreferredProMessage('Removed from your list.');
        setTimeout(() => setPreferredProMessage(''), 4000);
      } catch (e) {
        setPreferredProMessageError(true);
        setPreferredProMessage(e?.message || 'Could not remove.');
      } finally {
        setPreferredProBusy(false);
      }
    },
    [user?.uid, effectiveOwnerId, onProfileUpdate, delegateReadOnly]
  );

  useEffect(() => {
    if (!user?.uid || !effectiveOwnerId) return undefined;
    const q = query(collection(db, 'owner_professional_invites'), where('ownerId', '==', effectiveOwnerId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const ta = a.createdAt?.toDate?.()?.getTime?.() || 0;
          const tb = b.createdAt?.toDate?.()?.getTime?.() || 0;
          return tb - ta;
        });
        setOwnerProInvites(rows);
      },
      (err) => console.error('owner_professional_invites:', err)
    );
    return () => unsub();
  }, [user?.uid, effectiveOwnerId]);

  useEffect(() => {
    if (activeTab !== 'preferred-pros') setPreferredProHelpId(null);
    if (activeTab !== 'all-requests') setAllRequestsHelpId(null);
  }, [activeTab]);

  const pendingOwnerProInviteBadge = useMemo(
    () => ownerProInvites.filter((i) => String(i.status || '') === 'sent').length,
    [ownerProInvites]
  );

  const handleWithdrawProInvite = useCallback(async (inviteId) => {
    if (!inviteId) return;
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }
    setPreferredProBusy(true);
    setPreferredProMessage('');
    setPreferredProMessageError(false);
    try {
      await deleteDoc(doc(db, 'owner_professional_invites', inviteId));
      setPreferredProMessageError(false);
      setPreferredProMessage('Invitation withdrawn.');
      setTimeout(() => setPreferredProMessage(''), 3500);
    } catch (e) {
      setPreferredProMessageError(true);
      setPreferredProMessage(e?.message || 'Could not withdraw invitation.');
    } finally {
      setPreferredProBusy(false);
    }
  }, [delegateReadOnly]);

  const submitPreferredProfessionalEmail = useCallback(async () => {
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }
    const raw = preferredProEmail.trim();
    if (!raw) {
      setPreferredProMessageError(true);
      setPreferredProMessage('Enter their email address.');
      return;
    }
    setPreferredProBusy(true);
    setPreferredProMessage('');
    setPreferredProMessageError(false);
    try {
      const fn = httpsCallable(firebaseFunctions, 'sendOwnerProfessionalInvite');
      const { data } = await fn({ inviteeEmail: raw });
      setPreferredProEmail('');
      setPreferredProMessageError(false);
      const outcome = data?.outcome;
      if (outcome === 'added') {
        setPreferredProMessage('They already use Tenant Dispatch — added to your preferred list.');
      } else if (outcome === 'already_listed') {
        setPreferredProMessage('That professional is already in your preferred list.');
      } else if (outcome === 'invited') {
        setPreferredProMessage(
          'Invitation sent. They will appear on your list after they sign up with this email.'
        );
      } else {
        setPreferredProMessage('Done.');
      }
      onProfileUpdate?.();
      setTimeout(() => setPreferredProMessage(''), 5000);
    } catch (e) {
      setPreferredProMessageError(true);
      setPreferredProMessage(e?.message || String(e));
    } finally {
      setPreferredProBusy(false);
    }
  }, [preferredProEmail, onProfileUpdate, delegateReadOnly]);

  const dismissSelectedRequest = useCallback(() => {
    setSelectedRequest(null);
    setPreferredNudgeMessage('');
  }, []);

  const handlePreferredProsNudgeForRequest = useCallback(async () => {
    if (!selectedRequest?.id) return;
    if (delegateReadOnly) {
      alert('Your team access is read-only.');
      return;
    }
    setPreferredNudgeBusy(true);
    setPreferredNudgeMessage('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'ownerNudgePreferredProsForRequest');
      const { data } = await fn({ requestId: selectedRequest.id });
      const n = typeof data?.sent === 'number' ? data.sent : 0;
      setPreferredNudgeMessage(`Email sent to ${n} preferred pro(s).`);
      const snap = await getDoc(doc(db, 'maintenance_requests', selectedRequest.id));
      if (snap.exists()) setSelectedRequest({ id: snap.id, ...snap.data() });
    } catch (e) {
      setPreferredNudgeMessage(e?.message || String(e));
    } finally {
      setPreferredNudgeBusy(false);
    }
  }, [selectedRequest?.id, delegateReadOnly]);

  const sendStaffTeamInvite = useCallback(async () => {
    const raw = staffInviteEmail.trim().toLowerCase();
    if (!raw || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      setStaffInviteMsg('Enter a valid email address.');
      return;
    }
    setStaffInviteBusy(true);
    setStaffInviteMsg('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'sendOwnerStaffInvite');
      await fn({ inviteeEmail: raw, permission: staffInvitePermission });
      setStaffInviteEmail('');
      setStaffInviteMsg('Invitation sent.');
      setTimeout(() => setStaffInviteMsg(''), 5000);
    } catch (e) {
      setStaffInviteMsg(e?.message || String(e));
    } finally {
      setStaffInviteBusy(false);
    }
  }, [staffInviteEmail, staffInvitePermission]);

  const withdrawStaffTeamInvite = useCallback(async (inviteDocId) => {
    if (!inviteDocId) return;
    setStaffInviteBusy(true);
    setStaffInviteMsg('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'withdrawOwnerStaffInvite');
      await fn({ inviteDocId });
      setStaffInviteMsg('Invitation withdrawn.');
      setTimeout(() => setStaffInviteMsg(''), 4000);
    } catch (e) {
      setStaffInviteMsg(e?.message || String(e));
    } finally {
      setStaffInviteBusy(false);
    }
  }, []);

  const revokeStaffTeamMember = useCallback(async (memberUid) => {
    if (!memberUid) return;
    if (
      !window.confirm(
        'Remove this person’s access to your owner dashboard? They may need to refresh or sign in again.'
      )
    ) {
      return;
    }
    setStaffInviteBusy(true);
    setStaffInviteMsg('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'revokeOwnerStaffMember');
      await fn({ memberUid });
      setStaffInviteMsg('Access removed.');
      setTimeout(() => setStaffInviteMsg(''), 4000);
    } catch (e) {
      setStaffInviteMsg(e?.message || String(e));
    } finally {
      setStaffInviteBusy(false);
    }
  }, []);

  const getReportStartDate = () => {
    const now = new Date();
    if (reportRange === '30d') {
      const date = new Date(now);
      date.setDate(now.getDate() - 30);
      return date;
    }
    if (reportRange === '90d') {
      const date = new Date(now);
      date.setDate(now.getDate() - 90);
      return date;
    }
    const date = new Date(now);
    date.setMonth(now.getMonth() - 12);
    return date;
  };

  const reportStartDate = getReportStartDate();

  const reportRequests = requests.filter((request) => {
    const requestDate = parseRequestDate(request);
    return requestDate && requestDate >= reportStartDate;
  });

  const reportCompletedCount = reportRequests.filter((request) => request.status === 'completed').length;
  const reportPendingCount = reportRequests.filter(isOwnerMaintenanceActive).length;
  const reportCancelledCount = reportRequests.filter((request) => request.status === 'cancelled').length;
  const reportCompletionRate = reportRequests.length > 0
    ? Math.round((reportCompletedCount / reportRequests.length) * 100)
    : 0;

  const categoryReportMap = reportRequests.reduce((accumulator, request) => {
    const category = (request.category || 'other').replace('_', ' ');
    accumulator[category] = (accumulator[category] || 0) + 1;
    return accumulator;
  }, {});

  const categoryReportRows = Object.entries(categoryReportMap)
    .sort((a, b) => b[1] - a[1]);

  const maxCategoryCount = Math.max(...categoryReportRows.map(([, count]) => count), 1);

  const monthlyReportMap = reportRequests.reduce((accumulator, request) => {
    const requestDate = parseRequestDate(request);
    if (!requestDate) return accumulator;

    const monthKey = `${requestDate.getFullYear()}-${String(requestDate.getMonth() + 1).padStart(2, '0')}`;
    if (!accumulator[monthKey]) {
      accumulator[monthKey] = {
        monthLabel: requestDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
        total: 0
      };
    }

    accumulator[monthKey].total += 1;
    return accumulator;
  }, {});

  const monthlyReportRows = Object.entries(monthlyReportMap)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, value]) => value);

  const unitRequestReportMap = reportRequests.reduce((accumulator, request) => {
    const unitNumber = request?.tenantInfo?.unitNumber || 'Unknown Unit';
    accumulator[unitNumber] = (accumulator[unitNumber] || 0) + 1;
    return accumulator;
  }, {});

  const unitRequestReportRows = Object.entries(unitRequestReportMap)
    .sort((a, b) => b[1] - a[1]);

  const parsePaymentDate = (request) => {
    const raw = request?.paymentData?.paidAt;
    if (!raw) return parseRequestDate(request);
    if (typeof raw.toDate === 'function') return raw.toDate();
    if (typeof raw.seconds === 'number') return new Date(raw.seconds * 1000);
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? parseRequestDate(request) : date;
  };

  const getReportExpenseAmount = (request) => {
    if (request?.paymentStatus !== 'captured') return 0;
    const fromStripe = parseFloat(request.paymentData?.amount);
    if (!Number.isNaN(fromStripe) && fromStripe > 0) return fromStripe;
    const fromEstimate = parseFloat(request.profesionalEstimate?.amount);
    return !Number.isNaN(fromEstimate) && fromEstimate > 0 ? fromEstimate : 0;
  };

  const reportExpenseRequests = requests.filter((request) => {
    const amount = getReportExpenseAmount(request);
    if (amount <= 0) return false;
    const paidDate = parsePaymentDate(request);
    return paidDate && paidDate >= reportStartDate;
  });

  const reportTotalExpense = reportExpenseRequests.reduce((sum, r) => sum + getReportExpenseAmount(r), 0);
  const reportPaidJobCount = reportExpenseRequests.length;
  const reportAvgExpense = reportPaidJobCount > 0 ? reportTotalExpense / reportPaidJobCount : 0;

  const expenseByUnitMap = reportExpenseRequests.reduce((acc, request) => {
    const unitNumber = request?.tenantInfo?.unitNumber || request.unitNumber || 'Unknown Unit';
    acc[unitNumber] = (acc[unitNumber] || 0) + getReportExpenseAmount(request);
    return acc;
  }, {});

  const expenseByUnitRows = Object.entries(expenseByUnitMap).sort((a, b) => b[1] - a[1]);
  const maxExpenseUnit = Math.max(...expenseByUnitRows.map(([, v]) => v), 1);

  const expenseByMonthMap = reportExpenseRequests.reduce((acc, request) => {
    const paidDate = parsePaymentDate(request);
    if (!paidDate) return acc;
    const monthKey = `${paidDate.getFullYear()}-${String(paidDate.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthKey]) {
      acc[monthKey] = {
        monthLabel: paidDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
        total: 0,
      };
    }
    acc[monthKey].total += getReportExpenseAmount(request);
    return acc;
  }, {});

  const expenseByMonthRows = Object.entries(expenseByMonthMap)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, value]) => value);

  const expenseCategoryMap = reportExpenseRequests.reduce((acc, request) => {
    const category = (request.category || 'other').replace('_', ' ');
    acc[category] = (acc[category] || 0) + getReportExpenseAmount(request);
    return acc;
  }, {});

  const expenseCategoryRows = Object.entries(expenseCategoryMap).sort((a, b) => b[1] - a[1]);
  const maxExpenseCategory = Math.max(...expenseCategoryRows.map(([, v]) => v), 1);

  const formatOwnerCurrency = (n) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const formatCsvCell = (value) => {
    const normalizedValue = value === null || value === undefined ? '' : String(value);
    return `"${normalizedValue.replace(/"/g, '""')}"`;
  };

  const handleExportOwnerReportCsv = () => {
    const csvHeaders = [
      'Request ID',
      'Created Date',
      'Status',
      'Category',
      'Priority',
      'Location',
      'Unit',
      'Tenant',
      'Description'
    ];

    const csvRows = reportRequests.map((request) => {
      const requestDate = parseRequestDate(request);
      return [
        request.id || '',
        requestDate ? requestDate.toLocaleDateString() : '',
        getOwnerMaintenanceDisplayLabel(request).label,
        (request.category || '').replace('_', ' '),
        request.priority || '',
        (request.location || '').replace('_', ' '),
        request?.tenantInfo?.unitNumber || '',
        request?.tenantInfo?.fullName || '',
        request.description || ''
      ];
    });

    const csvContent = [
      csvHeaders.map(formatCsvCell).join(','),
      ...csvRows.map((row) => row.map(formatCsvCell).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);

    link.href = downloadUrl;
    link.download = `owner-report-${reportRange}-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  // Load pending tenant info when unit changes
  useEffect(() => {
    if (activeUnit) {
      setPendingTenantName(activeUnit.pendingTenantName || activeUnit.invitedTenant?.name || '');
      setPendingTenantEmail(activeUnit.pendingTenantEmail || activeUnit.invitedTenant?.email || '');
      setPendingTenantPhone(activeUnit.pendingTenantPhone || activeUnit.invitedTenant?.phone || '');



      // Also load unit settings
      setUnitServiceFeeEnabled(activeUnit.serviceFeeEnabled || false);
      setUnitServiceFeeAmount(activeUnit.serviceFeeAmount?.toString() || '');
      setUnitReadyForApplicants(activeUnit.readyForApplicants || false);
      setUnitVacancyNotes(activeUnit.vacancyNotes || '');
    }
  }, [activeUnit]);

  const trackQuickAction = (id) => {
    setQuickActionUsage(prev => {
      const updated = {
        ...prev,
        [id]: { count: (prev[id]?.count || 0) + 1, lastUsed: Date.now() }
      };
  try { localStorage.setItem(`qa_usage_${user?.uid}`, JSON.stringify(updated)); } catch {}
      return updated;
    });
  };

  const closeOwnerRequestModal = () => {
    setOwnerRequestSuccessOverlay(false);
    setOwnerRequestSubmitError(null);
    setOwnerRequestSubmitting(false);
    setCreatingOwnerRequest(false);
    setOwnerRequestSelectedUnitId('');
    setOwnerRequestDesc('');
    setOwnerRequestCategory('');
    setOwnerRequestPriority('normal');
    setOwnerRequestLocation('');
    setOwnerRequestImages([]);
    setOwnerRequestPreferredFirst(false);
    setOwnerRequestPreferredFirstHours(48);
    setOwnerTriageSession({ messages: [], aiTriageSummary: null });
    setOwnerCreateFormUnlocked(false);
    setOwnerCreateChatUserMessaged(false);
    setOwnerCreateChatAssistantReplied(false);
  };

  useEffect(() => {
    if (!creatingOwnerRequest) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeOwnerRequestModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [creatingOwnerRequest]);

  const ownerDisplayName = userData?.firstName || userData?.fullName || user?.displayName || 'there';
  const greetingHour = new Date().getHours();
  const greetingLabel = greetingHour < 12 ? 'Good morning' : greetingHour < 18 ? 'Good afternoon' : 'Good evening';
  const od = useMemo(() => getOwnerDashboardSkin(ownerThemeDark), [ownerThemeDark]);

  /** Owner create-request modal: priority row colors (avoid light tints + global CSS clashes in dark). */
  const ownerCreateRequestPriorityOptions = useMemo(
    () =>
      ownerThemeDark
        ? [
            {
              value: 'low',
              name: 'Low',
              desc: 'Can wait a few days',
              selBg: '#14532d',
              selBorder: '#22c55e',
              selTitle: '#ecfccb',
              selDesc: '#bbf7d0',
              idleBg: '#0f172a',
              idleBorder: '#334155',
              idleTitle: '#e2e8f0',
              idleDesc: '#94a3b8',
            },
            {
              value: 'normal',
              name: 'Normal',
              desc: 'Within 24-48 hours',
              selBg: '#713f12',
              selBorder: '#eab308',
              selTitle: '#fef9c3',
              selDesc: '#fde68a',
              idleBg: '#0f172a',
              idleBorder: '#334155',
              idleTitle: '#e2e8f0',
              idleDesc: '#94a3b8',
            },
            {
              value: 'urgent',
              name: 'Urgent',
              desc: 'Needs immediate attention',
              selBg: '#7f1d1d',
              selBorder: '#f87171',
              selTitle: '#fecaca',
              selDesc: '#fca5a5',
              idleBg: '#0f172a',
              idleBorder: '#334155',
              idleTitle: '#e2e8f0',
              idleDesc: '#94a3b8',
            },
          ]
        : [
            {
              value: 'low',
              name: 'Low',
              desc: 'Can wait a few days',
              selBg: '#dcfce7',
              selBorder: '#86efac',
              selTitle: '#166534',
              selDesc: '#64748b',
              idleBg: '#fff',
              idleBorder: '#e5e7eb',
              idleTitle: '#1e293b',
              idleDesc: '#64748b',
            },
            {
              value: 'normal',
              name: 'Normal',
              desc: 'Within 24-48 hours',
              selBg: '#fef3c7',
              selBorder: '#fcd34d',
              selTitle: '#92400e',
              selDesc: '#64748b',
              idleBg: '#fff',
              idleBorder: '#e5e7eb',
              idleTitle: '#1e293b',
              idleDesc: '#64748b',
            },
            {
              value: 'urgent',
              name: 'Urgent',
              desc: 'Needs immediate attention',
              selBg: '#fee2e2',
              selBorder: '#fca5a5',
              selTitle: '#991b1b',
              selDesc: '#64748b',
              idleBg: '#fff',
              idleBorder: '#e5e7eb',
              idleTitle: '#1e293b',
              idleDesc: '#64748b',
            },
          ],
    [ownerThemeDark]
  );

  return (
    <div className={`dashboard-container${mobileMenuOpen ? ' owner-mobile-nav-open' : ''}`}>
      {/* Mobile Menu Toggle */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => {
          if (creatingOwnerRequest) {
            closeOwnerRequestModal();
          }
          setMobileMenuOpen(!mobileMenuOpen);
        }}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Shared Sidebar */}
      <Sidebar
        ownerUnits={ownerUnits}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setShowAddUnitForm={setShowAddUnitForm}
        setEditingUnitId={setEditingUnitId}
        onLogout={onLogout}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        ownerInviteBadge={pendingOwnerProInviteBadge}
      />

      {/* MAIN CONTENT AREA */}
      <div className="dashboard-content">
        {showBillingPrompt ? (
          <div
            style={{
              margin: '0 0 16px',
              padding: '12px 16px',
              borderRadius: 12,
              background: ownerThemeDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
              border: ownerThemeDark ? '1px solid rgba(96,165,250,0.35)' : '1px solid #bfdbfe',
              color: ownerThemeDark ? '#e2e8f0' : '#0f172a',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 800 }}>
                Finish setup: complete your {selectedPlan === 'portfolio' ? 'Portfolio+' : 'Growth'} plan checkout.
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={billingBusy}
                  onClick={() => startSubscriptionCheckout(selectedPlan)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#3b82f6',
                    color: '#fff',
                    fontWeight: 800,
                    cursor: billingBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Continue to payment
                </button>
                <button
                  type="button"
                  disabled={billingBusy}
                  onClick={() => setShowBillingPrompt(false)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: ownerThemeDark ? '1px solid #475569' : '1px solid #cbd5e1',
                    background: ownerThemeDark ? '#0f172a' : '#fff',
                    color: ownerThemeDark ? '#e2e8f0' : '#111827',
                    fontWeight: 700,
                    cursor: billingBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Not now
                </button>
              </div>
            </div>
            {billingMessage ? (
              <div style={{ marginTop: 8, fontSize: 13, color: ownerThemeDark ? '#cbd5e1' : '#334155' }}>
                {billingMessage}
              </div>
            ) : null}
          </div>
        ) : null}
        {isDelegatedOwner ? (
          <div
            style={{
              margin: '0 0 16px',
              padding: '12px 16px',
              borderRadius: 10,
              background: delegateReadOnly ? '#eff6ff' : '#f0fdf4',
              border: `1px solid ${delegateReadOnly ? '#bfdbfe' : '#bbf7d0'}`,
              color: '#0f172a',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <strong>Team access:</strong>{' '}
            {delegateReadOnly
              ? 'You can view this account’s properties and maintenance work, but changes are disabled.'
              : 'You are signed in with full dashboard permissions on behalf of the property owner.'}
          </div>
        ) : null}
        {/* DASHBOARD VIEW */}
        {activeTab === 'dashboard' && (
          <div className="owner-dashboard-home" style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <div style={od.heroWrap}>
              <p style={od.heroKicker}>
                {greetingLabel}
              </p>
              <h2 style={od.heroTitle}>
                Welcome back, {ownerDisplayName}
              </h2>
              <p style={od.heroSub}>
                Here’s a quick look at your properties, tenants, and maintenance activity so you can stay on top of what needs attention today.
              </p>
            </div>

            {/* Quick Actions at the top (smoother pill UI) */}
            <div style={od.qaCard}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
                <div>
                  <h3 style={od.qaHeading}>Quick Actions</h3>
                  <p style={od.qaSub}>Frequently used actions appear first</p>
                </div>
                <div style={{position:'relative'}} ref={qaButtonRef}>
                  <button
                    className="qa-dropdown-button"
                    type="button"
                    data-owner-tip-target="owner-qa-toggle"
                    onClick={() => setQaOpen(prev => !prev)}
                    aria-haspopup="menu"
                    aria-expanded={qaOpen}
                    style={od.qaToggleBtn}
                  >
                    <span style={od.qaToggleLabel}>Quick Actions</span>
                    <span style={od.qaToggleChevron}>{qaOpen ? '▴' : '▾'}</span>
                  </button>

                  {qaOpen && (
                    <Portal>
                      <div ref={qaMenuRef} className="qa-dropdown-menu" style={qaMenuStyle}>
                      {[
                        { id: 'review-requests', icon: '📋', label: 'Review all maintenance requests', fn: () => setActiveTab('all-requests') },
                        { id: 'open-report',     icon: '📈', label: 'Open owner report',               fn: () => setActiveTab('report') },
                        ...(!delegateReadOnly
                          ? [
                              { id: 'add-unit',       icon: '➕', label: 'Add a new unit',            fn: () => { setShowAddUnitForm(true); setActiveTab('add'); } },
                              { id: 'create-request', icon: '🔧', label: 'Create maintenance request', fn: () => { setOwnerRequestSelectedUnitId(''); setOwnerRequestSuccessOverlay(false); setOwnerRequestSubmitError(null); setOwnerRequestSubmitting(false); setCreatingOwnerRequest(true); } },
                            ]
                          : []),
                      ]
                        .sort((a, b) => {
                          const au = quickActionUsage[a.id] || { count: 0, lastUsed: 0 };
                          const bu = quickActionUsage[b.id] || { count: 0, lastUsed: 0 };
                          return bu.count !== au.count ? bu.count - au.count : bu.lastUsed - au.lastUsed;
                        })
                        .map(action => (
                          <div
                            key={action.id}
                            className="qa-dropdown-item"
                            role="menuitem"
                            onClick={() => { trackQuickAction(action.id); action.fn(); setQaOpen(false); }}
                            style={od.qaMenuItemRow}
                          >
                            <span style={{width:28,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>{action.icon}</span>
                            <span style={od.qaMenuItemLabel}>{action.label}</span>
                          </div>
                        ))}
                      </div>
                    </Portal>
                  )}
                </div>
              </div>
            </div>

            {/* Stat cards (.owner-dashboard-kpi-grid: mobile 2-col in App.css) */}
            <div className="owner-dashboard-kpi-grid" style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:'14px',marginBottom:'28px'}}>
              <div
                onClick={() => setActiveTab('units')}
                style={{ ...od.kpiTile, ...od.kpiTileClickable, borderLeft: '4px solid #2563eb' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveTab('units');
                  }
                }}
              >
                <p style={od.kpiLabel}>TOTAL UNITS</p>
                <p style={od.kpiValue}>{ownerUnits.length}</p>
              </div>
              <div
                onClick={() => setActiveTab('tenants')}
                style={{ ...od.kpiTile, ...od.kpiTileClickable, borderLeft: '4px solid #0f766e' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveTab('tenants');
                  }
                }}
              >
                <p style={od.kpiLabel}>ACTIVE TENANTS</p>
                <p style={od.kpiValue}>{totalTenantsCount}</p>
              </div>
              <div
                onClick={() => {
                  setStatusFilter('all');
                  setActiveTab('all-requests');
                  onViewRequests?.();
                }}
                style={{ ...od.kpiTile, ...od.kpiTileClickable, borderLeft: '4px solid #b45309' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setStatusFilter('all');
                    setActiveTab('all-requests');
                    onViewRequests?.();
                  }
                }}
              >
                <p style={od.kpiLabel}>ACTIVE REQUESTS</p>
                <p style={od.kpiValue}>{openRequestsCount}</p>
              </div>
              <div
                onClick={() => {
                  setStatusFilter('completed');
                  setActiveTab('all-requests');
                }}
                style={{ ...od.kpiTile, ...od.kpiTileClickable, borderLeft: '4px solid #15803d' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setStatusFilter('completed');
                    setActiveTab('all-requests');
                  }
                }}
              >
                <p style={od.kpiLabel}>COMPLETED THIS MONTH</p>
                <p style={od.kpiValue}>{completedThisMonthCount}</p>
              </div>
            </div>

            {/* Main grid: Recent Requests — min(100%,320px) so one column always fills mobile width */}
            <div
              className="owner-dashboard-recent-section"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
                gap: '16px',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              <div style={od.recentCard}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
                  <h3 style={od.recentTitle}>Recent Requests</h3>
                  <button
                    onClick={() => setActiveTab('all-requests')}
                    style={od.recentViewAll}>
                    View all
                  </button>
                </div>
                {recentRequests.length === 0 ? (
                  <p style={od.recentEmpty}>No recent requests yet.</p>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                    {recentRequests.map((request) => (
                      <div
                        key={request.id}
                        onClick={() => {
                          getDoc(doc(db, 'maintenance_requests', request.id)).then(snap => {
                            if (snap.exists()) {
                              setSelectedRequest({ id: snap.id, ...snap.data() });
                            }
                          }).catch(err => console.error('Error fetching request:', err));
                        }}
                        style={od.recentItem}
                      >
                        <p style={od.recentItemTitle}>
                          {request.tenantInfo?.fullName || 'Tenant'} • Unit {request.unitNumber || request.tenantInfo?.unitNumber || 'N/A'}
                        </p>
                        <p style={od.recentItemMeta}>
                          {(request.category || 'General').replace('_', ' ')} • {getOwnerMaintenanceDisplayLabel(request).label}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* UNITS SUBVIEW */}
        {activeTab === 'units' && (
          <div>
            <UnitListPage
              ownerUnits={ownerUnits}
              unitTenantMap={unitTenantMap}
              setActiveTab={setActiveTab}
              setShowAddUnitForm={setShowAddUnitForm}
              allowAddUnit={!delegateReadOnly}
            />
          </div>
        )}

        {/* TENANTS SUBVIEW */}
        {activeTab === 'tenants' && (
          <div>
            <TenantListPage
              user={user}
              ownerIdForQueries={effectiveOwnerId}
              initialDetailTenantId={tenantsTabFocusId}
              onConsumedInitialDetailTenantId={clearTenantsTabFocus}
            />
          </div>
        )}

        {/* REPORT VIEW */}
        {activeTab === 'report' && (
          <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '18px'}}>
              <h2 style={{margin: 0}}>Owner Report</h2>
              <div style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
                <select
                  value={reportRange}
                  onChange={(event) => setReportRange(event.target.value)}
                  className="input-field"
                  style={{maxWidth: '220px', margin: 0}}
                >
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="12m">Last 12 months</option>
                </select>
                <button
                  onClick={handleExportOwnerReportCsv}
                  disabled={reportRequests.length === 0}
                  style={{
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    background: reportRequests.length === 0 ? '#9ca3af' : '#1d4ed8',
                    color: '#fff',
                    cursor: reportRequests.length === 0 ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  ⬇️ Export CSV
                </button>
              </div>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px'}}>
              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px'}}>
                <p style={{margin: 0, fontSize: '12px', color: '#6b7280'}}>Total Requests</p>
                <p style={{margin: '6px 0 0 0', fontSize: '24px', fontWeight: '700'}}>{reportRequests.length}</p>
              </div>
              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px'}}>
                <p style={{margin: 0, fontSize: '12px', color: '#6b7280'}}>Completed</p>
                <p style={{margin: '6px 0 0 0', fontSize: '24px', fontWeight: '700', color: '#15803d'}}>{reportCompletedCount}</p>
              </div>
              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px'}}>
                <p style={{margin: 0, fontSize: '12px', color: '#6b7280'}}>Active (not completed)</p>
                <p style={{margin: '6px 0 0 0', fontSize: '24px', fontWeight: '700', color: '#b45309'}}>{reportPendingCount}</p>
              </div>
              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px'}}>
                <p style={{margin: 0, fontSize: '12px', color: '#6b7280'}}>Cancelled</p>
                <p style={{margin: '6px 0 0 0', fontSize: '24px', fontWeight: '700', color: '#b91c1c'}}>{reportCancelledCount}</p>
              </div>
              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px'}}>
                <p style={{margin: 0, fontSize: '12px', color: '#6b7280'}}>Completion Rate</p>
                <p style={{margin: '6px 0 0 0', fontSize: '24px', fontWeight: '700'}}>{reportCompletionRate}%</p>
              </div>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px'}}>
              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '18px'}}>
                <h3 style={{margin: '0 0 12px 0', fontSize: '16px'}}>Requests by Category</h3>
                {categoryReportRows.length === 0 ? (
                  <p style={{margin: 0, color: '#6b7280'}}>No request data in this period.</p>
                ) : (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    {categoryReportRows.map(([category, count]) => (
                      <div key={category}>
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px'}}>
                          <span style={{textTransform: 'capitalize'}}>{category}</span>
                          <strong>{count}</strong>
                        </div>
                        <div style={{height: '8px', borderRadius: '999px', background: '#e5e7eb'}}>
                          <div style={{height: '8px', borderRadius: '999px', width: `${(count / maxCategoryCount) * 100}%`, background: '#2563eb'}} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '18px'}}>
                <h3 style={{margin: '0 0 12px 0', fontSize: '16px'}}>Requests by Unit</h3>
                {unitRequestReportRows.length === 0 ? (
                  <p style={{margin: 0, color: '#6b7280'}}>No unit activity in this period.</p>
                ) : (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    {unitRequestReportRows.map(([unitNumber, count]) => (
                      <div key={unitNumber} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{fontSize: '14px'}}>Unit {unitNumber}</span>
                        <strong>{count}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '18px', gridColumn: '1 / -1'}}>
                <h3 style={{margin: '0 0 12px 0', fontSize: '16px'}}>Monthly Request Trend</h3>
                {monthlyReportRows.length === 0 ? (
                  <p style={{margin: 0, color: '#6b7280'}}>No monthly trend data yet.</p>
                ) : (
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px'}}>
                    {monthlyReportRows.map((row) => (
                      <div key={row.monthLabel} style={{border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', background: '#f9fafb'}}>
                        <p style={{margin: 0, fontSize: '12px', color: '#6b7280'}}>{row.monthLabel}</p>
                        <p style={{margin: '6px 0 0 0', fontSize: '22px', fontWeight: '700'}}>{row.total}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Maintenance spend — captured professional payments in selected date range */}
            <div className="owner-report-expense-section" style={{ marginTop: '32px' }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                Maintenance spend
              </h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#64748b', lineHeight: 1.5, maxWidth: '720px' }}>
                Includes completed payments to professionals (Stripe) dated in this report window. Pending estimates and unpaid work are not counted.
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: '12px',
                  marginBottom: '20px',
                }}
              >
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #0d9488' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 600, letterSpacing: '0.04em' }}>TOTAL SPENT</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: 'clamp(18px, 4vw, 22px)', fontWeight: 700, color: '#0f766e' }}>{formatOwnerCurrency(reportTotalExpense)}</p>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #2563eb' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 600, letterSpacing: '0.04em' }}>PAID REPAIRS</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: 'clamp(18px, 4vw, 22px)', fontWeight: 700, color: '#1e293b' }}>{reportPaidJobCount}</p>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #7c3aed' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 600, letterSpacing: '0.04em' }}>AVG / PAID JOB</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: 'clamp(18px, 4vw, 22px)', fontWeight: 700, color: '#1e293b' }}>
                    {reportPaidJobCount ? formatOwnerCurrency(reportAvgExpense) : '—'}
                  </p>
                </div>
              </div>

              {reportExpenseRequests.length === 0 ? (
                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '24px 20px', textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: '15px', color: '#475569', fontWeight: 600 }}>No paid repairs in this period</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#94a3b8', lineHeight: 1.45 }}>
                    After you accept and pay a professional estimate, spending will show here by unit, month, and category.
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '18px', minWidth: 0 }}>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Spend by unit</h4>
                    <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#94a3b8' }}>Paid maintenance by property</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {expenseByUnitRows.map(([unitNumber, amount]) => (
                        <div key={unitNumber}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', fontSize: '13px', marginBottom: '4px' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {unitNumber === 'Unknown Unit' ? 'Unknown unit' : `Unit ${unitNumber}`}
                            </span>
                            <strong style={{ color: '#0f766e', flexShrink: 0 }}>{formatOwnerCurrency(amount)}</strong>
                          </div>
                          <div style={{ height: '8px', borderRadius: '999px', background: '#e5e7eb' }}>
                            <div
                              style={{
                                height: '8px',
                                borderRadius: '999px',
                                width: `${(amount / maxExpenseUnit) * 100}%`,
                                background: 'linear-gradient(90deg, #0d9488, #14b8a6)',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '18px', minWidth: 0 }}>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Spend by month</h4>
                    <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#94a3b8' }}>By payment date</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: '10px' }}>
                      {expenseByMonthRows.map((row) => (
                        <div key={row.monthLabel} style={{ border: '1px solid #99f6e4', borderRadius: '8px', padding: '12px 10px', background: '#f0fdfa' }}>
                          <p style={{ margin: 0, fontSize: '11px', color: '#64748b', fontWeight: 600 }}>{row.monthLabel}</p>
                          <p style={{ margin: '6px 0 0 0', fontSize: '15px', fontWeight: 700, color: '#0f766e', wordBreak: 'break-word' }}>
                            {formatOwnerCurrency(row.total)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '18px', minWidth: 0 }}>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Spend by repair category</h4>
                    <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#94a3b8' }}>Budgeting view — where payments concentrated</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {expenseCategoryRows.map(([category, amount]) => (
                        <div key={category}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', fontSize: '13px', marginBottom: '4px' }}>
                            <span style={{ textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{category}</span>
                            <strong style={{ color: '#1d4ed8', flexShrink: 0 }}>{formatOwnerCurrency(amount)}</strong>
                          </div>
                          <div style={{ height: '8px', borderRadius: '999px', background: '#e5e7eb' }}>
                            <div
                              style={{
                                height: '8px',
                                borderRadius: '999px',
                                width: `${(amount / maxExpenseCategory) * 100}%`,
                                background: 'linear-gradient(90deg, #2563eb, #6366f1)',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* APPLICANTS VIEW */}
        {activeTab === 'applicants' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ margin: '0 0 6px 0', color: '#111827', fontSize: 20, fontWeight: 700 }}>Applicant Queue</h2>
              <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>
                Review rental applications submitted by realtors. You can approve applicants to add them to your tenant roster, request follow-up information, or reject applications—the realtor is notified by email and the packet moves out of your active queue (see Archived).
              </p>
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid #e5e7eb', paddingBottom: 0, overflowX: 'auto' }}>
              {['all', 'pending', 'follow_up', 'approved', 'converted', 'archived'].map((filter) => {
                const filterLabels = {
                  all: 'Active Applications',
                  pending: 'Pending Review',
                  follow_up: 'Follow Up',
                  approved: 'Approved',
                  converted: 'Converted Tenants',
                  archived: 'Archived',
                };
                const isActive = applicantFilter === filter;
                return (
                  <button
                    key={filter}
                    onClick={() => setApplicantFilter(filter)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '12px 16px',
                      fontSize: 14,
                      fontWeight: isActive ? 700 : 600,
                      color: isActive ? '#6366f1' : '#6b7280',
                      borderBottom: isActive ? '2px solid #6366f1' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {filterLabels[filter]}
                  </button>
                );
              })}
            </div>

            {/* Applicants list */}
            {loadingApplicants ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⚙️</div>
                <p>Loading applications…</p>
              </div>
            ) : applicants.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 16, padding: 60, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                <h3 style={{ color: '#374151', marginBottom: 8 }}>No applications yet</h3>
                <p style={{ color: '#9ca3af' }}>Realtors will submit applications here for you to review and decide on.</p>
              </div>
            ) : (
                <div>
                  {applicants
                  .filter((applicant) => {
                    if (applicantFilter === 'all') {
                      // Show only active applications (exclude archived, rejected, and converted tenants)
                      return !['archived', 'rejected', 'converted_to_tenant'].includes(applicant.status);
                    }
                    if (applicantFilter === 'pending') {
                      return applicant.status === 'pending' || applicant.status === 'pending_review';
                    }
                    if (applicantFilter === 'converted') {
                      return applicant.status === 'converted_to_tenant';
                    }
                    if (applicantFilter === 'archived') {
                      return applicant.status === 'archived' || applicant.status === 'rejected';
                    }
                    return applicant.status === applicantFilter;
                  })
                   .map((applicant) => (
                     <ApplicantCard
                      key={applicant.id}
                      applicant={applicant}
                      applicantId={applicant.id}
                      ownerUnits={ownerUnits.filter((unit) => !occupiedUnitIds.has(unit.id))}
                      onUpdated={() => {
                        // Reload applicants - the listener will update automatically
                      }}
                    />
                  ))}
              </div>
            )}
          </div>
        )}

        {/* ALL REQUESTS VIEW */}
        {activeTab === 'all-requests' && (
          <div style={{minHeight: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column'}}>
            <h2
              style={{
                marginBottom: delegateReadOnly ? 12 : 4,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              All Maintenance Requests
              {delegateReadOnly ? (
                <InlineHelpBubble
                  bubbleId="owner-requests-workflow"
                  openId={allRequestsHelpId}
                  onToggle={setAllRequestsHelpId}
                  title="How request status works"
                >
                  Request status is set automatically from the maintenance workflow (
                  <strong>open → in progress with a pro → scheduled visit → completed</strong>). It matches what your
                  professionals see and <strong>cannot be edited manually</strong>.
                </InlineHelpBubble>
              ) : null}
            </h2>

            {!delegateReadOnly ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: 16,
                }}
              >
                <button
                  type="button"
                  className="primary-btn unit-list-add-unit-btn"
                  data-owner-tip-target="owner-add-maintenance-request"
                  onClick={() => {
                    setOwnerRequestSelectedUnitId('');
                    setOwnerRequestSuccessOverlay(false);
                    setOwnerRequestSubmitError(null);
                    setOwnerRequestSubmitting(false);
                    setCreatingOwnerRequest(true);
                  }}
                  style={{
                    flex: '1 1 220px',
                    minWidth: 0,
                    marginBottom: 0,
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#1e293b',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  ➕ Add maintenance request
                </button>
                <InlineHelpBubble
                  bubbleId="owner-requests-workflow"
                  openId={allRequestsHelpId}
                  onToggle={setAllRequestsHelpId}
                  title="How request status works"
                >
                  Request status is set automatically from the maintenance workflow (
                  <strong>open → in progress with a pro → scheduled visit → completed</strong>). It matches what your
                  professionals see and <strong>cannot be edited manually</strong>.
                </InlineHelpBubble>
              </div>
            ) : null}
            
            {requests.length === 0 ? (
              <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
                <p>No maintenance requests yet</p>
              </div>
            ) : (
              <div>
                {/* SUMMARY STATS - Clickable Filters (workflow-aligned) */}
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))', gap: '12px', marginBottom: '20px'}}>
                    <div 
                      onClick={() => setStatusFilter('all')}
                      className={`stat-tile ${statusFilter === 'all' ? 'active' : ''}`}
                      style={{background: '#fff', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '10px', textAlign: 'left', color: '#0f172a', cursor: 'pointer', transition: 'transform 0.12s', transform: statusFilter === 'all' ? 'scale(1.02)' : 'scale(1)', borderLeft: '4px solid #64748b', boxShadow: statusFilter === 'all' ? '0 6px 18px rgba(15,23,42,0.06)' : '0 1px 4px rgba(15,23,42,0.03)'}}
                    >
                      <p style={{margin:0,fontSize:'11px',color:'#64748b',fontWeight:600,letterSpacing:'0.04em'}}>ALL</p>
                      <p style={{margin:'6px 0 0 0',fontSize:'22px',fontWeight:'700',color:'#1e293b'}}>{requests.length}</p>
                    </div>
                    <div 
                      onClick={() => setStatusFilter(statusFilter === 'open' ? 'all' : 'open')}
                      className={`stat-tile ${statusFilter === 'open' ? 'active' : ''}`}
                      style={{background: '#fff', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '10px', textAlign: 'left', color: '#0f172a', cursor: 'pointer', transition: 'transform 0.12s', transform: statusFilter === 'open' ? 'scale(1.02)' : 'scale(1)', borderLeft: '4px solid #f59e0b', boxShadow: statusFilter === 'open' ? '0 6px 18px rgba(245,158,11,0.06)' : '0 1px 4px rgba(15,23,42,0.03)'}}
                    >
                      <p style={{margin:0,fontSize:'11px',color:'#94a3b8',fontWeight:600,letterSpacing:'0.04em'}}>OPEN</p>
                      <p style={{margin:'6px 0 0 0',fontSize:'22px',fontWeight:'700',color:'#1e293b'}}>{requests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'open').length}</p>
                    </div>
                    <div 
                      onClick={() => setStatusFilter(statusFilter === 'in_progress' ? 'all' : 'in_progress')}
                      className={`stat-tile ${statusFilter === 'in_progress' ? 'active' : ''}`}
                      style={{background: '#fff', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '10px', textAlign: 'left', color: '#0f172a', cursor: 'pointer', transition: 'transform 0.12s', transform: statusFilter === 'in_progress' ? 'scale(1.02)' : 'scale(1)', borderLeft: '4px solid #6366f1', boxShadow: statusFilter === 'in_progress' ? '0 6px 18px rgba(99,102,241,0.12)' : '0 1px 4px rgba(15,23,42,0.03)'}}
                    >
                      <p style={{margin:0,fontSize:'11px',color:'#94a3b8',fontWeight:600,letterSpacing:'0.04em'}}>IN PROGRESS</p>
                      <p style={{margin:'6px 0 0 0',fontSize:'22px',fontWeight:'700',color:'#1e293b'}}>{requests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'in_progress').length}</p>
                    </div>
                    <div 
                      onClick={() => setStatusFilter(statusFilter === 'scheduled' ? 'all' : 'scheduled')}
                      className={`stat-tile ${statusFilter === 'scheduled' ? 'active' : ''}`}
                      style={{background: '#fff', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '10px', textAlign: 'left', color: '#0f172a', cursor: 'pointer', transition: 'transform 0.12s', transform: statusFilter === 'scheduled' ? 'scale(1.02)' : 'scale(1)', borderLeft: '4px solid #0284c7', boxShadow: statusFilter === 'scheduled' ? '0 6px 18px rgba(2,132,199,0.1)' : '0 1px 4px rgba(15,23,42,0.03)'}}
                    >
                      <p style={{margin:0,fontSize:'11px',color:'#94a3b8',fontWeight:600,letterSpacing:'0.04em'}}>SCHEDULED</p>
                      <p style={{margin:'6px 0 0 0',fontSize:'22px',fontWeight:'700',color:'#1e293b'}}>{requests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'scheduled').length}</p>
                    </div>
                    <div 
                      onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')}
                      className={`stat-tile ${statusFilter === 'completed' ? 'active' : ''}`}
                      style={{background: '#fff', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '10px', textAlign: 'left', color: '#0f172a', cursor: 'pointer', transition: 'transform 0.12s', transform: statusFilter === 'completed' ? 'scale(1.02)' : 'scale(1)', borderLeft: '4px solid #15803d', boxShadow: statusFilter === 'completed' ? '0 6px 18px rgba(21,128,61,0.06)' : '0 1px 4px rgba(15,23,42,0.03)'}}
                    >
                      <p style={{margin:0,fontSize:'11px',color:'#94a3b8',fontWeight:600,letterSpacing:'0.04em'}}>COMPLETED</p>
                      <p style={{margin:'6px 0 0 0',fontSize:'22px',fontWeight:'700',color:'#1e293b'}}>{requests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'completed').length}</p>
                    </div>
                    <div 
                      onClick={() => setStatusFilter(statusFilter === 'cancelled' ? 'all' : 'cancelled')}
                      className={`stat-tile ${statusFilter === 'cancelled' ? 'active' : ''}`}
                      style={{background: '#fff', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '10px', textAlign: 'left', color: '#0f172a', cursor: 'pointer', transition: 'transform 0.12s', transform: statusFilter === 'cancelled' ? 'scale(1.02)' : 'scale(1)', borderLeft: '4px solid #dc3545', boxShadow: statusFilter === 'cancelled' ? '0 6px 18px rgba(220,53,69,0.06)' : '0 1px 4px rgba(15,23,42,0.03)'}}
                    >
                      <p style={{margin:0,fontSize:'11px',color:'#94a3b8',fontWeight:600,letterSpacing:'0.04em'}}>CANCELLED</p>
                      <p style={{margin:'6px 0 0 0',fontSize:'22px',fontWeight:'700',color:'#1e293b'}}>{requests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'cancelled').length}</p>
                    </div>
                </div>
                
                {/* Active filter indicator */}
                {statusFilter !== 'all' && (
                  <div style={{marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <span style={{fontSize: '14px', color: '#666'}}>Showing: <strong>{OWNER_REQ_STATUS_FILTER_LABELS[statusFilter] || statusFilter}</strong> requests</span>
                    <button 
                      onClick={() => setStatusFilter('all')}
                      style={{background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px'}}
                    >
                      Clear filter ✕
                    </button>
                  </div>
                )}

                {/* ALL REQUESTS LIST */}
                <div style={{flex:1, overflowY:'auto', paddingRight:'10px'}}>
                  <div className="requests-grid">
                  {requests
                    .filter(r => statusFilter === 'all' || getOwnerMaintenanceWorkflowKey(r) === statusFilter)
                    .sort((a, b) => (b.createdAt?.toDate?.() || b.createdAt) - (a.createdAt?.toDate?.() || a.createdAt))
                    .map(req => (
                      <div
                        key={req.id}
                        className="request-card owner-request-card"
                        style={{cursor: editingRequestId === req.id ? 'default' : 'pointer', transition: 'all 0.2s', border: '2px solid transparent'}}
                        onMouseEnter={(e) => editingRequestId !== req.id && (e.currentTarget.style.borderColor = '#007bff')}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                        onClick={() => {
                          if (editingRequestId !== req.id) {
                            // Fetch fresh data from Firestore to ensure we have latest estimate
                            getDoc(doc(db, 'maintenance_requests', req.id)).then(snap => {
                              if (snap.exists()) {
                                setSelectedRequest({ id: snap.id, ...snap.data() });
                              }
                            }).catch(err => console.error('Error fetching request:', err));
                          }
                        }}
                      >
                        {editingRequestId === req.id ? (
                          <div className="edit-form-container">
                            <h3 style={{marginTop: 0, marginBottom: '15px'}}>✏️ Edit Request</h3>
                            
                            {/* Category Selection */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>
                                Category <span style={{color: '#dc3545'}}>*</span>
                              </label>
                              <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px'}}>
                                {[
                                  { value: 'plumbing', label: 'Plumbing', icon: '🚿' },
                                  { value: 'electrical', label: 'Electrical', icon: '⚡' },
                                  { value: 'hvac', label: 'HVAC', icon: '❄️' },
                                  { value: 'appliance', label: 'Appliance', icon: '🔌' },
                                  { value: 'structural', label: 'Structural', icon: '🏠' },
                                  { value: 'pest', label: 'Pest', icon: '🐛' },
                                  { value: 'lock_key', label: 'Locks', icon: '🔑' },
                                  { value: 'other', label: 'Other', icon: '📝' }
                                ].map(cat => (
                                  <div
                                    key={cat.value}
                                    className={`td-request-edit-chip${editRequestCategory === cat.value ? ' td-request-edit-chip--selected' : ''}`}
                                    onClick={() => setEditRequestCategory(cat.value)}
                                    style={{
                                      padding: '8px 4px',
                                      border: editRequestCategory === cat.value ? '2px solid #007bff' : '1px solid #ddd',
                                      borderRadius: '8px',
                                      textAlign: 'center',
                                      cursor: 'pointer',
                                      background: editRequestCategory === cat.value ? '#e7f3ff' : '#fff',
                                      fontSize: '12px'
                                    }}
                                  >
                                    <div style={{fontSize: '18px'}}>{cat.icon}</div>
                                    <div>{cat.label}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Priority */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Priority</label>
                              <div style={{display: 'flex', gap: '10px'}}>
                                {['low', 'normal', 'urgent'].map(p => (
                                  <label key={p} className={`td-request-edit-chip${editRequestPriority === p ? ' td-request-edit-chip--selected' : ''}`} style={{
                                    flex: 1,
                                    padding: '10px',
                                    border: editRequestPriority === p ? '2px solid #007bff' : '1px solid #ddd',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: editRequestPriority === p ? '#e7f3ff' : '#fff',
                                    textTransform: 'capitalize'
                                  }}>
                                    <input type="radio" name="editPriority" value={p} checked={editRequestPriority === p} onChange={() => setEditRequestPriority(p)} style={{display: 'none'}} />
                                    {p === 'urgent' ? '🚨 ' : ''}{p}
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Location */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Location</label>
                              <select
                                value={editRequestLocation}
                                onChange={(e) => setEditRequestLocation(e.target.value)}
                                className="input-field"
                                style={{width: '100%'}}
                              >
                                <option value="">Select location...</option>
                                <option value="kitchen">Kitchen</option>
                                <option value="bathroom">Bathroom</option>
                                <option value="bedroom">Bedroom</option>
                                <option value="living_room">Living Room</option>
                                <option value="laundry">Laundry Area</option>
                                <option value="garage">Garage</option>
                                <option value="exterior">Exterior</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                            
                            {/* Description */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>
                                Description <span style={{color: '#dc3545'}}>*</span>
                              </label>
                              <textarea
                                value={editRequestText}
                                onChange={e => setEditRequestText(e.target.value)}
                                className="textarea-edit"
                                style={{width: '100%', minHeight: '80px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd'}}
                                placeholder="Describe the issue..."
                              />
                            </div>
                            
                            {/* Existing Images */}
                            {editRequestImages.length > 0 && (
                              <div style={{marginBottom: '15px'}}>
                                <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Current Photos</label>
                                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                                  {editRequestImages.map((url, idx) => (
                                    <div key={idx} style={{position: 'relative'}}>
                                      <img src={url} alt={`Thumbnail ${idx + 1}`} style={{width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd'}} />
                                      <button
                                        onClick={() => setEditRequestImages(editRequestImages.filter((_, i) => i !== idx))}
                                        style={{
                                          position: 'absolute',
                                          top: '-8px',
                                          right: '-8px',
                                          width: '22px',
                                          height: '22px',
                                          borderRadius: '50%',
                                          border: 'none',
                                          background: '#dc3545',
                                          color: '#fff',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: 'bold'
                                        }}
                                      >×</button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Add New Images */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Add More Photos</label>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => {
                                  if (e.target.files.length > 0) {
                                    setEditRequestNewImages([...editRequestNewImages, ...Array.from(e.target.files)]);
                                  }
                                  e.target.value = '';
                                }}
                                id="edit-request-camera-upload"
                                style={{display: 'none'}}
                              />
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => {
                                  if (e.target.files.length > 0) {
                                    setEditRequestNewImages([...editRequestNewImages, ...Array.from(e.target.files)]);
                                  }
                                  e.target.value = '';
                                }}
                                id="edit-request-photo-upload"
                                style={{display: 'none'}}
                              />
                              <div style={{display: 'flex', gap: '10px', marginBottom: '8px'}}>
                                <label htmlFor="edit-request-camera-upload" style={{
                                  padding: '10px 16px',
                                  background: '#1976d2',
                                  color: '#fff',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  📸 Camera
                                </label>
                                <label htmlFor="edit-request-photo-upload" style={{
                                  padding: '10px 16px',
                                  background: '#28a745',
                                  color: '#fff',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  🖼️ Gallery
                                </label>
                              </div>
                              {editRequestNewImages.length > 0 && (
                                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px'}}>
                                  {editRequestNewImages.map((img, idx) => (
                                    <div key={idx} style={{position: 'relative'}}>
                                      <img src={URL.createObjectURL(img)} alt={`New ${idx + 1}`} style={{width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #28a745'}} />
                                      <button
                                        onClick={() => setEditRequestNewImages(editRequestNewImages.filter((_, i) => i !== idx))}
                                        style={{
                                          position: 'absolute',
                                          top: '-8px',
                                          right: '-8px',
                                          width: '22px',
                                          height: '22px',
                                          borderRadius: '50%',
                                          border: 'none',
                                          background: '#dc3545',
                                          color: '#fff',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: 'bold'
                                        }}
                                      >×</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Permission to Enter */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                                <input
                                  type="checkbox"
                                  checked={editRequestPermission}
                                  onChange={(e) => setEditRequestPermission(e.target.checked)}
                                  style={{width: '18px', height: '18px'}}
                                />
                                <span>Permission to enter if not home</span>
                              </label>
                            </div>

                            {/* Preferred Contact */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Preferred Contact Method</label>
                              <div style={{display: 'flex', gap: '10px'}}>
                                {['phone', 'text', 'email'].map(method => (
                                  <label key={method} className={`td-request-edit-chip${editRequestPreferredContact === method ? ' td-request-edit-chip--selected' : ''}`} style={{
                                    flex: 1,
                                    padding: '10px',
                                    border: editRequestPreferredContact === method ? '2px solid #007bff' : '1px solid #ddd',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: editRequestPreferredContact === method ? '#e7f3ff' : '#fff',
                                    textTransform: 'capitalize'
                                  }}>
                                    <input type="radio" name="editContact" value={method} checked={editRequestPreferredContact === method} onChange={() => setEditRequestPreferredContact(method)} style={{display: 'none'}} />
                                    {method === 'phone' ? '📱' : method === 'text' ? '💬' : '📧'} {method}
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="edit-controls" style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                              <button 
                                onClick={(e) => { e.stopPropagation(); saveRequestEdit(); }} 
                                disabled={editRequestUploading || !editRequestText.trim() || !editRequestCategory}
                                className="btn-primary"
                                style={{flex: 1}}
                              >
                                {editRequestUploading ? 'Saving...' : '💾 Save Changes'}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); cancelRequestEdit(); }} className="btn-primary cancel" style={{flex: 1}}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="owner-request-card-body">
                            <div className="request-header" style={{position: 'relative'}}>
                              <div>
                                <p style={{margin: '0', fontWeight: 'bold', fontSize: '16px'}}>
                                  {req.tenantInfo?.fullName}
                                </p>
                                <p style={{margin: '5px 0 0 0', fontSize: '14px', color: '#666'}}>
                                  Unit {req.unitNumber || req.tenantInfo?.unitNumber || 'N/A'}
                                </p>
                              </div>
                              <span
                                className={`request-badge ${getOwnerMaintenanceWorkflowKey(req)}`}
                                title="Status updates automatically from the maintenance workflow"
                              >
                                {getOwnerMaintenanceDisplayLabel(req).label}
                              </span>
                            </div>
                            {/* Category and Priority badges */}
                            {(req.category || req.priority) && (
                              <div style={{display: 'flex', gap: '8px', margin: '8px 0', flexWrap: 'wrap'}}>
                                {req.category && (
                                  <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: '#e3f2fd',
                                    color: '#1565c0',
                                    textTransform: 'capitalize'
                                  }}>
                                    {req.category.replace('_', ' ')}
                                  </span>
                                )}
                                {req.priority && req.priority !== 'normal' && (
                                  <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: req.priority === 'urgent' ? '#ffebee' : '#e8f5e9',
                                    color: req.priority === 'urgent' ? '#c62828' : '#2e7d32',
                                    textTransform: 'capitalize'
                                  }}>
                                    {req.priority === 'urgent' ? '🚨 Urgent' : '📋 Low'}
                                  </span>
                                )}
                                {req.location && (
                                  <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    background: '#f5f5f5',
                                    color: '#666',
                                    textTransform: 'capitalize'
                                  }}>
                                    📍 {req.location.replace('_', ' ')}
                                  </span>
                                )}
                              </div>
                            )}
                            <p style={{margin: '10px 0', color: '#333'}}>{req.description}</p>
                            <div className="owner-request-card-spacer">
                              {req.images && req.images.length > 0 && (
                              <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '10px 0 0 0'}}>
                                {req.images.map((url, idx) => (
                                  <img
                                    key={idx}
                                    src={url}
                                    alt={`Attachment ${idx + 1}`}
                                    style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px', cursor: 'pointer', border: '1px solid #ddd'}}
                                    onClick={(e) => { e.stopPropagation(); window.open(url, '_blank'); }}
                                    title="Click to view full size"
                                  />
                                ))}
                              </div>
                              )}
                            </div>
                            </div>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#999', marginBottom: '10px'}}>
                              <span>📱 {req.tenantInfo?.phoneNumber}</span>
                              <span>{new Date(req.createdAt?.toDate?.() || req.createdAt).toLocaleDateString()}</span>
                            </div>
                            {!delegateReadOnly ? (
                              <div style={{display: 'flex', gap: '8px'}}>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); startEditingRequest(req); }}
                                  style={{flex: 1, padding: '10px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'}}
                                >
                                  ✏️ Edit Request
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteRequest(req.id); }}
                                  style={{flex: 1, padding: '10px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'}}
                                >
                                  🗑 Delete
                                </button>
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MAIN CONTENT AREA */}
        {activeTab === 'preferred-pros' && (
          <div>
            <h2 style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
              Preferred professionals
              <InlineHelpBubble
                bubbleId="pp-overview"
                openId={preferredProHelpId}
                onToggle={setPreferredProHelpId}
                title="About this page"
              >
                Invites and a saved list. Add by email or from maintenance history. When you create a request you can
                offer it first to preferred pros for a time window, or email them from an open request (once per 24h).
              </InlineHelpBubble>
            </h2>

            <div className="settings-card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                Add or invite by email
                <InlineHelpBubble
                  bubbleId="pp-email"
                  openId={preferredProHelpId}
                  onToggle={setPreferredProHelpId}
                  title="Email action"
                >
                  Existing professional profile for this address: added to your list. No profile yet: we send a signup
                  link; they appear here after completing signup with the same email.
                </InlineHelpBubble>
              </h3>
              {preferredProMessage ? (
                <div
                  style={{
                    marginBottom: '14px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    lineHeight: 1.45,
                    background: preferredProMessageError ? '#fef2f2' : '#ecfdf5',
                    color: preferredProMessageError ? '#991b1b' : '#065f46',
                    border: `1px solid ${preferredProMessageError ? '#fecaca' : '#6ee7b7'}`,
                  }}
                >
                  {preferredProMessage}
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  marginBottom: '22px',
                  alignItems: 'center',
                }}
              >
                <input
                  type="email"
                  className="input-field"
                  placeholder="Professional email"
                  value={preferredProEmail}
                  onChange={(e) => {
                    setPreferredProEmail(e.target.value);
                    setPreferredProMessage('');
                    setPreferredProMessageError(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitPreferredProfessionalEmail();
                    }
                  }}
                  style={{ flex: '1 1 280px', minWidth: 0, maxWidth: '100%' }}
                  disabled={preferredProBusy}
                />
                <button
                  type="button"
                  className="btn-primary"
                  disabled={preferredProBusy}
                  onClick={() => submitPreferredProfessionalEmail()}
                >
                  Add or send invite
                </button>
              </div>
              <h4
                style={{
                  margin: '0 0 10px',
                  fontSize: '14px',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                Invitations
                <InlineHelpBubble
                  bubbleId="pp-invites"
                  openId={preferredProHelpId}
                  onToggle={setPreferredProHelpId}
                  title="Invitation status"
                >
                  Pending: not used yet (withdraw if you need to). Signed up: they completed registration from the
                  invite.
                </InlineHelpBubble>
              </h4>
              {ownerProInvites.length === 0 ? (
                <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: '14px' }}>None right now.</p>
              ) : (
                <ul style={{ margin: '0 0 18px', padding: 0, listStyle: 'none' }}>
                  {ownerProInvites.map((inv) => (
                    <li
                      key={inv.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        padding: '10px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '10px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{inv.inviteeEmail}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                          {inv.status === 'sent'
                            ? 'Pending signup'
                            : inv.status === 'registered'
                              ? 'Signed up'
                              : String(inv.status || '')}
                          {inv.createdAt?.toDate
                            ? ` · ${inv.createdAt.toDate().toLocaleString()}`
                            : ''}
                        </div>
                      </div>
                      {inv.status === 'sent' ? (
                        <button
                          type="button"
                          className="btn-small cancel"
                          disabled={preferredProBusy}
                          onClick={() => handleWithdrawProInvite(inv.id)}
                        >
                          Withdraw
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              {suggestedProfessionalIds.length > 0 ? (
                <>
                  <p
                    style={{
                      margin: '0 0 8px',
                      fontWeight: 700,
                      fontSize: '13px',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    From maintenance
                    <InlineHelpBubble
                      bubbleId="pp-suggest"
                      openId={preferredProHelpId}
                      onToggle={setPreferredProHelpId}
                      title="Suggested pros"
                    >
                      Assigned on a past ticket, not yet on your preferred list.
                    </InlineHelpBubble>
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
                    {suggestedProfessionalIds.map((id) => (
                      <div
                        key={id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '10px',
                          flexWrap: 'wrap',
                          padding: '10px 12px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>
                          {preferredProDetails[id]?.businessName || `${id.slice(0, 8)}…`}
                        </span>
                        <button
                          type="button"
                          className="btn-small"
                          disabled={preferredProBusy}
                          onClick={() => addPreferredProfessionalById(id)}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              <p
                style={{
                  margin: '0 0 8px',
                  fontWeight: 700,
                  fontSize: '13px',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                Your list ({preferredProfessionalIds.length})
                <InlineHelpBubble
                  bubbleId="pp-list"
                  openId={preferredProHelpId}
                  onToggle={setPreferredProHelpId}
                  title="Preferred list"
                >
                Job counts are from your maintenance history. Used for optional first access when you post jobs and for
                email nudges on open requests. Remove anytime.
                </InlineHelpBubble>
              </p>
              {preferredProfessionalIds.length === 0 ? (
                <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>No preferred professionals yet.</p>
              ) : (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {preferredProfessionalIds.map((id) => (
                    <li
                      key={id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '10px',
                        flexWrap: 'wrap',
                        padding: '10px 0',
                        borderBottom: '1px solid #e5e7eb',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>
                          {preferredProDetails[id]?.businessName || id}
                        </div>
                        {preferredProDetails[id]?.email ? (
                          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                            {preferredProDetails[id].email}
                          </div>
                        ) : null}
                        {preferredProJobCounts[id] ? (
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                            {preferredProJobCounts[id].completed} completed · {preferredProJobCounts[id].assigned}{' '}
                            assigned job{preferredProJobCounts[id].assigned === 1 ? '' : 's'} with you
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="btn-small cancel"
                        disabled={preferredProBusy}
                        onClick={() => removePreferredProfessional(id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* SETTINGS PAGE */}
        {activeTab === 'settings' && (
          <div>
            <h2>Account Settings</h2>

            <DashboardAppearanceSettings style={{ marginBottom: '20px' }} />
            
            {profileMessage && (
              <div style={{
                padding: '15px',
                marginBottom: '20px',
                borderRadius: '5px',
                background: profileMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                color: profileMessage.includes('Error') ? '#721c24' : '#155724',
                border: `1px solid ${profileMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`
              }}>
                {profileMessage}
              </div>
            )}

            {editingProfile ? (
              <div className="form-section" style={{background: '#fff3cd', padding: '20px', borderRadius: '8px', border: '1px solid #ffc107'}}>
                <h3>Edit Profile</h3>
                
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>First Name *</label>
                    <input
                      type="text"
                      value={profileFirstName}
                      onChange={(e) => setProfileFirstName(e.target.value)}
                      className="input-field"
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>Last Name *</label>
                    <input
                      type="text"
                      value={profileLastName}
                      onChange={(e) => setProfileLastName(e.target.value)}
                      className="input-field"
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                <div style={{marginBottom: '15px'}}>
                  <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>Home Address *</label>
                  <input
                    type="text"
                    value={profileAddress}
                    onChange={(e) => setProfileAddress(e.target.value)}
                    className="input-field"
                    placeholder="Your home address"
                  />
                </div>

                <div style={{marginBottom: '15px'}}>
                  <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>Phone Number</label>
                  <input
                    type="text"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    className="input-field"
                    placeholder="Phone number"
                  />
                </div>

                <div style={{marginBottom: '15px'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                    <input
                      type="checkbox"
                      checked={useDifferentContactEmail}
                      onChange={(e) => setUseDifferentContactEmail(e.target.checked)}
                      style={{width: '18px', height: '18px'}}
                    />
                    <span style={{fontWeight: 'bold'}}>Use different email for tenant contact</span>
                  </label>
                  <p style={{color: '#666', fontSize: '13px', margin: '5px 0 0 28px'}}>By default, tenants will see your main email ({user?.email})</p>
                </div>

                {useDifferentContactEmail && (
                  <div style={{marginBottom: '15px', marginLeft: '28px'}}>
                    <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>Contact Email for Tenants</label>
                    <input
                      type="email"
                      value={profileContactEmail}
                      onChange={(e) => setProfileContactEmail(e.target.value)}
                      className="input-field"
                      placeholder="Email for tenant inquiries"
                    />
                  </div>
                )}

                <hr style={{margin: '20px 0'}} />

                <h4>Change Email (Optional)</h4>
                <p style={{color: '#666', fontSize: '14px'}}>A verification email will be sent to confirm the new address</p>
                <div style={{marginBottom: '15px'}}>
                  <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>Email Address</label>
                  <input
                    type="email"
                    value={profileEmail}
                    onChange={(e) => setProfileEmail(e.target.value)}
                    className="input-field"
                    placeholder="New email address"
                  />
                </div>

                <hr style={{margin: '20px 0'}} />

                <h4>Change Password (Optional)</h4>
                <p style={{color: '#666', fontSize: '14px'}}>Leave blank if you don't want to change your password</p>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>New Password</label>
                    <input
                      type="password"
                      value={profilePassword}
                      onChange={(e) => setProfilePassword(e.target.value)}
                      className="input-field"
                      placeholder="Leave blank if no change"
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: 'bold', marginBottom: '5px'}}>Confirm Password</label>
                    <input
                      type="password"
                      value={profilePasswordConfirm}
                      onChange={(e) => setProfilePasswordConfirm(e.target.value)}
                      className="input-field"
                      placeholder="Confirm password"
                    />
                  </div>
                </div>

                <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                  <button onClick={saveProfile} className="btn-primary">
                    Save Changes
                  </button>
                  <button onClick={cancelProfileEdit} className="btn-primary cancel">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="request-card">
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '16px',
                  }}
                >
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <p style={{ margin: '0 0 10px 0' }}><strong>Name:</strong> {userData?.firstName} {userData?.lastName}</p>
                    <p style={{ margin: '0 0 10px 0' }}><strong>Address:</strong> {userData?.homeAddress}</p>
                    <p style={{ margin: '0 0 10px 0' }}><strong>Phone:</strong> {userData?.phoneNumber || 'Not provided'}</p>
                    <p style={{ margin: '0 0 10px 0' }}><strong>Email:</strong> {user.email}</p>
                    <p style={{ margin: '0', color: '#666', fontSize: '14px' }}><strong>Account Created:</strong> {userData?.createdAt ? new Date(userData.createdAt.toDate?.() || userData.createdAt).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={startProfileEdit}
                    className="btn-primary"
                    style={{ width: 'auto', flex: '0 0 auto', alignSelf: 'flex-start' }}
                  >
                    Edit Profile
                  </button>
                </div>
              </div>
            )}

            {!isDelegatedOwner ? (
              <div className="settings-card" style={{ marginTop: '20px', marginBottom: '20px' }}>
                <h3>Authorized users</h3>
                <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                  Invite someone by email to view or help manage this owner account. They sign in with their own Tenant
                  Dispatch login and must not already have rental units on their profile.
                </p>
                {staffInviteMsg ? (
                  <div
                    style={{
                      marginBottom: '12px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      background:
                        staffInviteMsg.includes('sent') ||
                        staffInviteMsg.includes('withdrawn') ||
                        staffInviteMsg.includes('removed')
                          ? '#ecfdf5'
                          : '#fef2f2',
                      color:
                        staffInviteMsg.includes('sent') ||
                        staffInviteMsg.includes('withdrawn') ||
                        staffInviteMsg.includes('removed')
                          ? '#065f46'
                          : '#991b1b',
                      border: `1px solid ${
                        staffInviteMsg.includes('sent') ||
                        staffInviteMsg.includes('withdrawn') ||
                        staffInviteMsg.includes('removed')
                          ? '#6ee7b7'
                          : '#fecaca'
                      }`,
                      fontSize: '14px',
                    }}
                  >
                    {staffInviteMsg}
                  </div>
                ) : null}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginBottom: '12px',
                    alignItems: 'flex-end',
                  }}
                >
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px', fontSize: '13px' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      className="input-field"
                      value={staffInviteEmail}
                      onChange={(e) => setStaffInviteEmail(e.target.value)}
                      placeholder="name@company.com"
                      disabled={staffInviteBusy}
                    />
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px', fontSize: '13px' }}>
                      Access
                    </label>
                    <select
                      className="select-field"
                      value={staffInvitePermission}
                      onChange={(e) => setStaffInvitePermission(e.target.value)}
                      disabled={staffInviteBusy}
                      style={{ minWidth: 140 }}
                    >
                      <option value="read_only">Read-only</option>
                      <option value="full">Full access</option>
                    </select>
                  </div>
                  <button type="button" className="btn-primary" disabled={staffInviteBusy} onClick={sendStaffTeamInvite}>
                    {staffInviteBusy ? 'Sending…' : 'Send invite'}
                  </button>
                </div>
                {(() => {
                  const norm = (v) => String(v || '').trim().toLowerCase();

                  const pickMs = (ts) =>
                    ts?.toDate ? ts.toDate().getTime() : (typeof ts === 'number' ? ts : Number(ts) || 0);

                  // Prefer the latest invite per email (dedupe)
                  const inviteByEmail = new Map();
                  staffInvites.forEach((inv) => {
                    const emailKey = norm(inv.inviteeEmail);
                    if (!emailKey) return;
                    const prev = inviteByEmail.get(emailKey);
                    const prevMs = prev ? pickMs(prev.createdAt) : 0;
                    const nextMs = pickMs(inv.createdAt);
                    if (!prev || nextMs >= prevMs) inviteByEmail.set(emailKey, inv);
                  });

                  // Members keyed by email for 1-row rendering; we keep UID for revoke call
                  const memberByEmail = new Map();
                  staffMembers.forEach((m) => {
                    const emailKey = norm(m.memberEmail) || norm(m.email);
                    if (!emailKey) return;
                    memberByEmail.set(emailKey, m);
                  });

                  const allKeys = new Set([...inviteByEmail.keys(), ...memberByEmail.keys()]);
                  const rows = Array.from(allKeys).map((emailKey) => {
                    const inv = inviteByEmail.get(emailKey) || null;
                    const mem = memberByEmail.get(emailKey) || null;

                    const isActive = Boolean(mem);
                    const permission = String(mem?.permission || inv?.permission || 'read_only') === 'full' ? 'full' : 'read_only';

                    let status = 'pending';
                    if (isActive) status = 'active';
                    else {
                      const rawStatus = String(inv?.status || 'pending');
                      const expMs = pickMs(inv?.expiresAt);
                      const expired = rawStatus === 'pending' && expMs > 0 && expMs < Date.now();
                      status = expired ? 'expired' : (rawStatus === 'accepted' ? 'accepted' : 'pending');
                    }

                    const createdAtMs = Math.max(pickMs(inv?.createdAt), pickMs(mem?.joinedAt));
                    return { emailKey, inv, mem, permission, status, createdAtMs };
                  });

                  rows.sort((a, b) => {
                    // Show active first, then pending, then expired; within group, newest first
                    const rank = (s) => (s === 'active' ? 0 : s === 'pending' ? 1 : s === 'accepted' ? 2 : 3);
                    const ra = rank(a.status);
                    const rb = rank(b.status);
                    if (ra !== rb) return ra - rb;
                    return (b.createdAtMs || 0) - (a.createdAtMs || 0);
                  });

                  if (rows.length === 0) return null;

                  const statusMeta = (s) => {
                    if (s === 'active') return { label: 'Active', style: { background: '#ecfdf5', color: '#065f46', border: '1px solid #6ee7b7' } };
                    if (s === 'pending') return { label: 'Pending', style: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' } };
                    if (s === 'accepted') return { label: 'Accepted', style: { background: '#ecfdf5', color: '#065f46', border: '1px solid #6ee7b7' } };
                    return { label: 'Expired', style: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' } };
                  };

                  const fmtDate = (ts) =>
                    ts ? new Date(ts.toDate ? ts.toDate() : ts).toLocaleDateString() : null;

                  return (
                    <div style={{ marginTop: '16px' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: '15px' }}>People</h4>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {rows.map((r) => {
                          const meta = statusMeta(r.status);
                          const sent = r.inv?.createdAt ? fmtDate(r.inv.createdAt) : null;
                          const joined = r.mem?.joinedAt ? fmtDate(r.mem.joinedAt) : null;
                          const sub =
                            r.status === 'active'
                              ? `${r.permission === 'full' ? 'Full access' : 'Read-only'}${joined ? ` · joined ${joined}` : ''}`
                              : `${r.permission === 'full' ? 'Full access' : 'Read-only'}${sent ? ` · invited ${sent}` : ''}`;
                          return (
                            <li
                              key={r.emailKey}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '10px',
                                flexWrap: 'wrap',
                                padding: '10px 0',
                                borderBottom: '1px solid #e5e7eb',
                              }}
                            >
                              <div>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <div style={{ fontWeight: 700 }}>{r.inv?.inviteeEmail || r.mem?.memberEmail || r.emailKey}</div>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 800,
                                      padding: '3px 10px',
                                      borderRadius: 999,
                                      ...meta.style,
                                    }}
                                  >
                                    {meta.label}
                                  </span>
                                </div>
                                <div style={{ fontSize: '13px', color: '#64748b', marginTop: 2 }}>{sub}</div>
                              </div>
                              {r.status === 'pending' && r.inv ? (
                                <button
                                  type="button"
                                  className="btn-small cancel"
                                  disabled={staffInviteBusy}
                                  onClick={() => withdrawStaffTeamInvite(r.inv.id)}
                                >
                                  Revoke invite
                                </button>
                              ) : r.status === 'active' && r.mem ? (
                                <button
                                  type="button"
                                  className="btn-small cancel"
                                  disabled={staffInviteBusy}
                                  onClick={() => revokeStaffTeamMember(r.mem.id)}
                                >
                                  Remove access
                                </button>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {!isDelegatedOwner ? (
            <div className="settings-card" style={{marginTop: '20px'}}>
              <h3>🤝 Realtor Connection</h3>
              <p style={{color: ownerThemeDark ? '#94a3b8' : '#666', fontSize: '14px', marginBottom: '20px'}}>
                Review the realtor relationship tied to your account. Accepted connections stay active for application routing, and rejected invites are removed from this list.
              </p>

              {realtorInviteMessage && (
                <div style={{
                  background: realtorInviteMessage.includes('accepted')
                    ? (ownerThemeDark ? 'rgba(22, 163, 74, 0.2)' : '#d1fae5')
                    : (ownerThemeDark ? 'rgba(59, 130, 246, 0.18)' : '#eff6ff'),
                  border: `1px solid ${realtorInviteMessage.includes('accepted')
                    ? (ownerThemeDark ? 'rgba(34, 197, 94, 0.55)' : '#6ee7b7')
                    : (ownerThemeDark ? 'rgba(96, 165, 250, 0.5)' : '#bfdbfe')}`,
                  borderRadius: '8px',
                  padding: '12px 14px',
                  color: realtorInviteMessage.includes('accepted')
                    ? (ownerThemeDark ? '#bbf7d0' : '#065f46')
                    : (ownerThemeDark ? '#bfdbfe' : '#1d4ed8'),
                  fontWeight: '600',
                  marginBottom: '16px'
                }}>
                  {realtorInviteMessage}
                </div>
              )}

              {loadingRealtorConnections ? (
                <p style={{margin: 0, color: ownerThemeDark ? '#94a3b8' : '#64748b'}}>Loading realtor connection...</p>
              ) : realtorConnectionsError ? (
                <div style={{
                  background: ownerThemeDark ? 'rgba(234, 88, 12, 0.15)' : '#fff7ed',
                  border: ownerThemeDark ? '1px solid rgba(251, 146, 60, 0.45)' : '1px solid #fdba74',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  color: ownerThemeDark ? '#fed7aa' : '#9a3412'
                }}>
                  {realtorConnectionsError}
                </div>
              ) : realtorConnections.filter((connection) => connection.status !== 'rejected').length === 0 ? (
                <div style={{
                  background: ownerThemeDark ? '#0f172a' : '#f8fafc',
                  border: ownerThemeDark ? '1px solid #334155' : '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <p style={{margin: 0, color: ownerThemeDark ? '#f1f5f9' : '#334155', fontWeight: '600'}}>No realtor connected yet.</p>
                  <p style={{margin: '6px 0 0 0', color: ownerThemeDark ? '#94a3b8' : '#64748b', fontSize: '13px'}}>
                    If a realtor invites you and you register with that same email address, the connection will show here automatically.
                  </p>
                </div>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  {realtorConnections.filter((connection) => connection.status !== 'rejected').map((connection) => {
                    const sentDate = connection.sentAt?.toDate?.() || null;
                    const isConnected = connection.status === 'connected';
                    const canRemoveConnection = connection.status !== 'rejected';
                    const needsResponse = ['pending_signup', 'pending_owner_acceptance', 'registered'].includes(connection.status);
                    return (
                      <div key={connection.id} style={{
                        background: ownerThemeDark ? '#0f172a' : '#f8fafc',
                        border: ownerThemeDark ? '1px solid #334155' : '1px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '16px'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap'}}>
                          <div>
                            <p style={{margin: 0, fontWeight: '700', color: ownerThemeDark ? '#f1f5f9' : '#1e293b'}}>
                              {connection.realtorName || 'Realtor'}
                            </p>
                            <p style={{margin: '4px 0 0 0', color: ownerThemeDark ? '#94a3b8' : '#64748b', fontSize: '13px'}}>
                              {connection.realtorEmail || '—'}
                            </p>
                            <p style={{margin: '8px 0 0 0', color: ownerThemeDark ? '#94a3b8' : '#64748b', fontSize: '12px'}}>
                              Owner email on invite: {connection.ownerEmail || user.email}
                            </p>
                            {sentDate && (
                              <p style={{margin: '4px 0 0 0', color: ownerThemeDark ? '#64748b' : '#94a3b8', fontSize: '12px'}}>
                                Invite sent {sentDate.toLocaleDateString()} at {sentDate.toLocaleTimeString()}
                              </p>
                            )}
                          </div>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: '700',
                            whiteSpace: 'nowrap',
                            background: isConnected
                              ? (ownerThemeDark ? 'rgba(22, 163, 74, 0.25)' : '#dcfce7')
                              : (ownerThemeDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7'),
                            color: isConnected
                              ? (ownerThemeDark ? '#bbf7d0' : '#166534')
                              : (ownerThemeDark ? '#fde68a' : '#92400e'),
                            border: `1px solid ${isConnected
                              ? (ownerThemeDark ? 'rgba(34, 197, 94, 0.45)' : '#86efac')
                              : (ownerThemeDark ? 'rgba(251, 191, 36, 0.5)' : '#fcd34d')}`
                          }}>
                            <span style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: isConnected ? '#16a34a' : '#f59e0b'
                            }} />
                            {isConnected ? 'Connected' : 'Awaiting Your Decision'}
                          </span>
                        </div>
                        {canRemoveConnection && (
                          <div style={{display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center'}}>
                            {needsResponse && (
                              <button
                                onClick={() => handleRespondToRealtorInvite(connection.id, 'connected')}
                                disabled={realtorInviteActionId === connection.id}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  border: 'none',
                                  borderRadius: '6px',
                                  background: '#10b981',
                                  color: 'white',
                                  cursor: realtorInviteActionId === connection.id ? 'not-allowed' : 'pointer',
                                  opacity: realtorInviteActionId === connection.id ? 0.6 : 1,
                                  transition: 'all 0.2s'
                                }}
                              >
                                {realtorInviteActionId === connection.id ? 'Saving...' : 'Accept'}
                              </button>
                            )}
                            <button
                              onClick={() => handleRespondToRealtorInvite(connection.id, 'rejected')}
                              disabled={realtorInviteActionId === connection.id}
                              style={{
                                padding: '6px 12px',
                                fontSize: '13px',
                                fontWeight: '600',
                                border: ownerThemeDark ? '1px solid #475569' : '1px solid #e5e7eb',
                                borderRadius: '6px',
                                background: ownerThemeDark ? '#1e293b' : '#f9fafb',
                                color: ownerThemeDark ? '#f1f5f9' : '#374151',
                                cursor: realtorInviteActionId === connection.id ? 'not-allowed' : 'pointer',
                                opacity: realtorInviteActionId === connection.id ? 0.6 : 1,
                                transition: 'all 0.2s'
                              }}
                            >
                              {isConnected ? 'Remove' : 'Reject'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            ) : null}

            {/* NOTIFICATION SETTINGS */}
            <div className="settings-card" style={{marginTop: '20px'}}>
              <h3>🔔 Notifications</h3>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>Control how you receive updates about maintenance requests.</p>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">Push Notifications</span>
                  <span className="setting-desc">Get alerts on your device for new requests</span>
                </div>
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={notificationsEnabled}
                    onChange={(e) => {
                      setNotificationsEnabled(e.target.checked);
                      localStorage.setItem('notificationsEnabled', JSON.stringify(e.target.checked));
                    }}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">Email Alerts</span>
                  <span className="setting-desc">Receive email when tenants submit requests</span>
                </div>
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={emailAlerts}
                    onChange={(e) => {
                      setEmailAlerts(e.target.checked);
                      localStorage.setItem('emailAlerts', JSON.stringify(e.target.checked));
                    }}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* SUBSCRIPTION */}
            <div className="settings-card" style={{ marginTop: '20px' }}>
              <h3>💳 Subscription</h3>
              <p style={{ color: ownerThemeDark ? '#94a3b8' : '#666', fontSize: '14px', marginBottom: '12px' }}>
                Choose a plan for your owner portal. Professionals are billed per job separately.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: ownerThemeDark ? '#f1f5f9' : '#0f172a' }}>
                  Current plan:{' '}
                  {activePlanKey === 'portfolio'
                    ? 'Portfolio+'
                    : activePlanKey === 'growth'
                      ? 'Growth'
                      : 'Free'}
                </div>
                {subscriptionActive ? (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: ownerThemeDark ? 'rgba(34,197,94,0.18)' : '#dcfce7',
                      color: ownerThemeDark ? '#bbf7d0' : '#166534',
                      border: ownerThemeDark ? '1px solid rgba(34,197,94,0.35)' : '1px solid #86efac',
                    }}
                  >
                    Active
                  </span>
                ) : null}
              </div>

              {billingMessage ? (
                <div style={{ marginTop: 10, fontSize: 13, color: ownerThemeDark ? '#e2e8f0' : '#334155' }}>
                  {billingMessage}
                </div>
              ) : null}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
                <button
                  type="button"
                  disabled={billingBusy}
                  onClick={() => startSubscriptionCheckout('growth')}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: ownerThemeDark ? '1px solid #475569' : '1px solid #e5e7eb',
                    background: ownerThemeDark ? '#1e293b' : '#fff',
                    color: ownerThemeDark ? '#e2e8f0' : '#111827',
                    fontWeight: 700,
                    cursor: billingBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Upgrade to Growth
                </button>
                <button
                  type="button"
                  disabled={billingBusy}
                  onClick={() => startSubscriptionCheckout('portfolio')}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#3b82f6',
                    color: '#fff',
                    fontWeight: 800,
                    cursor: billingBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Upgrade to Portfolio+
                </button>
                {userData?.stripeCustomerId || ownerBilling?.subscriptionId ? (
                  <button
                    type="button"
                    disabled={billingBusy}
                    onClick={openBillingPortal}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: ownerThemeDark ? '1px solid #475569' : '1px solid #e5e7eb',
                      background: ownerThemeDark ? '#0f172a' : '#f9fafb',
                      color: ownerThemeDark ? '#e2e8f0' : '#111827',
                      fontWeight: 700,
                      cursor: billingBusy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Manage billing
                  </button>
                ) : null}
              </div>
            </div>
            
            {/* APP PREFERENCES */}
            <div className="settings-card" style={{marginTop: '20px'}}>
              <h3>📱 App Preferences</h3>
              
              <div className="setting-item" onClick={() => window.location.reload()} style={{cursor: 'pointer'}}>
                <div className="setting-info">
                  <span className="setting-label">Refresh App</span>
                  <span className="setting-desc">Reload to get the latest updates</span>
                </div>
                <span style={{color: '#666'}}>↻</span>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">App Version</span>
                  <span className="setting-desc">Tenant Dispatch</span>
                </div>
                <span style={{color: '#666', fontFamily: 'monospace'}}>1.0.0</span>
              </div>
            </div>
            
            {/* DANGER ZONE */}
            <div className="settings-card danger" style={{marginTop: '20px'}}>
              <h3>⚠️ Account Actions</h3>
              <button 
                onClick={onLogout}
                className="btn-logout-full"
              >
                🚪 Sign Out
              </button>
            </div>
          </div>
        )}

        {/* MAIN CONTENT AREA */}
        {/* ADD UNIT FORM */}
        {showAddUnitForm && (
          <div>
            <h2 style={od.addUnitPageTitle}>Add New Unit</h2>
            <div className="form-section" style={od.addUnitFormShell}>
              <p style={od.addUnitSectionTitle}>Unit Details</p>
              <input
                type="text"
                placeholder="Unit Number (e.g., 101, A, Apt 1)"
                value={newUnitFormData.unitNumber}
                onChange={(e) => setNewUnitFormData({...newUnitFormData, unitNumber: e.target.value})}
                className="input-field"
              />
              <AddressAutocomplete
                dark={ownerThemeDark}
                value={newUnitFormData.address}
                onChange={(addressInput) => {
                  // If it's an object from autocomplete selection, use full data
                  if (typeof addressInput === 'object' && addressInput.address) {
                    setNewUnitFormData({
                      ...newUnitFormData,
                      address: addressInput.address,
                      zipCode: addressInput.zipCode,
                      city: addressInput.city,
                      state: addressInput.state,
                      formattedAddress: addressInput.formattedAddress
                    });
                  } else {
                    // If it's just typing, keep it as string
                    setNewUnitFormData({...newUnitFormData, address: addressInput});
                  }
                }}
                placeholder="Search and select address"
              />
              <input
                type="text"
                placeholder="Monthly Rent (optional)"
                value={newUnitFormData.monthlyRent}
                onChange={(e) => setNewUnitFormData({...newUnitFormData, monthlyRent: e.target.value})}
                className="input-field"
              />
              <div style={{display: 'flex', gap: '10px'}}>
                <div style={{flex: 1}}>
                  <label style={od.addUnitLeaseLabel}>Lease Start Date</label>
                  <input
                    type="date"
                    value={newUnitFormData.leaseStart}
                    onChange={(e) => setNewUnitFormData({...newUnitFormData, leaseStart: e.target.value})}
                    className="input-field"
                    style={{marginBottom: '0'}}
                  />
                </div>
                <div style={{flex: 1}}>
                  <label style={od.addUnitLeaseLabel}>Lease Expiration</label>
                  <input
                    type="date"
                    value={newUnitFormData.leaseEnd}
                    onChange={(e) => setNewUnitFormData({...newUnitFormData, leaseEnd: e.target.value})}
                    className="input-field"
                    style={{marginBottom: '0'}}
                  />
                </div>
              </div>

              {/* Optional Tenant Info */}
              <div style={od.addUnitDivider}>
                <p style={od.addUnitSectionTitleTight}>
                  Invite a Tenant <span style={od.addUnitSectionTitleMuted}>(optional)</span>
                </p>
                <p style={od.addUnitHelpText}>If you add a tenant's email, they'll receive an invitation to register on Tenant Dispatch.</p>
                <input
                  type="text"
                  placeholder="Tenant Full Name"
                  value={newTenantInfo.name}
                  onChange={(e) => setNewTenantInfo({...newTenantInfo, name: e.target.value})}
                  className="input-field"
                />
                <input
                  type="email"
                  placeholder="Tenant Email"
                  value={newTenantInfo.email}
                  onChange={(e) => setNewTenantInfo({...newTenantInfo, email: e.target.value})}
                  className="input-field"
                />
                <input
                  type="text"
                  placeholder="Tenant Phone Number"
                  value={newTenantInfo.phone}
                  onChange={(e) => setNewTenantInfo({...newTenantInfo, phone: e.target.value})}
                  className="input-field"
                />
              </div>

              {/* Document Uploads */}
              <div style={od.addUnitDivider}>
                <p style={od.addUnitSectionTitleTight}>
                  Documents <span style={od.addUnitSectionTitleMuted}>(optional — lease, application, etc.)</span>
                </p>
                <input
                  ref={docInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{display: 'none'}}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setNewTenantDocs(prev => [...prev, ...files]);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  style={od.addUnitDocsButton}
                >
                  + Add Documents
                </button>
                {newTenantDocs.length > 0 && (
                  <ul style={{margin: '0', padding: '0', listStyle: 'none'}}>
                    {newTenantDocs.map((file, i) => (
                      <li key={i} style={od.addUnitDocRow}>
                        <span>📄 {file.name}</span>
                        <span
                          onClick={() => setNewTenantDocs(prev => prev.filter((_, idx) => idx !== i))}
                          style={{
                            cursor: 'pointer',
                            color: ownerThemeDark ? '#f87171' : '#ef4444',
                            fontWeight: '600',
                            padding: '0 4px',
                          }}
                        >✕</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div style={od.addUnitVacantPanel}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px'}}>
                  <div>
                    <p style={od.addUnitVacantTitle}>Vacant and ready for applicants</p>
                    <p style={od.addUnitVacantDesc}>Connected realtors will see this unit in their leasing pipeline and know to start sourcing applicants.</p>
                  </div>
                  <div
                    onClick={() => setNewUnitFormData({...newUnitFormData, readyForApplicants: !newUnitFormData.readyForApplicants})}
                    style={{
                      width: '50px',
                      height: '28px',
                      borderRadius: '14px',
                      background: newUnitFormData.readyForApplicants ? '#16a34a' : ownerThemeDark ? '#475569' : '#d1d5db',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px',
                      transition: 'all 0.2s',
                      flexShrink: 0
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '12px',
                      background: '#fff',
                      transition: 'all 0.2s',
                      marginLeft: newUnitFormData.readyForApplicants ? '22px' : '2px'
                    }} />
                  </div>
                </div>

                {newUnitFormData.readyForApplicants && (
                  <div style={{marginTop: '14px'}}>
                    <label style={od.addUnitNotesLabel}>
                      Notes for realtor <span style={od.addUnitSectionTitleMuted}>(optional)</span>
                    </label>
                    <textarea
                      value={newUnitFormData.vacancyNotes || ''}
                      onChange={(e) => setNewUnitFormData({...newUnitFormData, vacancyNotes: e.target.value})}
                      rows={3}
                      placeholder="Example: Looking for a May move-in, no pets preferred, recently refreshed kitchen."
                      className="input-field"
                      style={{resize: 'vertical'}}
                    />
                  </div>
                )}
              </div>

              <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                <button onClick={addNewUnit} className="btn-primary" disabled={sendingInvite}>
                  {sendingInvite ? 'Creating...' : newTenantInfo.email.trim() ? 'Create Unit & Send Invite' : 'Create Unit'}
                </button>
                <button 
                  onClick={() => {
                    setShowAddUnitForm(false);
                    setNewUnitFormData({unitNumber: '', address: '', monthlyRent: '', leaseStart: '', leaseEnd: '', readyForApplicants: false, vacancyNotes: ''});
                    setNewTenantInfo({ name: '', email: '', phone: '' });
                    setNewTenantDocs([]);
                    if (ownerUnits.length > 0) setActiveTab(ownerUnits[0].id);
                  }} 
                  className="btn-primary cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ACTIVE UNIT DISPLAY */}
        {!showAddUnitForm && activeUnit && (
          <div className="unit-detail-container">
            {/* Unit Header */}
            <div className="unit-header">
              <div className="unit-header-info">
                <h2 style={{margin: 0}}>Unit {activeUnit.unitNumber}</h2>
                <p className="unit-header-address" style={{margin: '5px 0 0', fontSize: '14px'}}>{activeUnit.address}</p>
              </div>
              <div className="unit-header-stats">
                <div className="stat-item">
                  <span className="stat-value">${activeUnit.monthlyRent || '0'}</span>
                  <span className="stat-label">Monthly Rent</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{activeTenants.length}</span>
                  <span className="stat-label">Tenants</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{activeRequests.filter(isOwnerMaintenanceActive).length}</span>
                  <span className="stat-label">Active requests</span>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="unit-tabs">
              <button 
                className={`unit-tab ${unitSubTab === 'overview' ? 'active' : ''}`}
                onClick={() => setUnitSubTab('overview')}
              >
                📋 Overview
              </button>
              <button 
                className={`unit-tab ${unitSubTab === 'tenants' ? 'active' : ''}`}
                onClick={() => setUnitSubTab('tenants')}
              >
                🏷️ Lease & Tenant {activeTenants.length > 0 && <span className="tab-badge">{activeTenants.length}</span>}
              </button>
              <button 
                className={`unit-tab ${unitSubTab === 'requests' ? 'active' : ''}`}
                onClick={() => setUnitSubTab('requests')}
              >
                🔧 Requests {activeRequests.length > 0 && <span className="tab-badge">{activeRequests.length}</span>}
              </button>
              <button 
                className={`unit-tab ${unitSubTab === 'settings' ? 'active' : ''}`}
                onClick={() => setUnitSubTab('settings')}
              >
                ⚙️ Settings
              </button>
            </div>

            {/* Tab Content */}
            <div className="unit-tab-content">
              {/* OVERVIEW TAB */}
              {unitSubTab === 'overview' && (
                <div className="tab-panel">
                  {editingUnitId === activeUnit.id && !delegateReadOnly ? (
                    <div className="edit-form-card">
                      <h3 style={{marginTop: 0}}>Edit Unit Details</h3>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Unit Number</label>
                          <input
                            type="text"
                            value={editFormData.unitNumber}
                            onChange={(e) => setEditFormData({...editFormData, unitNumber: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Monthly Rent</label>
                          <input
                            type="text"
                            value={editFormData.monthlyRent}
                            onChange={(e) => setEditFormData({...editFormData, monthlyRent: e.target.value})}
                            className="input-field"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          type="text"
                          value={editFormData.address}
                          onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                          className="input-field"
                        />
                      </div>
                      <div className="form-actions">
                        <button onClick={saveUnitEdit} className="btn-save">Save Changes</button>
                        <button onClick={cancelEditingUnit} className="btn-cancel">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Property Details Card */}
                      <div className="detail-card">
                        <div className="detail-card-header">
                          <h3>Property Details</h3>
                          {!delegateReadOnly ? (
                            <div style={{display: 'flex', gap: '8px'}}>
                              <button onClick={() => startEditingUnit(activeUnit)} className="btn-edit">
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => deleteUnit(activeUnit.id)}
                                style={{background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: '600'}}
                              >
                                🗑 Delete Unit
                              </button>
                            </div>
                          ) : null}
                        </div>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <span className="detail-label">Unit Number</span>
                            <span className="detail-value">{activeUnit.unitNumber}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Property Address</span>
                            <span className="detail-value">{activeUnit.address}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Added</span>
                            <span className="detail-value">
                              {activeUnit.createdAt ? new Date(activeUnit.createdAt.toDate?.() || activeUnit.createdAt).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Occupancy</span>
                            <span className="detail-value" style={{color: activeTenants.length > 0 ? '#16a34a' : (activeUnit.readyForApplicants ? '#15803d' : '#dc2626'), fontWeight: '600'}}>
                              {activeTenants.length > 0
                                ? `✅ Occupied — ${activeTenants.map(t => t.fullName).join(', ')}`
                                : activeUnit.readyForApplicants
                                  ? '🟢 Vacant — ready for applicants'
                                  : '🔴 Vacant'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="quick-actions-grid">
                        {!delegateReadOnly ? (
                          <div className="quick-action-card" onClick={() => setUnitSubTab('share')}>
                            <span className="quick-action-icon">🔗</span>
                            <span className="quick-action-title">Invite Tenant</span>
                            <span className="quick-action-desc">Share signup link</span>
                          </div>
                        ) : null}
                        <div className="quick-action-card" onClick={() => setUnitSubTab('requests')}>
                          <span className="quick-action-icon">🔧</span>
                          <span className="quick-action-title">View Requests</span>
                          <span className="quick-action-desc">{activeRequests.filter(isOwnerMaintenanceActive).length} active</span>
                        </div>
                        <div className="quick-action-card" onClick={() => setUnitSubTab('tenants')}>
                          <span className="quick-action-icon">🏷️</span>
                          <span className="quick-action-title">Lease & Tenant</span>
                          <span className="quick-action-desc">{activeTenants.length > 0 ? activeTenants[0].fullName : 'Vacant'}</span>
                        </div>
                      </div>

                      {/* Recent Activity */}
                      <div className="detail-card">
                        <h3>Recent Activity</h3>
                        {activeRequests.length === 0 ? (
                          <p className="empty-state">No recent maintenance requests</p>
                        ) : (
                          <div className="activity-list">
                            {activeRequests.slice(0, 3).map(req => (
                              <div key={req.id} className="activity-item">
                                <div className="activity-icon">
                                  {(() => {
                                    const k = getOwnerMaintenanceWorkflowKey(req);
                                    if (k === 'completed') return '✅';
                                    if (k === 'cancelled') return '❌';
                                    if (k === 'scheduled') return '📅';
                                    if (k === 'in_progress') return '🔧';
                                    return '🟡';
                                  })()}
                                </div>
                                <div className="activity-content">
                                  <p className="activity-title">{req.category ? req.category.replace('_', ' ') : 'Maintenance'} Request</p>
                                  <p className="activity-desc">{req.description.substring(0, 60)}...</p>
                                </div>
                                <span className="activity-date">
                                  {new Date(req.createdAt?.toDate?.() || req.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* LEASE & TENANT TAB */}
              {unitSubTab === 'tenants' && (
                <div className="tab-panel">

                  {/* Lease Terms */}
                  <div className="detail-card">
                    <div className="detail-card-header">
                      <h3>📋 Lease Terms</h3>
                      {!delegateReadOnly ? (
                        <button onClick={() => startEditingUnit(activeUnit)} className="btn-edit">✏️ Edit</button>
                      ) : null}
                    </div>
                    {editingUnitId === activeUnit.id && !delegateReadOnly ? (
                      <div>
                        <div className="form-grid">
                          <div className="form-group">
                            <label>Monthly Rent</label>
                            <input type="text" value={editFormData.monthlyRent} onChange={(e) => setEditFormData({...editFormData, monthlyRent: e.target.value})} className="input-field" />
                          </div>
                          <div className="form-group">
                            <label>Lease Start</label>
                            <input type="date" value={editFormData.leaseStart || ''} onChange={(e) => setEditFormData({...editFormData, leaseStart: e.target.value})} className="input-field" />
                          </div>
                          <div className="form-group">
                            <label>Lease Expiration</label>
                            <input type="date" value={editFormData.leaseEnd || ''} onChange={(e) => setEditFormData({...editFormData, leaseEnd: e.target.value})} className="input-field" />
                          </div>
                        </div>
                        <div className="form-actions">
                          <button onClick={saveUnitEdit} className="btn-save">Save</button>
                          <button onClick={cancelEditingUnit} className="btn-cancel">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-label">Monthly Rent</span>
                          <span className="detail-value">${activeUnit.monthlyRent || '0'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Lease Start</span>
                          <span className="detail-value">{activeUnit.leaseStart || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Lease Expiration</span>
                          <span className="detail-value">{activeUnit.leaseEnd || '—'}</span>
                        </div>
                        {activeUnit.leaseStart && activeUnit.leaseEnd && (
                          <div className="detail-item">
                            <span className="detail-label">Status</span>
                            <span className="detail-value" style={{color: new Date(activeUnit.leaseEnd) >= new Date() ? '#16a34a' : '#dc2626', fontWeight: '600'}}>
                              {new Date(activeUnit.leaseEnd) >= new Date() ? '✅ Active' : '⚠️ Expired'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Current Tenant */}
                  <div className="detail-card" style={{marginTop: '16px'}}>
                    <div className="detail-card-header">
                      <h3>👤 Current Tenant</h3>
                      {!delegateReadOnly && activeTenants.length === 0 && !editingPendingTenant && (
                        <button className="btn-primary-outline" onClick={() => startEditingPendingTenant(activeUnit)}>+ Add Details</button>
                      )}
                    </div>
                    
                    {!delegateReadOnly && editingPendingTenant && activeTenants.length === 0 ? (
                      <div className="unit-tenant-pending-form" style={{ padding: '20px', borderRadius: '8px' }}>
                        <h4 style={{ marginTop: 0, marginBottom: '16px' }}>Tenant Information</h4>
                        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div className="form-group">
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', fontSize: '13px' }}>Full Name</label>
                            <input
                              type="text"
                              value={pendingTenantName}
                              onChange={(e) => setPendingTenantName(e.target.value)}
                              placeholder="e.g., John Smith"
                              className="input-field"
                            />
                          </div>
                          <div className="form-group">
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', fontSize: '13px' }}>Email</label>
                            <input
                              type="email"
                              value={pendingTenantEmail}
                              onChange={(e) => setPendingTenantEmail(e.target.value)}
                              placeholder="email@example.com"
                              className="input-field"
                            />
                          </div>
                          <div className="form-group">
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', fontSize: '13px' }}>Phone</label>
                            <input
                              type="tel"
                              value={pendingTenantPhone}
                              onChange={(e) => setPendingTenantPhone(e.target.value)}
                              placeholder="(555) 123-4567"
                              className="input-field"
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                          <button 
                            onClick={savePendingTenantInfo}
                            style={{
                              background: '#3b82f6',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}
                          >
                            ✓ Save & Share
                          </button>
                          <button 
                            onClick={cancelEditingPendingTenant}
                            style={{
                              background: '#e5e7eb',
                              color: '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : activeTenants.length === 0 ? (
                      <div className="empty-state-card" style={{padding: '20px 0'}}>
                        <span className="empty-icon">👤</span>
                        <h4>Vacant</h4>
                        <p>No registered tenant for this unit</p>
                        {(activeUnit.pendingTenantName ||
                          activeUnit.pendingTenantEmail ||
                          activeUnit.pendingTenantPhone ||
                          activeUnit.invitedTenant?.email) ? (
                          <div className="unit-pending-invite-box" style={{ borderRadius: '6px', padding: '12px', marginBottom: '16px', fontSize: '13px' }}>
                            <p style={{ margin: '0 0 4px 0', fontWeight: '600' }}>Pending tenant (not registered yet)</p>
                            {(activeUnit.pendingTenantName || activeUnit.invitedTenant?.name) ? (
                              <p style={{ margin: '0 0 2px 0' }}>Name: {activeUnit.pendingTenantName || activeUnit.invitedTenant?.name || '—'}</p>
                            ) : null}
                            {(activeUnit.pendingTenantEmail || activeUnit.invitedTenant?.email) ? (
                              <p style={{ margin: '0 0 2px 0' }}>Email: {activeUnit.pendingTenantEmail || activeUnit.invitedTenant?.email}</p>
                            ) : null}
                            {(activeUnit.pendingTenantPhone || activeUnit.invitedTenant?.phone) ? (
                              <p style={{ margin: '0' }}>Phone: {activeUnit.pendingTenantPhone || activeUnit.invitedTenant?.phone}</p>
                            ) : null}
                          </div>
                        ) : null}
                        {!delegateReadOnly ? (
                          <button className="btn-primary" onClick={() => { setShowInviteModal(true); setInviteModalUnitId(activeUnit.id); }}>Send Invite Link</button>
                        ) : null}
                      </div>
                    ) : (
                      <div>
                        {activeTenants.map(tenant => (
                          <div key={tenant.id} className="unit-tenant-row" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f1f5f9', gap: '12px', flexWrap: 'wrap'}}>
                            <div style={{display: 'flex', gap: '12px', alignItems: 'flex-start', flex: 1, minWidth: '0'}}>
                              <div className="tenant-avatar" style={{width: '42px', height: '42px', fontSize: '18px', flexShrink: 0}}>
                                {tenant.fullName?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <div style={{minWidth: '0', flex: 1}}>
                                <p style={{margin: '0 0 4px', fontWeight: '600', color: '#1e293b', overflowWrap: 'break-word'}}>{tenant.fullName}</p>
                                <p style={{margin: '0 0 2px', color: '#64748b', fontSize: '13px', overflowWrap: 'break-word'}}>📧 {tenant.email}</p>
                                <p style={{margin: '0 0 2px', color: '#64748b', fontSize: '13px', overflowWrap: 'break-word'}}>📱 {tenant.phoneNumber || 'Not provided'}</p>
                                <p style={{margin: '0', color: '#94a3b8', fontSize: '12px'}}>Registered: {tenant.createdAt ? new Date(tenant.createdAt.toDate?.() || tenant.createdAt).toLocaleDateString() : 'N/A'}</p>
                              </div>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, minWidth: '90px'}}>
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveTab('tenants');
                                  setTenantsTabFocusId(tenant.id);
                                }}
                                style={{background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap'}}
                              >
                                📄 Details
                              </button>
                              {!delegateReadOnly ? (
                                <>
                                  <button
                                    onClick={() => startEditingTenantInfo(tenant)}
                                    style={{background: '#dbeafe', color: '#0284c7', border: '1px solid #7dd3fc', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap'}}
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => removeTenant(tenant)}
                                    style={{
                                      background: ownerThemeDark ? 'rgba(239,68,68,0.15)' : '#fee2e2',
                                      color: ownerThemeDark ? '#fecaca' : '#dc2626',
                                      border: ownerThemeDark ? '1px solid rgba(239,68,68,0.35)' : 'none',
                                      borderRadius: '6px',
                                      padding: '8px 10px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    Remove
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Documents — tenant-scoped (see Tenants tab) */}
                  <div className="detail-card" style={{ marginTop: '16px' }}>
                    <div className="detail-card-header">
                      <h3>📄 Documents</h3>
                    </div>
                    <p style={{ fontSize: '14px', margin: '0 0 12px', lineHeight: 1.55 }}>
                      Lease and tenant files are stored on each <strong>tenant</strong> (not the unit). Open{' '}
                      <button
                        type="button"
                        className="unit-doc-link"
                        onClick={() => setActiveTab('tenants')}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          fontWeight: 700,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Tenants
                      </button>
                      , click a tenant, then upload or view documents. Files sync to the tenant&apos;s Documents tab.
                    </p>
                    {activeTenants.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: '18px', color: '#334155', fontSize: '14px' }}>
                        {activeTenants.map((t) => (
                          <li key={t.id} style={{ marginBottom: '8px' }}>
                            <button
                              type="button"
                              className="unit-doc-link"
                              onClick={() => {
                                setActiveTab('tenants');
                                setTenantsTabFocusId(t.id);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                fontWeight: 600,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                            >
                              {t.fullName || 'Tenant'}
                            </button>
                            {' — documents & details'}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
                        When a tenant is assigned, use the Tenants tab to add lease files for them.
                      </p>
                    )}
                  </div>

                  {editingTenantId && (
                    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
                      <div className="unit-modal-card" style={{borderRadius:'12px',padding:'24px',width:'90%',maxWidth:'400px'}}>
                        <h3 style={{marginTop:0,marginBottom:'20px'}}>✏️ Edit Tenant Contact Info</h3>
                        <div style={{marginBottom:'16px'}}>
                          <label style={{display:'block',fontWeight:'600',marginBottom:'8px'}}>Email</label>
                          <input type="email" value={editTenantEmail} onChange={(e)=>setEditTenantEmail(e.target.value)} className="input-field" style={{width:'100%'}} />
                        </div>
                        <div style={{marginBottom:'24px'}}>
                          <label style={{display:'block',fontWeight:'600',marginBottom:'8px'}}>Phone</label>
                          <input type="tel" value={editTenantPhone} onChange={(e)=>setEditTenantPhone(e.target.value)} className="input-field" style={{width:'100%'}} />
                        </div>
                        <div style={{display:'flex',gap:'12px'}}>
                          <button onClick={saveTenantInfo} style={{flex:1,background:'#16a34a',color:'#fff',border:'none',borderRadius:'6px',padding:'10px',cursor:'pointer',fontWeight:'600'}}>✓ Save</button>
                          <button onClick={cancelEditingTenantInfo} style={{flex:1,background:'#e2e8f0',color:'#64748b',border:'none',borderRadius:'6px',padding:'10px',cursor:'pointer',fontWeight:'600'}}>✕ Cancel</button>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* REQUESTS TAB */}
              {unitSubTab === 'requests' && (
                <div className="tab-panel">
                  <div className="section-header">
                    <h3>Maintenance Requests</h3>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                      {!delegateReadOnly ? (
                        <button 
                          onClick={() => { setOwnerRequestSelectedUnitId(activeUnit?.id || ''); setOwnerRequestSuccessOverlay(false); setOwnerRequestSubmitError(null); setOwnerRequestSubmitting(false); setCreatingOwnerRequest(true); }}
                          style={{background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'}}
                        >
                          ➕ Create Request
                        </button>
                      ) : null}
                      <div className="request-filters" style={{ flexWrap: 'wrap' }}>
                        <span className="filter-badge open">{activeRequests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'open').length} Open</span>
                        <span className="filter-badge in_progress">{activeRequests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'in_progress').length} In progress</span>
                        <span className="filter-badge scheduled">{activeRequests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'scheduled').length} Scheduled</span>
                        <span className="filter-badge completed">{activeRequests.filter(r => getOwnerMaintenanceWorkflowKey(r) === 'completed').length} Completed</span>
                      </div>
                    </div>
                  </div>
                  
                  {activeRequests.length === 0 ? (
                    <div className="empty-state-card">
                      <span className="empty-icon">🔧</span>
                      <h4>No Maintenance Requests</h4>
                      <p>When tenants submit requests, they'll appear here</p>
                    </div>
                  ) : (
                    <div className="requests-list">
                      {activeRequests
                        .sort((a, b) => (b.createdAt?.toDate?.() || b.createdAt) - (a.createdAt?.toDate?.() || a.createdAt))
                        .map(req => (
                          <div key={req.id} className="request-item-card" onClick={() => {
                            // Fetch fresh data from Firestore to ensure we have latest estimate
                            getDoc(doc(db, 'maintenance_requests', req.id)).then(snap => {
                              if (snap.exists()) {
                                setSelectedRequest({ id: snap.id, ...snap.data() });
                              }
                            }).catch(err => console.error('Error fetching request:', err));
                          }} style={{cursor: 'pointer'}}>
                            <div className="request-item-header">
                              <div className="request-meta">
                                <span className={`status-badge ${getOwnerMaintenanceWorkflowKey(req)}`}>{getOwnerMaintenanceDisplayLabel(req).label}</span>
                                {req.priority && req.priority !== 'normal' && (
                                  <span className={`priority-badge ${req.priority}`}>
                                    {req.priority === 'urgent' ? '🚨 Urgent' : '📋 Low'}
                                  </span>
                                )}
                                {req.category && (
                                  <span className="category-badge">{req.category.replace('_', ' ')}</span>
                                )}
                                {hasPendingMeetingProposals(req) && (
                                  <span className="category-badge unit-meeting-proposals-chip">
                                    📅 {normalizeMeetingProposals(req).length} pro{normalizeMeetingProposals(req).length === 1 ? '' : 's'} · times
                                  </span>
                                )}
                              </div>
                              <div className="request-actions" onClick={(e) => e.stopPropagation()}>
                                {!delegateReadOnly ? (
                                  <button
                                    onClick={() => deleteRequest(req.id)}
                                    style={{
                                      background: '#dc3545',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      fontWeight: '600'
                                    }}
                                  >
                                    🗑 Delete
                                  </button>
                                ) : null}
                              </div>
                            </div>
                            
                            <div className="request-item-body">
                              <p className="request-description">{req.description}</p>
                              
                              {req.location && (
                                <p className="request-location">📍 Location: {req.location.replace('_', ' ')}</p>
                              )}
                              
                              {req.images && req.images.length > 0 && (
                                <div className="request-images">
                                  {req.images.map((url, idx) => (
                                    <img
                                      key={idx}
                                      src={url}
                                      alt={`Attachment ${idx + 1}`}
                                      onClick={() => window.open(url, '_blank')}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <div className="request-item-footer">
                              <div className="requester-info">
                                <span className="requester-name">{req.tenantInfo?.fullName}</span>
                                <span className="requester-phone">📱 {req.tenantInfo?.phoneNumber}</span>
                              </div>
                              <span className="request-date">
                                {new Date(req.createdAt?.toDate?.() || req.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* SETTINGS TAB */}
              {unitSubTab === 'settings' && (
                <div className="tab-panel">
                  <div className="unit-settings-shell" style={{ padding: '30px', borderRadius: '12px', border: '1px solid #e5e7eb', maxWidth: '500px' }}>
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ margin: '0 0 8px 0' }}>⚙️ Unit Settings</h3>
                      <p className="unit-settings-muted" style={{ margin: 0, fontSize: '14px' }}>Configure settings for this unit</p>
                    </div>

                    {delegateReadOnly ? (
                      <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#0f172a', fontSize: 13, lineHeight: 1.5 }}>
                        <strong>Read-only access:</strong> unit settings are disabled for authorized users with read-only permissions.
                      </div>
                    ) : null}

                    {unitSettingsMessage && (
                      <div
                        className={
                          unitSettingsMessage.includes('✓')
                            ? 'unit-settings-flash unit-settings-flash--success'
                            : 'unit-settings-flash unit-settings-flash--error'
                        }
                        style={{
                        padding: '12px 16px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        fontSize: '14px',
                      }}>
                        {unitSettingsMessage}
                      </div>
                    )}

                    {/* Service Fee Toggle */}
                    <div className="unit-settings-callout" style={{ marginBottom: '24px', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div>
                          <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                            💰 Tenant pays service fee
                          </label>
                          <p className="unit-settings-muted" style={{ margin: 0, fontSize: '13px' }}>
                            Tenant will be charged this fee when submitting repair requests
                          </p>
                        </div>
                        <div style={{
                          width: '50px',
                          height: '28px',
                          borderRadius: '14px',
                          background: unitServiceFeeEnabled ? '#3b82f6' : '#d1d5db',
                          cursor: delegateReadOnly ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '2px',
                          transition: 'all 0.2s',
                          flexShrink: 0
                        }}
                        onClick={() => { if (!delegateReadOnly) setUnitServiceFeeEnabled(!unitServiceFeeEnabled); }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '12px',
                            background: '#fff',
                            transition: 'all 0.2s',
                            marginLeft: unitServiceFeeEnabled ? '22px' : '2px'
                          }} />
                        </div>
                      </div>

                      {unitServiceFeeEnabled && (
                        <div style={{ marginTop: '12px' }}>
                          <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Service Fee Amount ($)
                          </label>
                          <input
                            type="number"
                            value={unitServiceFeeAmount}
                            onChange={(e) => setUnitServiceFeeAmount(e.target.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="input-field"
                            disabled={delegateReadOnly}
                          />
                          <p className="unit-settings-muted" style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                            This amount will be charged to the tenant for each maintenance request
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="unit-settings-callout--green" style={{ marginBottom: '24px', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: unitReadyForApplicants ? '12px' : 0, gap: '16px' }}>
                        <div>
                          <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                            🏷️ Vacant and ready for applicants
                          </label>
                          <p style={{ margin: 0, fontSize: '13px' }}>
                            Show this unit to connected realtors so they know you are actively looking for an applicant.
                          </p>
                        </div>
                        <div style={{
                          width: '50px',
                          height: '28px',
                          borderRadius: '14px',
                          background: unitReadyForApplicants ? '#16a34a' : '#d1d5db',
                          cursor: delegateReadOnly ? 'not-allowed' : (activeTenants.length > 0 ? 'not-allowed' : 'pointer'),
                          display: 'flex',
                          alignItems: 'center',
                          padding: '2px',
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          opacity: activeTenants.length > 0 || delegateReadOnly ? 0.6 : 1
                        }}
                        onClick={() => {
                          if (delegateReadOnly) return;
                          if (activeTenants.length > 0) return;
                          setUnitReadyForApplicants(!unitReadyForApplicants);
                        }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '12px',
                            background: '#fff',
                            transition: 'all 0.2s',
                            marginLeft: unitReadyForApplicants ? '22px' : '2px'
                          }} />
                        </div>
                      </div>

                      {activeTenants.length > 0 && (
                        <p className="unit-settings-warn" style={{ margin: '12px 0 0 0', fontSize: '12px', fontWeight: '600' }}>
                          Remove the current tenant first if you want to reopen this unit to applicants.
                        </p>
                      )}

                      {unitReadyForApplicants && (
                        <div style={{ marginTop: '12px' }}>
                          <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Notes for realtor
                          </label>
                          <textarea
                            value={unitVacancyNotes}
                            onChange={(e) => setUnitVacancyNotes(e.target.value)}
                            rows={3}
                            className="input-field"
                            placeholder="Example: Looking for a May 1 move-in, fresh paint complete, pets case-by-case."
                            style={{ resize: 'vertical' }}
                            disabled={delegateReadOnly}
                          />
                        </div>
                      )}
                    </div>

                    {!delegateReadOnly ? (
                      <button 
                        onClick={saveUnitSettings}
                        style={{
                          width: '100%',
                          background: '#3b82f6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 16px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}
                      >
                        ✓ Save Settings
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CREATE REQUEST MODAL */}
        {creatingOwnerRequest && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Create maintenance request"
            onClick={closeOwnerRequestModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: ownerRequestSuccessOverlay ? 'center' : 'flex-start',
              justifyContent: 'center',
              zIndex: 1000,
              overflowY: 'auto',
              padding: ownerRequestSuccessOverlay ? '20px' : '20px 0',
            }}
          >
            <div
              className="owner-create-request-modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'relative',
                borderRadius: '12px',
                padding: ownerRequestSuccessOverlay ? '40px 28px 32px' : '24px',
                width: '90%',
                maxWidth: ownerRequestSuccessOverlay ? '420px' : '700px',
                margin: '0 auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              }}
            >
              <button
                type="button"
                onClick={closeOwnerRequestModal}
                aria-label="Close"
                title="Close"
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: ownerThemeDark ? '1px solid #475569' : '1px solid #e5e7eb',
                  background: ownerThemeDark ? '#0f172a' : '#fff',
                  color: ownerThemeDark ? '#e2e8f0' : '#111827',
                  fontSize: 18,
                  fontWeight: 900,
                  lineHeight: '34px',
                  cursor: 'pointer',
                  zIndex: 20,
                }}
              >
                ×
              </button>
              {ownerRequestSuccessOverlay ? (
                <div style={{ textAlign: 'center', paddingTop: 8 }}>
                  <div
                    aria-hidden
                    style={{
                      width: '76px',
                      height: '76px',
                      margin: '0 auto 18px',
                      borderRadius: '50%',
                      background: 'linear-gradient(145deg, #d1fae5 0%, #6ee7b7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '38px',
                      boxShadow: '0 12px 32px rgba(16, 185, 129, 0.22)',
                    }}
                  >
                    ✓
                  </div>
                  <h3 style={{ margin: '0 0 8px', color: '#065f46', fontSize: '1.35rem', fontWeight: 800 }}>
                    Request submitted
                  </h3>
                  <p style={{ margin: '0 0 24px', color: '#047857', fontSize: '15px', lineHeight: 1.55 }}>
                    Your maintenance request is live. Professionals can see it and respond shortly.
                    {ownerRequestPreferredFirst && preferredProfessionalIds.length > 0 ? (
                      <span style={{ display: 'block', marginTop: 10, fontSize: 14, color: '#166534' }}>
                        Preferred-first window is on — only your preferred pros see it on the open board until the time
                        you chose.
                      </span>
                    ) : null}
                  </p>
                  <button
                    type="button"
                    onClick={closeOwnerRequestModal}
                    style={{
                      background: '#059669',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 36px',
                      fontSize: '15px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                    }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
              <h3 style={{marginTop:0,marginBottom:'8px',color: ownerThemeDark ? '#f1f5f9' : '#1e293b'}}>📝 Create Maintenance Request</h3>
              <p style={{margin:'0 0 20px 0',color: ownerThemeDark ? '#94a3b8' : '#666',fontSize:'14px'}}>Submit a maintenance request for one of your units</p>

              {/* Unit selector — shown when not opened from a specific unit */}
              {!activeUnit && (
                <div style={{marginBottom:'20px'}}>
                  <label style={{display:'block',fontWeight:'600',marginBottom:'8px',color: ownerThemeDark ? '#f1f5f9' : '#1e293b',fontSize:'14px'}}>Select Unit <span style={{color:'#dc3545'}}>*</span></label>
                  <select
                    className="input-field"
                    value={ownerRequestSelectedUnitId}
                    onChange={(e) => setOwnerRequestSelectedUnitId(e.target.value)}
                    style={{width:'100%',padding:'10px',border: ownerThemeDark ? '1px solid #475569' : '1px solid #d1d5db',borderRadius:'6px',fontSize:'14px',boxSizing:'border-box'}}
                  >
                    <option value=''>— Choose a unit —</option>
                    {ownerUnits.map(u => (
                      <option key={u.id} value={u.id}>Unit {u.unitNumber}{u.address ? ` — ${u.address}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              {ownerTriageSelectedUnit && (
                <MaintenanceTriageChat
                  key={ownerTriageSelectedUnit.id}
                  actor="owner"
                  user={user}
                  userData={userData}
                  selectedUnit={{
                    id: ownerTriageSelectedUnit.id,
                    unitNumber: ownerTriageSelectedUnit.unitNumber,
                  }}
                  compact
                  onDraftMerge={mergeOwnerTriageDraft}
                  onSessionUpdate={handleOwnerTriageSessionUpdate}
                  onSkipAssistantToForm={skipOwnerAssistantToForm}
                  requireChatBeforeSkip
                  onGateMilestone={handleOwnerTriageGateMilestone}
                />
              )}

              {!ownerCreateFormUnlocked && !ownerCreateChatUserMessaged && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '12px 14px',
                    borderRadius: 10,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    fontSize: 13,
                    color: '#475569',
                    lineHeight: 1.5,
                  }}
                >
                  <strong style={{ color: '#1e293b' }}>Step 1:</strong> Send a message in the chat above to describe
                  the issue.
                </div>
              )}

              {!ownerCreateFormUnlocked && ownerCreateChatAssistantReplied && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    fontSize: 13,
                    color: '#78350f',
                    lineHeight: 1.5,
                  }}
                >
                  <p style={{ margin: '0 0 10px 0' }}>
                    Want to proceed with a maintenance request? You can continue to the form now, or keep chatting for
                    more troubleshooting.
                  </p>
                  <button
                    type="button"
                    onClick={unlockOwnerCreateFormAnyway}
                    style={{
                      padding: '8px 14px',
                      background: '#fff',
                      color: '#b45309',
                      border: '1px solid #fbbf24',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Continue to request form
                  </button>
                </div>
              )}

              {ownerCreateFormUnlocked && (
                <div
                  style={{
                    marginBottom: 14,
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#065f46',
                  }}
                >
                  Form unlocked — confirm details below, then submit.
                </div>
              )}

              {ownerCreateFormUnlocked ? (
                <>
                  {/* Category Selection */}
                  <div style={{marginBottom:'24px'}}>
                    <label style={{display:'block',fontWeight:'600',marginBottom:'12px',color: ownerThemeDark ? '#f1f5f9' : '#1e293b',fontSize:'14px'}}>Issue Category <span style={{color:'#dc3545'}}>*</span></label>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:'10px'}}>
                      {[
                        { value: 'plumbing', label: 'Plumbing', icon: '🚿' },
                        { value: 'electrical', label: 'Electrical', icon: '⚡' },
                        { value: 'hvac', label: 'HVAC', icon: '❄️' },
                        { value: 'appliance', label: 'Appliance', icon: '🔌' },
                        { value: 'structural', label: 'Structural', icon: '🏠' },
                        { value: 'pest', label: 'Pest Control', icon: '🐛' },
                        { value: 'lock_key', label: 'Locks & Keys', icon: '🔑' },
                        { value: 'other', label: 'Other', icon: '📝' }
                      ].map(cat => (
                        <div
                          key={cat.value}
                          onClick={() => setOwnerRequestCategory(cat.value)}
                          style={{
                            padding:'12px',
                            textAlign:'center',
                            border: ownerRequestCategory === cat.value
                              ? '2px solid #6366f1'
                              : ownerThemeDark ? '2px solid #475569' : '2px solid #e5e7eb',
                            borderRadius:'8px',
                            cursor:'pointer',
                            background: ownerRequestCategory === cat.value
                              ? (ownerThemeDark ? '#312e81' : '#eff6ff')
                              : (ownerThemeDark ? '#0f172a' : '#fff'),
                            transition:'all 0.2s'
                          }}
                        >
                          <div style={{fontSize:'24px',marginBottom:'4px'}}>{cat.icon}</div>
                          <div style={{
                            fontSize:'12px',
                            fontWeight:'600',
                            color: ownerRequestCategory === cat.value
                              ? (ownerThemeDark ? '#e0e7ff' : '#0284c7')
                              : (ownerThemeDark ? '#cbd5e1' : '#1e293b'),
                          }}>{cat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

              {ownerCreateFormUnlocked && preferredProfessionalIds.length > 0 ? (
                <div
                  style={{
                    marginBottom: 20,
                    padding: 14,
                    borderRadius: 10,
                    border: ownerThemeDark ? '1px solid #6366f1' : '1px solid #e0e7ff',
                    background: ownerThemeDark ? 'rgba(49, 46, 129, 0.35)' : '#eef2ff',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      cursor: 'pointer',
                      fontSize: 14,
                      color: ownerThemeDark ? '#e2e8f0' : '#1e293b',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={ownerRequestPreferredFirst}
                      onChange={(e) => setOwnerRequestPreferredFirst(e.target.checked)}
                      style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
                    />
                    <span>
                      <strong>Offer first to my preferred pros</strong>
                      <span
                        style={{ display: 'block', marginTop: 6, fontSize: 13, color: ownerThemeDark ? '#94a3b8' : '#475569', fontWeight: 400, lineHeight: 1.45 }}
                      >
                        Only they see this job on the open board until the window ends; then it opens to everyone who
                        matches the category.
                      </span>
                    </span>
                  </label>
                  {ownerRequestPreferredFirst ? (
                    <div style={{ marginTop: 12, marginLeft: 28 }}>
                      <label style={{ fontSize: 13, color: ownerThemeDark ? '#94a3b8' : '#475569', display: 'block', marginBottom: 6 }}>Window</label>
                      <select
                        className="input-field"
                        value={ownerRequestPreferredFirstHours}
                        onChange={(e) => setOwnerRequestPreferredFirstHours(Number(e.target.value))}
                        style={{ padding: 8, borderRadius: 6, border: ownerThemeDark ? '1px solid #475569' : '1px solid #cbd5e1', fontSize: 14, maxWidth: 220 }}
                      >
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={72}>72 hours</option>
                      </select>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Priority Selection */}
              <div style={{marginBottom:'24px'}}>
                <label style={{display:'block',fontWeight:'600',marginBottom:'12px',color: ownerThemeDark ? '#f1f5f9' : '#1e293b',fontSize:'14px'}}>Priority Level</label>
                <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                  {ownerCreateRequestPriorityOptions.map((pri) => {
                    const sel = ownerRequestPriority === pri.value;
                    return (
                    <label 
                      key={pri.value}
                      style={{
                        display:'flex',
                        alignItems:'center',
                        padding:'12px',
                        border: `2px solid ${sel ? pri.selBorder : pri.idleBorder}`,
                        borderRadius:'8px',
                        background: sel ? pri.selBg : pri.idleBg,
                        cursor:'pointer',
                        transition:'all 0.2s'
                      }}
                    >
                      <input 
                        type="radio" 
                        name="priority" 
                        value={pri.value} 
                        checked={sel}
                        onChange={(e) => setOwnerRequestPriority(e.target.value)}
                        style={{marginRight:'12px',cursor:'pointer'}}
                      />
                      <div>
                        <div style={{fontWeight:'600',color: sel ? pri.selTitle : pri.idleTitle}}>{pri.name}</div>
                        <div style={{fontSize:'12px',color: sel ? pri.selDesc : pri.idleDesc}}>{pri.desc}</div>
                      </div>
                    </label>
                  );
                })}
                </div>
              </div>

              {/* Location */}
              <div style={{marginBottom:'24px'}}>
                <label style={{display:'block',fontWeight:'600',marginBottom:'8px',color: ownerThemeDark ? '#f1f5f9' : '#1e293b',fontSize:'14px'}}>Location in Unit</label>
                <select 
                  className="input-field"
                  value={ownerRequestLocation} 
                  onChange={(e) => setOwnerRequestLocation(e.target.value)}
                  style={{width:'100%',padding:'10px',border: ownerThemeDark ? '1px solid #475569' : '1px solid #d1d5db',borderRadius:'6px',fontSize:'14px'}}
                >
                  <option value="">Select location...</option>
                  <option value="kitchen">Kitchen</option>
                  <option value="bathroom">Bathroom</option>
                  <option value="bedroom">Bedroom</option>
                  <option value="living_room">Living Room</option>
                  <option value="laundry">Laundry Area</option>
                  <option value="garage">Garage</option>
                  <option value="exterior">Exterior</option>
                  <option value="multiple">Multiple Areas</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Description */}
              <div style={{marginBottom:'24px'}}>
                <label style={{display:'block',fontWeight:'600',marginBottom:'8px',color: ownerThemeDark ? '#f1f5f9' : '#1e293b',fontSize:'14px'}}>Describe the Issue <span style={{color:'#dc3545'}}>*</span></label>
                <textarea 
                  className="input-field"
                  value={ownerRequestDesc} 
                  onChange={(e) => setOwnerRequestDesc(e.target.value)}
                  placeholder="Please describe the issue in detail..."
                  style={{
                    width:'100%',
                    padding:'10px',
                    border: ownerThemeDark ? '1px solid #475569' : '1px solid #d1d5db',
                    borderRadius:'6px',
                    fontFamily: 'var(--font-sans), system-ui, sans-serif',
                    minHeight:'100px',
                    fontSize:'14px',
                    color: ownerThemeDark ? '#e2e8f0' : undefined,
                    background: ownerThemeDark ? '#0f172a' : undefined,
                  }}
                />
              </div>

              {/* Photo Upload */}
              <div style={{marginBottom:'24px'}}>
                <label style={{display:'block',fontWeight:'600',marginBottom:'8px',color: ownerThemeDark ? '#f1f5f9' : '#1e293b',fontSize:'14px'}}>Attach Photos</label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => { if (e.target.files.length > 0) { handleOwnerRequestImageUpload(e.target.files); } e.target.value = ''; }}
                  id="owner-camera-upload"
                  style={{display: 'none'}}
                />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => { if (e.target.files.length > 0) { handleOwnerRequestImageUpload(e.target.files); } e.target.value = ''; }}
                  id="owner-photo-upload"
                  style={{display: 'none'}}
                />
                <div style={{display: 'flex', gap: '10px', marginBottom: '12px'}}>
                  <label htmlFor="owner-camera-upload" style={{padding:'10px 16px',background:'#1976d2',color:'#fff',borderRadius:'8px',cursor:'pointer',fontSize:'14px',display:'flex',alignItems:'center',gap:'6px',fontWeight:'600',flex:1,textAlign:'center',justifyContent:'center',opacity:ownerRequestUploading?0.6:1,pointerEvents:ownerRequestUploading?'none':'auto'}}>
                    📸 Camera
                  </label>
                  <label htmlFor="owner-photo-upload" style={{padding:'10px 16px',background:'#28a745',color:'#fff',borderRadius:'8px',cursor:'pointer',fontSize:'14px',display:'flex',alignItems:'center',gap:'6px',fontWeight:'600',flex:1,textAlign:'center',justifyContent:'center',opacity:ownerRequestUploading?0.6:1,pointerEvents:ownerRequestUploading?'none':'auto'}}>
                    🖼️ Gallery
                  </label>
                </div>
                {ownerRequestImages.length > 0 && (
                  <div style={{marginTop:'12px',display:'flex',flexWrap:'wrap',gap:'8px'}}>
                    {ownerRequestImages.map((img, idx) => (
                      <div key={idx} style={{position:'relative',width:'80px',height:'80px'}}>
                        <img src={img} alt={`Upload ${idx}`} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'6px'}} />
                        <button 
                          type="button"
                          onClick={() => setOwnerRequestImages(prev => prev.filter((_, i) => i !== idx))}
                          style={{position:'absolute',top:'4px',right:'4px',background:'rgba(0,0,0,0.7)',color:'#fff',border:'none',borderRadius:'50%',width:'24px',height:'24px',cursor:'pointer',fontSize:'14px',fontWeight:'bold'}}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {ownerRequestSubmitError && (
                <div
                  role="alert"
                  style={{
                    marginBottom: '16px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#991b1b',
                    fontSize: '14px',
                    lineHeight: 1.45,
                  }}
                >
                  {ownerRequestSubmitError}
                </div>
              )}

              {/* Buttons */}
              <div style={{display:'flex',gap:'12px'}}>
                <button 
                  type="button"
                  onClick={createOwnerRequest}
                  disabled={
                    ownerRequestSubmitting ||
                    ownerRequestUploading ||
                    !ownerCreateFormUnlocked ||
                    !ownerRequestDesc.trim() ||
                    !ownerRequestCategory ||
                    !(ownerRequestSelectedUnitId || activeUnit)
                  }
                  style={{
                    flex:1,
                    background:'#16a34a',
                    color:'#fff',
                    border:'none',
                    borderRadius:'6px',
                    padding:'10px',
                    cursor:'pointer',
                    fontWeight:'600',
                    boxShadow: ownerThemeDark ? '0 2px 12px rgba(22, 163, 74, 0.35)' : undefined,
                    opacity:(ownerRequestSubmitting||ownerRequestUploading||!ownerCreateFormUnlocked||!ownerRequestDesc.trim()||!ownerRequestCategory||!(ownerRequestSelectedUnitId||activeUnit))?0.6:1,
                  }}
                >
                  {ownerRequestSubmitting ? '⏳ Submitting...' : ownerRequestUploading ? '⏳ Uploading photos...' : '✓ Submit Request'}
                </button>
                <button 
                  type="button"
                  onClick={closeOwnerRequestModal}
                  style={{
                    flex:1,
                    background: ownerThemeDark ? '#334155' : '#e2e8f0',
                    color: ownerThemeDark ? '#f1f5f9' : '#374151',
                    border: ownerThemeDark ? '1px solid #475569' : 'none',
                    borderRadius:'6px',
                    padding:'10px',
                    cursor:'pointer',
                    fontWeight:'600',
                  }}
                >
                  ✕ Cancel
                </button>
              </div>
                </>
              ) : null}
                </>
              )}
            </div>
          </div>
        )}

        {/* INVITE LINK MODAL */}
        {showInviteModal && inviteModalUnitId && (
          <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
            <div style={{background:'#fff',borderRadius:'12px',padding:'30px',width:'90%',maxWidth:'600px',boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}}>
              <div style={{textAlign: 'center', marginBottom: '24px'}}>
                <span style={{fontSize: '40px', display: 'block', marginBottom: '12px'}}>🔗</span>
                <h3 style={{margin: '0 0 8px 0', color: '#1e293b'}}>Invite Tenant</h3>
                <p style={{margin: 0, color: '#666', fontSize: '14px'}}>Share this link with your tenant so they can create their account and submit maintenance requests.</p>
              </div>

              <div style={{marginBottom: '20px'}}>
                <label style={{display:'block',fontWeight:'600',marginBottom:'8px',color:'#1e293b',fontSize:'14px'}}>Tenant Signup Link</label>
                <div style={{display:'flex', gap: '8px'}}>
                  <input 
                    type="text" 
                    value={generateShareLink(inviteModalUnitId)} 
                    readOnly 
                    style={{flex:1,padding:'10px',border:'1px solid #d1d5db',borderRadius:'6px',fontFamily:'monospace',fontSize:'12px'}}
                  />
                  <button 
                    onClick={() => copyToClipboard(generateShareLink(inviteModalUnitId))} 
                    style={{background:'#3b82f6',color:'#fff',border:'none',borderRadius:'6px',padding:'10px 16px',cursor:'pointer',fontWeight:'600',whiteSpace:'nowrap'}}
                  >
                    📋 Copy
                  </button>
                </div>
              </div>

              <div style={{display:'flex',gap:'12px',marginBottom:'20px',flexWrap:'wrap'}}>
                <button 
                  type="button"
                  disabled={inviteModalEmailSending}
                  onClick={async () => {
                    if (!inviteModalUnitId) return;
                    setInviteModalEmailSending(true);
                    try {
                      const fn = httpsCallable(firebaseFunctions, 'sendTenantUnitInvite');
                      await fn({ unitId: inviteModalUnitId });
                      alert('Invitation email sent to the tenant address on file for this unit.');
                    } catch (e) {
                      alert(e?.message || 'Could not send email. Add the tenant email on the unit, or copy the link below.');
                    } finally {
                      setInviteModalEmailSending(false);
                    }
                  }}
                  style={{flex:1,minWidth:'140px',background: inviteModalEmailSending ? '#94a3b8' : '#06b6d4',color:'#fff',border:'none',borderRadius:'6px',padding:'10px 16px',cursor: inviteModalEmailSending ? 'not-allowed' : 'pointer',fontWeight:'600'}}
                >
                  {inviteModalEmailSending ? 'Sending…' : '✉️ Send invitation email'}
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    const unit = ownerUnits.find(u => u.id === inviteModalUnitId);
                    if (unit) sendShareLinkViaEmail(unit.unitNumber, generateShareLink(inviteModalUnitId));
                  }}
                  style={{flex:1,minWidth:'140px',background:'#f3f4f6',color:'#475569',border:'1px solid #cbd5e1',borderRadius:'6px',padding:'10px 16px',cursor:'pointer',fontWeight:'600'}}
                >
                  Open mail app instead
                </button>
              </div>

              <div style={{background:'#f0f9ff',border:'1px solid #bfdbfe',borderRadius:'8px',padding:'16px',marginBottom:'20px'}}>
                <h4 style={{margin:'0 0 12px 0',color:'#0284c7',fontSize:'14px'}}>Instructions for Tenants</h4>
                <ol style={{margin:0,paddingLeft:'20px',color:'#1e40af',fontSize:'13px'}}>
                  <li>Click the signup link</li>
                  <li>Create an account with their email</li>
                  <li>They'll be automatically assigned to this unit</li>
                  <li>Once registered, they can submit maintenance requests</li>
                </ol>
              </div>

              <button 
                onClick={() => { setShowInviteModal(false); setInviteModalUnitId(null); }}
                style={{width:'100%',background:'#e5e7eb',color:'#374151',border:'none',borderRadius:'6px',padding:'10px',cursor:'pointer',fontWeight:'600'}}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* REQUEST DETAIL MODAL - Show Meeting Options for Acceptance */}
        {selectedRequest && (
          <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1001,overflowY:'auto',padding:'20px'}}>
            <div
              className="owner-request-detail-modal"
              style={{borderRadius:'12px',padding:'30px',width:'100%',maxWidth:'700px',boxShadow:'0 10px 40px rgba(0,0,0,0.2)',marginTop:'20px',marginBottom:'20px',maxHeight:'90vh',overflowY:'auto'}}
            >
              
              {/* Header */}
              <div className="owner-request-detail-modal__header">
                <div>
                  <h2 style={{margin:'0 0 8px 0',color:'#1e293b',fontSize:'24px'}}>Request Details</h2>
                  <p style={{margin:0,color:'#666',fontSize:'12px'}}>Request #{selectedRequest.id.slice(0,8).toUpperCase()}</p>
                </div>
                <button 
                  onClick={() => dismissSelectedRequest()}
                  style={{background:'none',border:'none',fontSize:'28px',cursor:'pointer',padding:'0',width:'32px',height:'32px',display:'flex',alignItems:'center',justifyContent:'center'}}
                >
                  ✕
                </button>
              </div>

              {/* Request Info Grid */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'24px'}}>
                <div>
                  <p style={{margin:'0 0 8px 0',color:'#666',fontSize:'12px',fontWeight:'600'}}>CATEGORY</p>
                  <p style={{margin:0,color:'#1e293b',fontSize:'15px',fontWeight:'500'}}>{selectedRequest.category || 'N/A'}</p>
                </div>
                <div>
                  <p style={{margin:'0 0 8px 0',color:'#666',fontSize:'12px',fontWeight:'600'}}>PRIORITY</p>
                  <p style={{margin:0,color:'#1e293b',fontSize:'15px',fontWeight:'500'}}>{(selectedRequest.priority || 'normal').toUpperCase()}</p>
                </div>
                <div>
                  <p style={{margin:'0 0 8px 0',color:'#666',fontSize:'12px',fontWeight:'600'}}>LOCATION</p>
                  <p style={{margin:0,color:'#1e293b',fontSize:'15px',fontWeight:'500'}}>{selectedRequest.location || 'N/A'}</p>
                </div>
                <div>
                  <p style={{margin:'0 0 8px 0',color:'#666',fontSize:'12px',fontWeight:'600'}}>STATUS</p>
                  <p style={{margin:0,color:'#1e293b',fontSize:'15px',fontWeight:'500'}}>{getOwnerMaintenanceDisplayLabel(selectedRequest).label}</p>
                  <p style={{margin:'6px 0 0 0',color:'#94a3b8',fontSize:'11px',lineHeight:1.4}}>Set by workflow (tenant/pro actions), not editable here.</p>
                </div>
              </div>

              {delegateReadOnly ? (
                <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#0f172a', fontSize: 13, lineHeight: 1.5 }}>
                  <strong>Read-only access:</strong> actions like emailing pros, accepting visit times, and payments are disabled.
                </div>
              ) : null}

              {preferredNudgeMessage ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    lineHeight: 1.45,
                    background: preferredNudgeMessage.startsWith('Email sent') ? '#ecfdf5' : '#fef2f2',
                    color: preferredNudgeMessage.startsWith('Email sent') ? '#065f46' : '#991b1b',
                    border: `1px solid ${preferredNudgeMessage.startsWith('Email sent') ? '#6ee7b7' : '#fecaca'}`,
                  }}
                >
                  {preferredNudgeMessage}
                </div>
              ) : null}

              {(() => {
                const ids = Array.isArray(selectedRequest.preferredOfferProIds)
                  ? selectedRequest.preferredOfferProIds
                  : [];
                const until = selectedRequest.preferredOfferUntil?.toDate?.();
                if (!ids.length || !until) return null;
                const active = until.getTime() > Date.now();
                return (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 12,
                      borderRadius: 8,
                      border: '1px solid #ddd6fe',
                      background: '#f5f3ff',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#5b21b6' }}>Preferred-first</p>
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#5b21b6', lineHeight: 1.45 }}>
                      {active
                        ? `Early access until ${until.toLocaleString()} (${ids.length} pro(s)).`
                        : `Early-access window ended ${until.toLocaleString()}.`}
                    </p>
                  </div>
                );
              })()}

              {preferredProfessionalIds.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#64748b' }}>YOUR PREFERRED PROS</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {preferredProfessionalIds.map((id) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() =>
                          setProProfilePeek({
                            professionalId: id,
                            fallbackName: preferredProDetails[id]?.businessName || 'Professional',
                          })
                        }
                        style={{
                          padding: '6px 12px',
                          borderRadius: 999,
                          border: '1px solid #cbd5e1',
                          background: '#fff',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {preferredProDetails[id]?.businessName || `${id.slice(0, 6)}…`}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {!delegateReadOnly && (selectedRequest.status === 'pending' || selectedRequest.status === 'open') &&
              !(selectedRequest.assignedProfessional && String(selectedRequest.assignedProfessional).trim()) &&
              preferredProfessionalIds.length > 0 ? (
                <div
                  style={{
                    marginBottom: 20,
                    padding: 14,
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                  }}
                >
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#334155', fontWeight: 600 }}>
                    Notify preferred pros
                  </p>
                  <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                    Short email with a link to open jobs. At most once every 24 hours for this request.
                  </p>
                  {selectedRequest.lastPreferredProNudgeAt?.toDate ? (
                    <p style={{ margin: '0 0 10px', fontSize: 12, color: '#94a3b8' }}>
                      Last sent: {selectedRequest.lastPreferredProNudgeAt.toDate().toLocaleString()}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={preferredNudgeBusy}
                    onClick={() => handlePreferredProsNudgeForRequest()}
                    style={{ padding: '8px 16px', fontSize: 14 }}
                  >
                    {preferredNudgeBusy ? 'Sending…' : 'Email my preferred pros'}
                  </button>
                </div>
              ) : null}

              {/* Description */}
              {selectedRequest.description && (
                <div style={{marginBottom:'24px',padding:'16px',background:'#f8fafc',borderRadius:'8px',borderLeft:'4px solid #3b82f6'}}>
                  <p style={{margin:'0 0 8px 0',color:'#666',fontSize:'12px',fontWeight:'600'}}>DESCRIPTION</p>
                  <p style={{margin:0,color:'#1e293b',fontSize:'14px',lineHeight:'1.5'}}>{selectedRequest.description}</p>
                </div>
              )}

              {/* Images */}
              {selectedRequest.images && selectedRequest.images.length > 0 && (
                <div style={{marginBottom:'24px'}}>
                  <p style={{margin:'0 0 12px 0',color:'#666',fontSize:'12px',fontWeight:'600'}}>IMAGES</p>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:'12px'}}>
                    {selectedRequest.images.map((img, idx) => (
                      <img key={idx} src={img} alt="request" style={{width:'100%',height:'120px',objectFit:'cover',borderRadius:'8px',border:'1px solid #e5e7eb'}} />
                    ))}
                  </div>
                </div>
              )}

              {/* Meeting proposals (up to {MAX} professionals) */}
              {hasPendingMeetingProposals(selectedRequest) && (
                <div style={{marginBottom:'24px',padding:'20px',background:'#f0fdf4',borderRadius:'8px',border:'1px solid #bbf7d0'}}>
                  <h3 style={{margin:'0 0 8px 0',color:'#16a34a',fontSize:'16px',fontWeight:'600'}}>📅 Choose a visit time</h3>
                  <p style={{margin:'0 0 16px 0',color:'#15803d',fontSize:'13px',lineHeight:1.45}}>
                    {normalizeMeetingProposals(selectedRequest).length} professional{normalizeMeetingProposals(selectedRequest).length === 1 ? '' : 's'} suggested times (max {MAX_PROFESSIONAL_MEETING_PROPOSALS} on the open market). Your tenant sees the same list — whoever accepts first schedules the visit.
                  </p>
                  <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
                    {normalizeMeetingProposals(selectedRequest).map((prop) => (
                      <div
                        key={prop.professionalId}
                        style={{padding:'14px',background:'#fff',borderRadius:'8px',border:'1px solid #86efac'}}
                      >
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0 0 8px 0' }}>
                          <button
                            type="button"
                            onClick={() =>
                              setProProfilePeek({
                                professionalId: prop.professionalId,
                                fallbackName: prop.businessName || 'Professional',
                              })
                            }
                            title="View business profile and contact details"
                            style={{
                              margin: 0,
                              padding: 0,
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '700',
                              color: '#14532d',
                              textAlign: 'left',
                              textDecoration: 'underline',
                              textUnderlineOffset: '3px',
                            }}
                          >
                            {prop.businessName || 'Professional'}
                          </button>
                          {isProfessionalOnPreferredOfferList(selectedRequest, prop.professionalId) ? (
                            <span
                              title="On your preferred-pro list for this job."
                              style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '3px 8px',
                                borderRadius: '999px',
                                background: '#ede9fe',
                                color: '#5b21b6',
                                border: '1px solid #c4b5fd',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Preferred
                            </span>
                          ) : null}
                        </div>
                        <ProfessionalRatingsHint
                          professionalId={prop.professionalId}
                          businessName={prop.businessName}
                        />
                        <div style={{display:'flex',flexDirection:'column',gap:'10px',marginTop:'12px'}}>
                          {(prop.options || []).map((option, idx) => (
                            <div
                              key={idx}
                              style={{
                                display:'flex',
                                justifyContent:'space-between',
                                alignItems:'center',
                                gap:'12px',
                                flexWrap:'wrap',
                                padding:'10px 12px',
                                background:'#f7fee7',
                                borderRadius:'8px',
                                border:'1px solid #d9f99d',
                              }}
                            >
                              <span style={{color:'#1e293b',fontSize:'14px',fontWeight:'600'}}>
                                {formatMeetingOptionForViewer(option.date, option.time)}
                              </span>
                              {!delegateReadOnly && selectedRequest.status !== 'completed' && !selectedRequest.acceptedMeetingTime ? (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await updateDoc(doc(db, 'maintenance_requests', selectedRequest.id), {
                                        acceptedMeetingTime: {
                                          date: option.date,
                                          time: option.time,
                                          acceptedAt: serverTimestamp(),
                                          acceptedBy: 'owner',
                                          professionalId: prop.professionalId,
                                        },
                                        status: 'scheduled',
                                        assignedProfessional: prop.professionalId,
                                        professionalMeetingProposals: deleteField(),
                                        proposalProfessionalIds: deleteField(),
                                        meetingOptions: deleteField(),
                                        proposedByProfessional: deleteField(),
                                      });
                                      alert('✅ Meeting time accepted. This request is now assigned and scheduled.');
                                      dismissSelectedRequest();
                                    } catch (err) {
                                      console.error('❌ Error updating request:', err);
                                      alert('❌ Error accepting meeting time: ' + err.message);
                                    }
                                  }}
                                  style={{background:'#16a34a',color:'#fff',border:'none',borderRadius:'6px',padding:'8px 16px',cursor:'pointer',fontWeight:'600',fontSize:'14px',whiteSpace:'nowrap'}}
                                >
                                  Accept this time
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!hasPendingMeetingProposals(selectedRequest) && !selectedRequest.acceptedMeetingTime && selectedRequest.status !== 'scheduled' && (
                <div style={{marginBottom:'24px',padding:'16px',background:'#fef3c7',borderRadius:'8px',border:'1px solid #fcd34d'}}>
                  <p style={{margin:0,color:'#92400e',fontSize:'14px'}}>⏳ Waiting for professionals to propose visit times...</p>
                </div>
              )}

              {/* Professional's Estimate */}
              {selectedRequest.profesionalEstimate && (
                <div style={{marginBottom:'24px',padding:'20px',background: selectedRequest.profesionalEstimate.status === 'accepted' ? '#f0fdf4' : selectedRequest.profesionalEstimate.status === 'rejected' ? '#fef2f2' : '#eff6ff',borderRadius:'8px',border: selectedRequest.profesionalEstimate.status === 'accepted' ? '1px solid #bbf7d0' : selectedRequest.profesionalEstimate.status === 'rejected' ? '1px solid #fecaca' : '1px solid #bfdbfe'}}>
                  <h3 style={{margin:'0 0 16px 0',color: selectedRequest.profesionalEstimate.status === 'accepted' ? '#16a34a' : selectedRequest.profesionalEstimate.status === 'rejected' ? '#dc2626' : '#2563eb',fontSize:'16px',fontWeight:'600'}}>
                    💰 Professional's Estimate
                    {selectedRequest.profesionalEstimate.status === 'accepted' && <span style={{marginLeft:'8px',fontSize:'12px',background:'#16a34a',color:'#fff',padding:'2px 8px',borderRadius:'4px'}}>Accepted</span>}
                    {selectedRequest.profesionalEstimate.status === 'rejected' && <span style={{marginLeft:'8px',fontSize:'12px',background:'#dc2626',color:'#fff',padding:'2px 8px',borderRadius:'4px'}}>Rejected</span>}
                    {selectedRequest.profesionalEstimate.status === 'pending' && <span style={{marginLeft:'8px',fontSize:'12px',background:'#f59e0b',color:'#fff',padding:'2px 8px',borderRadius:'4px'}}>Pending Review</span>}
                  </h3>
                  
                  <div style={{display:'flex',alignItems:'baseline',gap:'8px',marginBottom:'12px'}}>
                    <span style={{fontSize:'32px',fontWeight:'700',color:'#1e293b'}}>${parseFloat(selectedRequest.profesionalEstimate.amount).toFixed(2)}</span>
                    <span style={{fontSize:'14px',color:'#64748b'}}>estimated cost</span>
                  </div>
                  
                  {selectedRequest.profesionalEstimate.description && (
                    <div style={{marginBottom:'12px',padding:'12px',background:'#fff',borderRadius:'6px',border:'1px solid #e2e8f0'}}>
                      <p style={{margin:'0 0 4px 0',fontSize:'12px',color:'#64748b',fontWeight:'600'}}>DESCRIPTION</p>
                      <p style={{margin:0,fontSize:'14px',color:'#1e293b',lineHeight:'1.5'}}>{selectedRequest.profesionalEstimate.description}</p>
                    </div>
                  )}
                  
                  {selectedRequest.profesionalEstimate.submittedBy?.businessName && (
                    <p style={{margin:'0 0 12px 0',fontSize:'13px',color:'#64748b'}}>
                      Submitted by:{' '}
                      {selectedRequest.profesionalEstimate.submittedBy?.id ? (
                        <button
                          type="button"
                          title="View business profile and contact details"
                          onClick={() =>
                            setProProfilePeek({
                              professionalId: selectedRequest.profesionalEstimate.submittedBy.id,
                              fallbackName: selectedRequest.profesionalEstimate.submittedBy.businessName,
                            })
                          }
                          style={{
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontWeight: 700,
                            color: '#1d4ed8',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                            fontSize: '13px',
                          }}
                        >
                          {selectedRequest.profesionalEstimate.submittedBy.businessName}
                        </button>
                      ) : (
                        <strong>{selectedRequest.profesionalEstimate.submittedBy.businessName}</strong>
                      )}
                    </p>
                  )}
                  
                  {selectedRequest.profesionalEstimate.images && selectedRequest.profesionalEstimate.images.length > 0 && (
                    <div style={{marginBottom:'12px'}}>
                      <p style={{margin:'0 0 8px 0',fontSize:'12px',color:'#64748b',fontWeight:'600'}}>ESTIMATE PHOTOS</p>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(100px,1fr))',gap:'8px'}}>
                        {selectedRequest.profesionalEstimate.images.map((img, idx) => (
                          <img key={idx} src={img} alt="estimate" onClick={() => window.open(img, '_blank')} style={{width:'100%',height:'100px',objectFit:'cover',borderRadius:'6px',border:'1px solid #e5e7eb',cursor:'pointer'}} />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Accept / Reject buttons - only show for pending estimates */}
                  {!delegateReadOnly && selectedRequest.profesionalEstimate.status === 'pending' && (
                    <div style={{display:'flex',gap:'12px',marginTop:'16px'}}>
                      <button
                        onClick={() => {
                          setShowPaymentModal(selectedRequest);
                        }}
                        style={{flex:1,background:'#16a34a',color:'#fff',border:'none',borderRadius:'6px',padding:'12px',cursor:'pointer',fontWeight:'600',fontSize:'14px'}}
                      >
                        ✅ Accept & Pay ${parseFloat(selectedRequest.profesionalEstimate.amount).toFixed(2)}
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const rejectConfirmed = window.confirm(
                              'Reject this estimate?\n\nYou can keep this request with the current professional, or send it back to the professional pool so someone else can submit a proposal.'
                            );
                            if (!rejectConfirmed) return;

                            const sendBackToPool = window.confirm(
                              'Would you like to send this request back to the professional pool now?\n\nChoose OK to reopen it for other professionals, or Cancel to keep it with the current professional.'
                            );

                            if (sendBackToPool) {
                              await updateDoc(doc(db, 'maintenance_requests', selectedRequest.id), {
                                'profesionalEstimate.status': 'rejected',
                                profesionalEstimate: null,
                                meetingOptions: deleteField(),
                                acceptedMeetingTime: null,
                                proposedByProfessional: deleteField(),
                                professionalMeetingProposals: deleteField(),
                                proposalProfessionalIds: deleteField(),
                                assignedProfessional: null,
                                status: 'pending',
                              });
                              alert('Estimate rejected. The request is now reopened for professionals to submit new proposals.');
                            } else {
                              await updateDoc(doc(db, 'maintenance_requests', selectedRequest.id), {
                                'profesionalEstimate.status': 'rejected'
                              });
                              alert('Estimate rejected. The current professional can submit a revised estimate.');
                            }
                            dismissSelectedRequest();
                          } catch (err) {
                            alert('Error rejecting estimate: ' + err.message);
                          }
                        }}
                        style={{flex:1,background:'#dc2626',color:'#fff',border:'none',borderRadius:'6px',padding:'12px',cursor:'pointer',fontWeight:'600',fontSize:'14px'}}
                      >
                        ❌ Reject
                      </button>
                    </div>
                  )}
                  
                  {/* Payment completed info */}
                  {selectedRequest.paymentStatus === 'captured' && (
                    <div style={{marginTop:'12px',padding:'12px',background:'#f0fdf4',borderRadius:'6px',border:'1px solid #bbf7d0'}}>
                      <p style={{margin:0,color:'#16a34a',fontSize:'14px',fontWeight:'600'}}>💳 Payment completed</p>
                    </div>
                  )}
                </div>
              )}

              {selectedRequest.professionalCompletion?.summary ? (
                <div
                  style={{
                    marginBottom: '24px',
                    padding: '20px',
                    background: '#f0fdf4',
                    borderRadius: '8px',
                    border: '1px solid #86efac',
                  }}
                >
                  <h3 style={{ margin: '0 0 12px 0', color: '#166534', fontSize: '16px', fontWeight: '600' }}>
                    Work completed (professional)
                  </h3>
                  <p
                    style={{
                      margin: '0 0 12px',
                      fontSize: '14px',
                      color: '#14532d',
                      lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedRequest.professionalCompletion.summary}
                  </p>
                  {Array.isArray(selectedRequest.professionalCompletion.imageUrls) &&
                  selectedRequest.professionalCompletion.imageUrls.length > 0 ? (
                    <div>
                      <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#166534', fontWeight: '600' }}>
                        Completion photos
                      </p>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                          gap: '8px',
                        }}
                      >
                        {selectedRequest.professionalCompletion.imageUrls.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt=""
                            onClick={() => window.open(img, '_blank', 'noopener,noreferrer')}
                            style={{
                              width: '100%',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb',
                              cursor: 'pointer',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              
              {/* No Estimate Yet */}
              {!selectedRequest.profesionalEstimate && (selectedRequest.assignedProfessional || countProposalProfessionals(selectedRequest) > 0) && (
                <div style={{marginBottom:'24px',padding:'16px',background:'#f5f3ff',borderRadius:'8px',border:'1px solid #ddd6fe'}}>
                  <p style={{margin:0,color:'#7c3aed',fontSize:'14px'}}>📝 Waiting for professional to submit an estimate...</p>
                </div>
              )}

              {(() => {
                const proId =
                  selectedRequest.assignedProfessional ||
                  normalizeMeetingProposals(selectedRequest)[0]?.professionalId;
                const jobEnded =
                  selectedRequest.status === 'completed' ||
                  selectedRequest.paymentStatus === 'captured';
                if (!proId || !jobEnded) return null;
                return selectedRequest.ownerRating?.stars ? (
                  <div style={{ marginBottom: '20px', padding: '14px', background: '#eef2ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
                    <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 13, color: '#3730a3' }}>Your review (owner)</p>
                    <p style={{ margin: 0, fontSize: 16, letterSpacing: 2, color: '#ca8a04' }}>
                      {'★'.repeat(selectedRequest.ownerRating.stars)}
                      {'☆'.repeat(5 - selectedRequest.ownerRating.stars)}
                    </p>
                    {selectedRequest.ownerRating.comment ? (
                      <p style={{ margin: '10px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.45 }}>
                        {selectedRequest.ownerRating.comment}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ marginBottom: '20px', padding: '16px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 14, color: '#92400e' }}>Rate the service professional</p>
                    <p style={{ margin: '0 0 10px', fontSize: 12, color: '#78350f' }}>
                      Your feedback helps tenants and other owners choose professionals.
                    </p>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setOwnerModalRatingStars(n)}
                          disabled={ownerModalRatingSaving}
                          style={{
                            fontSize: 26,
                            lineHeight: 1,
                            border: 'none',
                            background: 'transparent',
                            cursor: ownerModalRatingSaving ? 'default' : 'pointer',
                            color: ownerModalRatingStars >= n ? '#ca8a04' : '#d1d5db',
                            padding: '2px 4px',
                          }}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={ownerModalRatingComment}
                      onChange={(e) => setOwnerModalRatingComment(e.target.value.slice(0, 2000))}
                      disabled={ownerModalRatingSaving}
                      placeholder="Comments (optional)"
                      rows={3}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: 8,
                        borderRadius: 6,
                        border: '1px solid #e5e7eb',
                        fontSize: 13,
                      }}
                    />
                    <button
                      type="button"
                      onClick={submitOwnerModalRating}
                      disabled={ownerModalRatingSaving || !ownerModalRatingStars}
                      style={{
                        marginTop: 10,
                        width: '100%',
                        padding: '10px',
                        background: ownerModalRatingSaving || !ownerModalRatingStars ? '#cbd5e1' : '#1e293b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        fontWeight: 700,
                        cursor: ownerModalRatingSaving || !ownerModalRatingStars ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {ownerModalRatingSaving ? 'Submitting…' : 'Submit review'}
                    </button>
                  </div>
                );
              })()}

              {selectedRequest.status === 'completed' && selectedRequest.assignedProfessional && selectedRequest.tenantRating?.stars ? (
                <div style={{ marginBottom: '20px', padding: '14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                  <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 13, color: '#166534' }}>Tenant&apos;s review</p>
                  <p style={{ margin: 0, fontSize: 16, letterSpacing: 2, color: '#ca8a04' }}>
                    {'★'.repeat(selectedRequest.tenantRating.stars)}
                    {'☆'.repeat(5 - selectedRequest.tenantRating.stars)}
                  </p>
                  {selectedRequest.tenantRating.comment ? (
                    <p style={{ margin: '10px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.45 }}>
                      {selectedRequest.tenantRating.comment}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {/* Close Button */}
              <button 
                onClick={() => dismissSelectedRequest()}
                style={{width:'100%',background:'#e5e7eb',color:'#374151',border:'none',borderRadius:'6px',padding:'12px',cursor:'pointer',fontWeight:'600',fontSize:'14px'}}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {proProfilePeek?.professionalId ? (
          <ProfessionalProfilePeekModal
            professionalId={proProfilePeek.professionalId}
            fallbackName={proProfilePeek.fallbackName}
            onClose={() => setProProfilePeek(null)}
          />
        ) : null}

        {/* PAYMENT MODAL */}
        {showPaymentModal && showPaymentModal.profesionalEstimate && (
          <PaymentModal
            estimateAmount={showPaymentModal.profesionalEstimate.amount}
            requestId={showPaymentModal.id}
            professionalName={showPaymentModal.profesionalEstimate.submittedBy?.businessName || 'Professional'}
            professionalId={
              showPaymentModal.assignedProfessional ||
              normalizeMeetingProposals(showPaymentModal)[0]?.professionalId
            }
            onCancel={() => setShowPaymentModal(null)}
            onPaymentSuccess={async () => {
              alert('✅ Payment successful! The professional has been notified.');
              setShowPaymentModal(null);
              dismissSelectedRequest();
            }}
          />
        )}

        {/* NO UNITS MESSAGE */}
        {!showAddUnitForm && !activeUnit && ownerUnits.length === 0 && (
          <div style={{textAlign: 'center', padding: '40px'}}>
            <h2>Welcome to Your Owner Portal!</h2>
            <p style={{color: '#666'}}>Get started by adding your first rental unit.</p>
            <button 
              onClick={() => setShowAddUnitForm(true)} 
              className="btn-primary"
              style={{marginTop: '20px'}}
            >
              Add Your First Unit
            </button>
          </div>
        )}
      </div>

      {ownerTourActive ? (
        <Portal>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="owner-tour-title"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10050,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            {(() => {
              const tourDim = 'rgba(15, 23, 42, 0.78)';
              const s = ownerTourSpot;
              if (!s) {
                return (
                  <div
                    style={{ position: 'fixed', inset: 0, background: tourDim }}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                );
              }
              const { left, top, width, height } = s;
              return (
                <>
                  <div
                    style={{ position: 'fixed', left: 0, right: 0, top: 0, height: top, background: tourDim }}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                  <div
                    style={{ position: 'fixed', left: 0, top, width: left, height, background: tourDim }}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                  <div
                    style={{
                      position: 'fixed',
                      left: left + width,
                      right: 0,
                      top,
                      height,
                      background: tourDim,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                  <div
                    style={{
                      position: 'fixed',
                      left: 0,
                      right: 0,
                      top: top + height,
                      bottom: 0,
                      background: tourDim,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                  <div
                    style={{
                      position: 'fixed',
                      left,
                      top,
                      width,
                      height,
                      borderRadius: 12,
                      border: '2px solid rgba(129, 140, 248, 0.95)',
                      pointerEvents: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </>
              );
            })()}

            <div
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: ownerTourTooltipPos.left,
                top: ownerTourTooltipPos.top,
                width: ownerTourTooltipPos.width,
                maxWidth: 'calc(100vw - 24px)',
                zIndex: 10052,
                boxSizing: 'border-box',
                background: ownerThemeDark ? '#0f172a' : '#ffffff',
                color: ownerThemeDark ? '#e2e8f0' : '#0f172a',
                borderRadius: 12,
                padding: '16px 18px',
                boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
                border: ownerThemeDark ? '1px solid rgba(148,163,184,0.28)' : '1px solid #e2e8f0',
                overflowWrap: 'anywhere',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <p id="owner-tour-title" style={{ margin: 0, fontWeight: 900, fontSize: 16, lineHeight: 1.25 }}>
                    {OWNER_TOUR_STEPS[ownerTourStep]?.title || 'Quick tour'}
                  </p>
                  <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.55, opacity: 0.95 }}>
                    {OWNER_TOUR_STEPS[ownerTourStep]?.body || ''}
                  </p>
                  {!ownerTourSpot ? (
                    <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.45, opacity: 0.88 }}>
                      Tip: on smaller screens, open the menu (☰) so the sidebar is visible for the next steps.
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={dismissOwnerTour}
                  aria-label="Close tour"
                  title="Close"
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: ownerThemeDark ? '1px solid rgba(148,163,184,0.35)' : '1px solid #cbd5e1',
                    background: ownerThemeDark ? 'rgba(30,41,59,0.9)' : '#f8fafc',
                    color: ownerThemeDark ? '#e2e8f0' : '#0f172a',
                    fontWeight: 900,
                    cursor: 'pointer',
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                {ownerTourStep < OWNER_TOUR_STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setOwnerTourStep((x) => Math.min(OWNER_TOUR_STEPS.length - 1, x + 1))}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: 'none',
                      background: '#6366f1',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={dismissOwnerTour}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: 'none',
                      background: '#1e293b',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        </Portal>
      ) : null}
    </div>
  );
}

export default OwnerDashboard;
